// GharSaathi v9.8 — UI/UX improvements 23-28 applied
import { useState, useEffect, useRef, useCallback } from "react";

// ── Config ────────────────────────────────────────────────────
const API = "https://api.minimuff.in/api";

// ── Constants ─────────────────────────────────────────────────
const MONTHS   = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const FULLM    = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WDAYS    = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
const WS       = ["S","M","T","W","T","F","S"];
const TODAY    = new Date();
const TY       = TODAY.getFullYear();
const TM       = TODAY.getMonth();
const TD       = TODAY.getDate();

// ── Master role list (single source of truth) ─────────────────
const ROLES = [
  "Cook","Maid (General)","Maid (Utensils)","Maid (Cleaning)",
  "Deep Cleaner","Washroom Cleaner","Baby Nurse / Jappa","Nanny / Ayah",
  "Elder Care","Driver","Gardener / Mali","Laundry / Dhobi","Ironing",
  "Watchman","Masseuse","Gym Trainer","Physiotherapist","Yoga Trainer",
  "Priest / Pandit Ji","Teacher / Tutor","Other"
];

const ROLE_ICON = {
  "Cook":"🍳","Maid (General)":"🧹","Maid (Utensils)":"🥣",
  "Maid (Cleaning)":"🧽","Deep Cleaner":"✨","Washroom Cleaner":"🚿",
  "Baby Nurse / Jappa":"🍼","Nanny / Ayah":"👶","Elder Care":"🧓",
  "Driver":"🚗","Gardener / Mali":"🌿","Laundry / Dhobi":"👕",
  "Ironing":"👔","Watchman":"🔒","Masseuse":"💆","Gym Trainer":"💪",
  "Physiotherapist":"🏥","Yoga Trainer":"🧘","Priest / Pandit Ji":"🙏",
  "Teacher / Tutor":"📚","Other":"✏️"
};
const ri = r => ROLE_ICON[r] || "👤";
const QUICK_ROLES = ["Cook","Maid (General)","Driver","Nanny / Ayah"];

// ── Salary status ─────────────────────────────────────────────
const STATUS = {
  P:{l:"P",full:"Present",bg:"#10b981",fg:"#fff"},
  A:{l:"A",full:"Absent",bg:"#f43f5e",fg:"#fff"},
  H:{l:"½",full:"Half Day",bg:"#f59e0b",fg:"#fff"},
  L:{l:"L",full:"Leave",bg:"#8b5cf6",fg:"#fff"},
  W:{l:"W",full:"Week Off",bg:"#64748b",fg:"#fff"},
};
const SLOT_META = {
  M:{short:"Mor",bg:"#e0f2fe",active:"#0284c7",calBg:"#f0f9ff"},
  A:{short:"Aft",bg:"#fef3c7",active:"#d97706",calBg:"#fffbeb"},
  E:{short:"Eve",bg:"#ede9fe",active:"#7c3aed",calBg:"#faf5ff"},
  N:{short:"Ngt",bg:"#e0e7ff",active:"#4338ca",calBg:"#eef2ff"},
};

// ── Colours ───────────────────────────────────────────────────
const C = {
  ind:"#1a1a2e", indD:"#0f0f1a", gold:"#f5c518",
  white:"#ffffff", bg:"#f1f5f9", card:"#ffffff",
  green:"#10b981", red:"#f43f5e", amber:"#f59e0b",
  purple:"#8b5cf6", border:"#e2e8f0",
  text:"#0f172a", mid:"#334155", light:"#64748b", xlight:"#94a3b8",
};
const HDR = `linear-gradient(135deg,${C.ind} 0%,${C.indD} 100%)`;

// ── CSS ───────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
  *{box-sizing:border-box;-webkit-tap-highlight-color:transparent;}
  body{margin:0;background:${C.bg};font-family:'Plus Jakarta Sans',sans-serif;}
  button{cursor:pointer;font-family:inherit;}
  button:active{opacity:.8;}
  input,select,textarea{font-family:inherit;}
  input:focus,select:focus,textarea:focus{outline:none;border-color:${C.ind}!important;box-shadow:0 0 0 3px rgba(26,26,46,.1);}
  ::-webkit-scrollbar{display:none;}
  input[type=number]::-webkit-inner-spin-button{opacity:.4;}
  @keyframes up{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes pop{0%{transform:scale(.85)}60%{transform:scale(1.06)}100%{transform:scale(1)}}
  .su{animation:up .22s ease both;}
  .pop{animation:pop .2s ease both;}
`;

const IS = {
  width:"100%",border:`1.5px solid ${C.border}`,borderRadius:"12px",
  padding:"12px 14px",fontSize:"14px",background:C.white,
  color:C.text,display:"block",transition:"border-color .15s",
};

// ── Token helpers ─────────────────────────────────────────────
const getToken  = () => localStorage.getItem("gs_tok9");
const setToken  = t  => { if (t && t !== "undefined" && t !== "null") localStorage.setItem("gs_tok9", t); };
const clearAuth = () => localStorage.removeItem("gs_tok9");

// ── API ───────────────────────────────────────────────────────
let _sessionExpiredAt = 0;
async function api(path, method="GET", body=null, hid=null) {
  const h = {"Content-Type":"application/json"};
  const tok = getToken();
  if (tok) h["Authorization"] = `Bearer ${tok}`;
  if (hid) h["x-household-id"] = hid;
  const r = await fetch(`${API}${path}`, {method, headers:h, body:body?JSON.stringify(body):null});
  if (r.status === 401 && tok && !/^\/auth\/(login|register|join|me)\b/.test(path)) {
    _sessionExpiredAt = Date.now();
    window.dispatchEvent(new CustomEvent("gs:session-expired"));
    throw Object.assign(new Error("Session expired"), {sessionExpired:true, status:401});
  }
  const d = await r.json();
  if (!r.ok) throw new Error(d.error || "Request failed");
  return d;
}

// ── Utils ─────────────────────────────────────────────────────
const pad   = n => String(n).padStart(2,"0");
const fmtC  = n => "₹"+Number(n||0).toLocaleString("en-IN");
const dim   = (y,m) => new Date(y,m+1,0).getDate();
const dk    = (y,m,d) => `${y}-${pad(m+1)}-${pad(d)}`;
const dow   = (y,m,d) => new Date(y,m,d).getDay();
const t2m   = t => { if(!t)return 0; const[h,mn]=t.split(":").map(Number); return h*60+mn; };
const isWO  = (s,dw) => (s.weekOffDays||[]).includes(dw);
const isFut = (y,m,d) => new Date(y,m,d) > TODAY;
const isToday=(y,m,d)=> y===TY&&m===TM&&d===TD;
function fmtDate(ds) {
  if(!ds) return "—";
  const s = String(ds).replace(/T.*$/, "");
  const d = new Date(s + "T12:00:00");
  if(isNaN(d)) return String(ds);
  return d.toLocaleDateString("en-IN", {day:"numeric",month:"short",year:"numeric"});
}

function getEff(att,sl,sid,k) {
  return (att[sid]||{})[k] || ((sl[sid]||{})[k] ? "L" : null);
}
function sk(d,si) { return si===0 ? d : `${d}_s${si}`; }

// ── Cycle days ────────────────────────────────────────────────
function cycleDays(staff,ry,rm) {
  const s=staff.cycleStart||1; const days=[];
  if(s===1){ for(let d=1;d<=dim(ry,rm);d++) days.push({y:ry,m:rm,d}); }
  else {
    let pm=rm-1,py=ry; if(pm<0){pm=11;py--;}
    for(let d=s;d<=dim(py,pm);d++) days.push({y:py,m:pm,d});
    for(let d=1;d<=Math.min(s-1,dim(ry,rm));d++) days.push({y:ry,m:rm,d});
  }
  return days;
}

function salOn(staff,ds) {
  const h=(staff.salaryHistory||[]).slice().sort((a,b)=>b.date.localeCompare(a.date));
  const m=h.find(x=>x.date<=ds); return m?Number(m.amount):(staff.salary||0);
}

// ── Salary calc ───────────────────────────────────────────────
function calcSal(staff,att,sl,tlog,pays,advs,adjs,ry,rm) {
  const Z={P:0,A:0,H:0,W:0,woWorked:0,woCredit:0,paidLeavesUsed:0,unpaidLeaves:0,
    earned:0,otAmt:0,gross:0,pfDed:0,esicDed:0,advDed:0,adjNet:0,
    totalDed:0,net:0,wkDays:0,daysInCycle:0,workingDaysCount:0,weekOffDaysCount:0,
    weekOffWorked:0,dailyRate:0,tHours:0,otHours:0,
    dedLines:[],adjs:[],paidRec:null,locked:false,cycleLabel:"",carryFwd:0};
  if(!staff?.id||!staff.salary) return Z;
  const days=cycleDays(staff,ry,rm);
  const daysInCycle=days.length;
  if(!daysInCycle) return Z;
  const sess=staff.sessions||1;
  const plA=staff.paidLeaveDays||0;

  // Single pass: count attendance and build per-day data for earned calculation
  let P=0,A=0,H=0,W=0,woW=0,plU=0,unpaidL=0,totMin=0,otMin=0,weekOffDaysCount=0;
  const dayData=[]; // {dr, isWOD, status:'P'|'A'|'H'|'WO'|null}

  for(const {y,m,d} of days) {
    const k=dk(y,m,d); const dw=dow(y,m,d); const isWOD=isWO(staff,dw);
    const sal=salOn(staff,k); const dr=sal/daysInCycle;
    const _ts=staff.trackingStart||staff.joinDate;if(_ts&&k<_ts){dayData.push({dr,isWOD,status:'SKIP'});continue;}
    if(isWOD) weekOffDaysCount++;
    let status=null;
    if(sess>1){
      let dp=0,da=0;
      for(let si=0;si<sess;si++){const e=(att[staff.id]||{})[sk(k,si)];if(e==="P")dp++;else if(e==="A")da++;}
      if(dp===sess){status="P";P++;if(isWOD)woW++;}
      else if(dp>0){status="H";H++;}
      else if(da>0){status=isWOD?"WO":"A";if(isWOD)W++;else A++;}
      else{status=isWOD?"WO":null;if(isWOD)W++;}
    } else {
      const e=getEff(att,sl,staff.id,k);
      if(e==="P"){status="P";P++;if(isWOD)woW++;}
      else if(e==="A"){status=isWOD?"WO":"A";if(isWOD)W++;else A++;}
      else if(e==="H"){status="H";H++;}
      else if(e==="L"){
        if(plU<plA){status="P";plU++;P++;}
        else{status="A";unpaidL++;A++;}
      }
      else{status=isWOD?"WO":null;if(isWOD)W++;}
    }
    dayData.push({dr,isWOD,status});
    if(staff.timeTracking){
      const tl=(tlog[staff.id]||{})[k];
      if(tl?.inTime&&tl?.outTime){
        const w=Math.max(0,t2m(tl.outTime)-t2m(tl.inTime));
        totMin+=w;if(w>(staff.stdHours||8)*60)otMin+=w-(staff.stdHours||8)*60;
      }
    }
  }

  const workingDaysCount=daysInCycle-weekOffDaysCount;
  const wkDays=workingDaysCount;
  const absOffsetByWO=Math.min(woW,A); // WO-worked days that offset absences
  const woCredit=absOffsetByWO;
  const otCreditDays=Math.max(0,woW-A); // WO-worked days that earn OT credit
  const baseSal=dayData[0]?.dr*daysInCycle||salOn(staff,dk(days[0].y,days[0].m,days[0].d));
  const dailyRate=Math.round((baseSal/daysInCycle)*100)/100;

  // Compute earned — WO days always paid, absences deducted, WO offset applied
  let earned=0;
  if(staff.salaryType==="monthly"){
    let woCredLeft=absOffsetByWO;
    for(const {dr,isWOD,status} of dayData){
      if(status==='SKIP') continue;
      if(isWOD){earned+=dr;} // WO day: always paid regardless of what's marked
      else if(status==="P"){earned+=dr;}
      else if(status==="H"){earned+=dr*0.5;}
      else if(status==="A"){if(woCredLeft>0){woCredLeft--;earned+=dr;}/* else absent=0 */}
      // null (unmarked non-WO): 0 earning
    }
    // OT credit for WO-worked days beyond absences (extra work)
    if(otCreditDays>0) earned+=otCreditDays*dailyRate;
  } else {
    earned=(P+H*0.5)*baseSal; // daily rate: pay per day worked
  }
  earned=Math.round(Math.max(0,earned));

  // Time-tracking OT
  let otAmt=0;
  if(staff.otEnabled&&otMin>0){
    if(staff.otType==="fixed")otAmt=(otMin/60)*(staff.otRate||0);
    else otAmt=(otMin/60)*(dailyRate/(staff.stdHours||8))*(staff.otMultiplier||1.5);
  }
  otAmt=Math.round(otAmt);
  const gross=earned+otAmt;

  const pfDed=staff.pfEnabled?Math.round(earned*0.12):0;
  const esicDed=(staff.esicEnabled&&earned<=21000)?Math.round(earned*0.0075):0;

  const payRecs=(pays||[]).filter(p=>p.staff_id===staff.id&&Number(p.year)===ry&&Number(p.month)===rm);
  const payRec=payRecs.length>0?payRecs[payRecs.length-1]:null;
  const totalPaid=payRecs.reduce((s,p)=>s+Number(p.amount_paid||0),0);
  const locked=payRecs.length>0;

  // Advance deductions — if advance has adjustments for this month, use those amounts
  const sAdvs=(advs||[]).filter(a=>a.staff_id===staff.id&&!a.cleared);
  let advDed=0;
  const dedLines=sAdvs.map(a=>{
    let amt=0;
    if(locked){
      const snap=(payRec?.adv_deductions||[]).find(x=>x.advance_id===a.id);
      amt=snap?Number(snap.amount):0;
    } else {
      // Check for manual adjustment recorded for this month
      const adj=(a.adjustments||[]).find(x=>Number(x.year)===ry&&Number(x.month)===rm);
      if(adj){
        amt=Number(adj.amount_adjusted);
      } else {
        // Fall back to legacy installment logic for backwards compat
        const skipd=(a.skipped_months||[]).find(x=>Number(x.year)===ry&&Number(x.month)===rm);
        amt=skipd?0:(a.recovery_type==="full"?Number(a.remaining||a.total_amount):(Number(a.installment_amount)||0));
      }
    }
    advDed+=amt; return{...a,thisMonth:amt};
  });

  const sAdjs=(adjs||[]).filter(a=>a.staff_id===staff.id&&Number(a.year)===ry&&Number(a.month)===rm);
  const adjNet=sAdjs.reduce((s,a)=>s+Number(a.amount||0),0);
  const prm=rm===0?11:rm-1; const pry=rm===0?ry-1:ry;
  const prevRec=(pays||[]).find(p=>p.staff_id===staff.id&&Number(p.year)===pry&&Number(p.month)===prm);
  const carryFwd=prevRec?.diff?Number(prevRec.diff):0;
  const totalDed=pfDed+esicDed+advDed;
  const net=Math.round(gross-pfDed-esicDed+adjNet);
  const s2=staff.cycleStart||1;
  let cycleLabel=`${FULLM[rm]} ${ry}`;
  if(s2!==1){let pm=rm-1,py=ry;if(pm<0){pm=11;py--;}cycleLabel=`${s2} ${MONTHS[pm]} – ${s2-1} ${MONTHS[rm]} ${ry}`;}
  return{P,A,H,W,woWorked:woW,woCredit,paidLeavesUsed:plU,unpaidLeaves:unpaidL,
    daysInCycle,workingDaysCount,weekOffDaysCount,weekOffWorked:woW,dailyRate,
    tHours:Math.round(totMin/6)/10,otHours:Math.round(otMin/6)/10,otAmt,earned,gross,
    pfDed,esicDed,advDed,adjNet,adjs:sAdjs,totalDed,net,wkDays,dedLines,
    paidRec:payRec||null,payRecs,totalPaid,locked,cycleLabel,carryFwd};
}

// ── Salary overdue helpers ───────────────────────────────────
function payDueDate(staff,refYear,refMonth) {
  const cs=staff.cycleStart||1;
  const dy=refMonth===11?refYear+1:refYear;
  const dm=refMonth===11?0:refMonth+1;
  return new Date(dy,dm,cs);
}
function isSalaryOverdue(staff,pays,refYear,refMonth,today=new Date()) {
  const paid=(pays||[]).find(p=>p.staff_id===staff.id&&Number(p.year)===refYear&&Number(p.month)===refMonth&&p.locked);
  if(paid) return false;
  if(!staff.salary||staff.salary===0) return false;
  const _eff=staff.trackingStart||staff.joinDate;
  if(_eff){
    const days=cycleDays(staff,refYear,refMonth);
    if(!days.length) return false;
    const last=days[days.length-1];
    const cycleEnd=new Date(last.y,last.m,last.d);
    const join=new Date(_eff);
    if(join>cycleEnd) return false;
  }
  return today>=payDueDate(staff,refYear,refMonth);
}

// ── Cycle helpers ─────────────────────────────────────────────
function ord(n){const s=['th','st','nd','rd'],v=n%100;return n+(s[(v-20)%10]||s[v]||s[0]);}

// Returns {year,month} of the last COMPLETED cycle (end-month convention:
// for cycleStart=8, rm=3 means 8 Mar – 7 Apr, i.e. cycle that ENDS in April).
function getDefaultCycle(staff,today=new Date()){
  const cs=staff.cycleStart||1;
  let y=today.getFullYear(),m=today.getMonth();
  for(let i=0;i<24;i++){
    const endDate=cs===1?new Date(y,m+1,0):new Date(y,m,cs-1);
    if(endDate<=today){
      const _geff=staff.trackingStart||staff.joinDate;
      if(_geff){
        const days=cycleDays(staff,y,m);
        if(days.length){
          const cycleStart=new Date(days[0].y,days[0].m,days[0].d);
          const join=new Date(_geff);
          if(cycleStart>=join) return {year:y,month:m};
          // Cycle started before join — find the cycle that contains joinDate
          let fy=y,fm=m;
          for(let j=0;j<24;j++){
            const fd=cycleDays(staff,fy,fm);
            if(fd.length){
              const fs=new Date(fd[0].y,fd[0].m,fd[0].d);
              const fe=new Date(fd[fd.length-1].y,fd[fd.length-1].m,fd[fd.length-1].d);
              if(join>=fs&&join<=fe) return {year:fy,month:fm};
            }
            fm++;if(fm>11){fm=0;fy++;}
          }
          return {year:y,month:m};
        }
      }
      return {year:y,month:m};
    }
    m--;if(m<0){m=11;y--;}
  }
  return {year:today.getFullYear(),month:today.getMonth()};
}

// Returns true if the cycle {refY,refM} has not yet ended as of today.
function isCycleRunning(staff,refY,refM,today=new Date()){
  const cs=staff.cycleStart||1;
  const endDate=cs===1?new Date(refY,refM+1,0):new Date(refY,refM,cs-1);
  return endDate>today;
}

// Returns a short date-range label: "1 Apr – 30 Apr 2026" or "8 Mar – 7 Apr 2026"
function cyclePeriodLabel(staff,refY,refM){
  const cs=staff.cycleStart||1;
  if(cs===1){const d=new Date(refY,refM+1,0).getDate();return `1 ${MONTHS[refM]} – ${d} ${MONTHS[refM]} ${refY}`;}
  let pm=refM-1,py=refY;if(pm<0){pm=11;py--;}
  return `${cs} ${MONTHS[pm]} – ${cs-1} ${MONTHS[refM]} ${refY}`;
}

// Count working days in a cycle that have already been reached (on/before today)
// and are in scope (on/after tracking_start) but have NO attendance marked.
// Used to gate settlement: unmarked days must be resolved, not silently counted
// as absent. Future/unreached days in a running cycle are never counted.
function cycleUnmarked(staff,att,sl,ry,rm,today=new Date()){
  const days=cycleDays(staff,ry,rm);
  const eff=staff.trackingStart||staff.joinDate;
  const sess=staff.sessions||1;
  let required=0,unmarked=0;
  for(const {y,m,d} of days){
    if(new Date(y,m,d)>today) continue;        // not yet reached
    const k=dk(y,m,d);
    if(eff&&k<eff) continue;                    // before tracking_start — out of scope
    if(isWO(staff,dow(y,m,d))) continue;        // week-off — no marking needed
    required++;
    let marked;
    if(sess>1){ marked=false; for(let si=0;si<sess;si++){ if((att[staff.id]||{})[sk(k,si)]){marked=true;break;} } if(!marked&&(sl[staff.id]||{})[k])marked=true; }
    else { marked=!!getEff(att,sl,staff.id,k); }
    if(!marked) unmarked++;
  }
  return {required,unmarked};
}

// ── Phase-3 cycle-box helpers (end-month convention, 0-indexed) ──
// Advance a cycle by n cycles (n may be negative).
function addCycle(ry,rm,n){
  let m=rm+n,y=ry;
  while(m>11){m-=12;y++;} while(m<0){m+=12;y--;}
  return {year:y,month:m};
}
// Currently-accruing cycle = exactly one cycle AFTER the last completed one.
function accruingCycle(staff,today=new Date()){
  const dc=getDefaultCycle(staff,today);
  return addCycle(dc.year,dc.month,1);
}
// The payment row (if any) for a staff's cycle.
function payFor(pays,sid,ry,rm){
  return (pays||[]).find(p=>p.staff_id===sid&&Number(p.year)===ry&&Number(p.month)===rm)||null;
}
// State of a completed cycle: 'settled' | 'pending' | 'unpaid'.
function cycleState(pays,sid,ry,rm){
  const p=payFor(pays,sid,ry,rm);
  if(!p) return 'unpaid';
  if(p.state==='settled') return 'settled';
  if(p.state==='pending') return 'pending';
  return p.state||'settled';
}
// Vertical stack of cycle boxes: oldest unsettled completed cycles first,
// accruing cycle last. Walks back from last completed cycle, stops at the
// first settled cycle, bounded by join/tracking_start and a hard cap.
function openCycleBoxes(staff,pays,today=new Date()){
  const dc=getDefaultCycle(staff,today);
  const acc=accruingCycle(staff,today);
  const CAP=12;
  const eff=staff.trackingStart||staff.joinDate;
  const join=eff?new Date(eff):null;
  const open=[];
  let cur=dc;
  for(let i=0;i<CAP;i++){
    const days=cycleDays(staff,cur.year,cur.month);
    if(!days.length) break;
    if(join){
      const end=new Date(days[days.length-1].y,days[days.length-1].m,days[days.length-1].d);
      if(end<join) break;
    }
    const st=cycleState(pays,staff.id,cur.year,cur.month);
    if(st==='settled') break;
    open.unshift({year:cur.year,month:cur.month,state:st,accruing:false});
    cur=addCycle(cur.year,cur.month,-1);
  }
  const boxes=[...open,{year:acc.year,month:acc.month,state:'accruing',accruing:true}];
  return {boxes,allSettled:open.length===0};
}

// ── Normalise staff from API ───────────────────────────────────
function norm(s) {
  return {
    ...s,
    weekOffDays:s.week_off_days||[],
    salaryType:s.salary_type||"monthly",
    cycleStart:s.cycle_start||1,
    salaryHistory:(s.salary_history||[]).map(h=>({date:h.date,amount:Number(h.amount),reason:h.reason})),
    roles:s.roles||[],
    sessions:s.sessions||1,
    slots:(()=>{if(s.slots&&s.slots.length)return s.slots;const sess=s.sessions||1;if(sess===1)return["M"];if(sess===2)return["M","E"];return["M","A","E"];})(),
    timeTracking:s.time_tracking||false,
    startTime:s.start_time||"09:00",
    stdHours:s.std_hours||8,
    otEnabled:s.ot_enabled||false,
    otType:s.ot_type||"fixed",
    otRate:Number(s.ot_rate)||0,
    otMultiplier:Number(s.ot_multiplier)||1.5,
    pfEnabled:s.pf_enabled||false,
    esicEnabled:s.esic_enabled||false,
    fullTime:s.full_time||false,
    paidLeaveDays:s.paid_leave_days||0,
    left:s.is_left===true||s.is_left===1||s.is_left==='t'||!!s.left_date,
    leftDate:s.left_date||"",
    joinDate:s.join_date||"",
    dateOfBirth:s.date_of_birth||"",
    emergencyContact:s.emergency_contact||"",
    emergencyPhone:s.emergency_phone||"",
    phone:s.phone||"",
    notes:s.notes||"",
    photoUrl:s.photo_url||"",
    staffType:s.staff_type||"regular",
    trackingStart:s.tracking_start||"",
  };
}

// ── Shared UI components ──────────────────────────────────────
const Logo = ({size=32}) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
    <polyline points="4,26 20,10 36,26" stroke={C.gold} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="7" y1="33" x2="33" y2="33" stroke={C.gold} strokeWidth="2.5" strokeLinecap="round"/>
    <circle cx="13" cy="26" r="2.6" fill={C.gold}/>
    <circle cx="20" cy="26" r="2.6" fill={C.gold}/>
    <circle cx="27" cy="26" r="2.6" fill={C.gold}/>
  </svg>
);

function Spin({size=28,color=C.gold}) {
  return <div style={{width:size,height:size,border:`3px solid rgba(0,0,0,.08)`,borderTop:`3px solid ${color}`,borderRadius:"50%",animation:"spin .7s linear infinite",flexShrink:0}}/>;
}

function Avatar({name,photo,size=42,fs=16}) {
  if(photo) return <img src={photo} style={{width:size,height:size,borderRadius:"50%",objectFit:"cover",flexShrink:0}}/>;
  return <div style={{width:size,height:size,borderRadius:"50%",background:HDR,display:"flex",alignItems:"center",justifyContent:"center",color:C.gold,fontWeight:800,fontSize:fs,flexShrink:0}}>{name?.[0]?.toUpperCase()||"?"}</div>;
}

function Chip({label,color=C.light,bg=C.bg,sm}) {
  return <span style={{background:bg,color,fontSize:sm?"10px":"11px",fontWeight:700,borderRadius:"20px",padding:sm?"2px 8px":"3px 12px"}}>{label}</span>;
}

function InfoBox({children,color=C.light,bg="#f8fafc",icon}) {
  return <div style={{background:bg,borderRadius:"10px",padding:"10px 14px",marginBottom:"12px",fontSize:"13px",color,fontWeight:500,lineHeight:1.6,display:"flex",gap:"8px",alignItems:"flex-start"}}>{icon&&<span style={{flexShrink:0}}>{icon}</span>}<span>{children}</span></div>;
}

function Empty({icon,title,sub}) {
  return <div style={{textAlign:"center",padding:"56px 24px"}}><div style={{fontSize:"48px",marginBottom:"14px",opacity:.7}}>{icon}</div><div style={{fontFamily:"'DM Serif Display',serif",fontSize:"20px",color:C.text,marginBottom:"8px"}}>{title}</div><div style={{fontSize:"13px",color:C.xlight,lineHeight:1.7,maxWidth:"220px",margin:"0 auto"}}>{sub}</div></div>;
}

function Toast({msg,type="success",onDone}) {
  useEffect(()=>{const t=setTimeout(onDone,3000);return()=>clearTimeout(t);},[]);
  const bg=type==="error"?"#fff0f0":"#f0fdf4";
  const border=type==="error"?C.red:C.green;
  const color=type==="error"?C.red:"#15803d";
  return <div style={{position:"fixed",bottom:"96px",left:"50%",transform:"translateX(-50%)",background:bg,borderLeft:`4px solid ${border}`,borderRadius:"12px",padding:"12px 20px",fontWeight:600,fontSize:"14px",color,zIndex:600,boxShadow:"0 4px 24px rgba(0,0,0,.12)",animation:"up .2s ease",maxWidth:"320px",whiteSpace:"normal",lineHeight:1.4}}>{msg}</div>;
}

function Fld({label,hint,children,req}) {
  return <div style={{marginBottom:"15px"}}>
    <label style={{fontSize:"11px",color:C.light,fontWeight:700,letterSpacing:".8px",textTransform:"uppercase",display:"block",marginBottom:"7px"}}>{label}{req&&<span style={{color:C.red,marginLeft:"3px"}}>*</span>}</label>
    {children}
    {hint&&<div style={{fontSize:"11px",color:C.xlight,marginTop:"5px",lineHeight:1.5}}>{hint}</div>}
  </div>;
}

function Tog({on,toggle,label,sub,disabled}) {
  return <div onClick={disabled?null:toggle} style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:on?"#f0fdf4":C.bg,borderRadius:"12px",padding:"12px 14px",marginBottom:"10px",cursor:disabled?"default":"pointer",border:`1px solid ${on?"#bbf7d0":C.border}`,opacity:disabled?.5:1,transition:"all .2s"}}>
    <div><div style={{fontSize:"14px",color:C.mid,fontWeight:500}}>{label}</div>{sub&&<div style={{fontSize:"11px",color:C.xlight,marginTop:"2px"}}>{sub}</div>}</div>
    <div style={{width:"44px",height:"26px",borderRadius:"13px",background:on?C.green:C.border,position:"relative",transition:"background .2s",flexShrink:0,marginLeft:"12px"}}>
      <div style={{position:"absolute",top:"3px",left:on?"21px":"3px",width:"20px",height:"20px",borderRadius:"50%",background:"#fff",transition:"left .2s",boxShadow:"0 1px 4px rgba(0,0,0,.2)"}}/>
    </div>
  </div>;
}

function Seg({opts,val,onChange,sm}) {
  return <div style={{display:"flex",gap:"4px",background:C.bg,borderRadius:"12px",padding:"4px"}}>
    {opts.map(o=><button key={o.v} onClick={()=>onChange(o.v)} style={{flex:1,padding:sm?"7px 4px":"10px 6px",borderRadius:"9px",fontWeight:600,fontSize:sm?"11px":"13px",border:"none",background:val===o.v?C.ind:"transparent",color:val===o.v?C.gold:C.light,transition:"all .15s"}}>{o.l}</button>)}
  </div>;
}

function Btn({label,onClick,variant="primary",loading,disabled,full,sm,icon}) {
  const vs={
    primary:{background:HDR,color:C.gold,boxShadow:"0 4px 16px rgba(26,26,46,.25)"},
    green:{background:"linear-gradient(135deg,#10b981,#059669)",color:"#fff",boxShadow:"0 4px 16px rgba(16,185,129,.25)"},
    ghost:{background:C.bg,color:C.mid,border:`1px solid ${C.border}`},
    danger:{background:"#fff0f0",color:C.red,border:"1px solid #fecaca"},
    gold:{background:C.gold,color:C.ind},
  };
  const st=vs[variant]||vs.primary;
  return <button onClick={disabled||loading?null:onClick} disabled={disabled||loading} style={{...st,padding:sm?"9px 16px":"14px 20px",borderRadius:"14px",fontWeight:700,fontSize:sm?"13px":"14px",border:"none",display:"flex",alignItems:"center",justifyContent:"center",gap:"8px",width:full?"100%":undefined,opacity:disabled||loading?.7:1,transition:"all .15s"}}>
    {loading&&<Spin size={18} color={variant==="primary"?C.gold:"#fff"}/>}
    {icon&&!loading&&<span>{icon}</span>}
    {label}
  </button>;
}

function SaveRow({onSave,onClose,label="Save Changes",loading}) {
  return <div style={{display:"flex",gap:"10px",marginTop:"24px",paddingTop:"20px",borderTop:`1px solid ${C.border}`}}>
    <Btn label="Cancel" onClick={onClose} variant="ghost" full/>
    <Btn label={label} onClick={onSave} loading={loading} full/>
  </div>;
}

function Modal({onClose,title,sub,children,accent=C.gold}) {
  useEffect(()=>{document.body.style.overflow="hidden";return()=>{document.body.style.overflow="";};},[]);
  return <div onClick={e=>{if(e.target===e.currentTarget)onClose();}} style={{position:"fixed",inset:0,background:"rgba(10,15,30,.7)",backdropFilter:"blur(8px)",zIndex:400,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
    <div className="su" style={{background:C.white,borderRadius:"28px 28px 0 0",width:"100%",maxWidth:"480px",height:"88vh",maxHeight:"88vh",overflow:"hidden",display:"flex",flexDirection:"column",boxShadow:"0 -20px 80px rgba(0,0,0,.3)"}}>
      <div style={{height:"5px",background:accent,borderRadius:"28px 28px 0 0",flexShrink:0}}/>
      <div style={{padding:"18px 20px 12px",flexShrink:0,borderBottom:`1px solid ${C.border}`}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{fontFamily:"'DM Serif Display',serif",fontSize:"20px",color:C.text}}>{title}</div>
          <button onClick={onClose} style={{width:"32px",height:"32px",borderRadius:"50%",background:C.bg,border:"none",color:C.light,fontSize:"18px",display:"flex",alignItems:"center",justifyContent:"center"}}>×</button>
        </div>
        {sub&&<div style={{color:C.light,fontSize:"13px",marginTop:"4px"}}>{sub}</div>}
      </div>
      <div style={{overflowY:"auto",flex:1,minHeight:0,padding:"16px 20px 48px",WebkitOverflowScrolling:"touch"}}>{children}</div>
    </div>
  </div>;
}

// ── Role Picker ───────────────────────────────────────────────
function RolePicker({value,onChange}) {
  const [open,setOpen] = useState(false);
  const [search,setSearch] = useState("");
  const [customMode,setCustom] = useState(false);
  const [customVal,setCustomVal] = useState("");
  const ref = useRef(null);

  const list = search ? ROLES.filter(r=>r.toLowerCase().includes(search.toLowerCase())) : ROLES;

  const select = r => {
    if(r==="Other"){ setCustom(true); setOpen(false); setSearch(""); return; }
    onChange(r); setOpen(false); setSearch(""); setCustom(false);
  };

  // Close on outside click
  useEffect(()=>{
    const handler = e => { if(ref.current&&!ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown",handler);
    return ()=>document.removeEventListener("mousedown",handler);
  },[]);

  if(customMode) return <div>
    <input value={customVal} onChange={e=>setCustomVal(e.target.value)} placeholder="Type custom role…" autoFocus style={IS}/>
    <div style={{display:"flex",gap:"6px",marginTop:"6px"}}>
      <button type="button" onClick={()=>{if(customVal.trim())onChange(customVal.trim());setCustom(false);}} style={{flex:1,padding:"8px",borderRadius:"10px",background:C.ind,color:C.gold,fontWeight:700,fontSize:"13px",border:"none"}}>Use This Role</button>
      <button type="button" onClick={()=>setCustom(false)} style={{padding:"8px 14px",borderRadius:"10px",background:C.bg,color:C.light,fontWeight:600,fontSize:"13px",border:`1px solid ${C.border}`}}>Back</button>
    </div>
  </div>;

  return <div style={{position:"relative"}} ref={ref}>
    {/* Trigger button */}
    <button type="button" onClick={()=>setOpen(o=>!o)} style={{...IS,display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",textAlign:"left"}}>
      <span style={{color:value?C.text:C.xlight}}>
        {value ? <>{ri(value)} {value}</> : "Select role…"}
      </span>
      <span style={{color:C.xlight,fontSize:"12px"}}>{open?"▲":"▼"}</span>
    </button>

    {/* Quick picks — always visible below the button */}
    <div style={{display:"flex",gap:"6px",flexWrap:"wrap",marginTop:"8px"}}>
      {QUICK_ROLES.map(r=><button key={r} type="button" onClick={()=>select(r)} style={{padding:"6px 12px",borderRadius:"20px",fontSize:"12px",fontWeight:600,border:`1.5px solid ${value===r?C.ind:C.border}`,background:value===r?C.ind:"#fff",color:value===r?C.gold:C.mid,display:"flex",alignItems:"center",gap:"5px"}}>
        <span>{ri(r)}</span><span>{r.split(" ")[0]}</span>
      </button>)}
    </div>

    {/* Dropdown */}
    {open&&<div style={{position:"absolute",top:"calc(100% + 4px)",left:0,right:0,background:"#fff",borderRadius:"14px",border:`1px solid ${C.border}`,boxShadow:"0 8px 32px rgba(0,0,0,.15)",zIndex:500,overflow:"hidden"}}>
      <div style={{padding:"10px 10px 6px"}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search roles…" autoFocus style={{...IS,fontSize:"13px"}}/>
      </div>
      <div style={{maxHeight:"220px",overflowY:"auto"}}>
        {list.filter(r=>r!=="Other").map((r,i)=><div key={r} onClick={()=>select(r)} style={{padding:"9px 14px",cursor:"pointer",background:value===r?"#f0fdf4":"#fff",borderBottom:`1px solid ${C.bg}`,fontSize:"13px",color:value===r?C.green:C.mid,fontWeight:value===r?700:400,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <span>{r}</span>
          {value===r&&<span style={{color:C.green,fontSize:"11px"}}>✓</span>}
        </div>)}
        {list.length===0&&search&&<div onClick={()=>{onChange(search);setOpen(false);setSearch("");}} style={{padding:"12px 14px",color:"#4f46e5",fontSize:"13px",fontWeight:600,cursor:"pointer",background:"#f0f4ff"}}>+ Use "{search}"</div>}
        <div onClick={()=>select("Other")} style={{padding:"9px 14px",cursor:"pointer",background:"#fff",fontSize:"13px",color:C.mid,fontWeight:400,borderTop:`1px solid ${C.bg}`}}>✏️ Other (custom…)</div>
      </div>
    </div>}
  </div>;
}

function friendlyError(msg) {
  if(!msg) return "Something went wrong. Please try again.";
  const m=msg.toLowerCase();
  if(m.includes("too many requests")||m.includes("429")||m.includes("rate limit")) return "Too many actions at once. Please wait a moment and try again.";
  if(m.includes("timeout")||m.includes("etimedout")||m.includes("econnrefused")) return "Could not reach the server. Please check your connection.";
  if(m.includes("unauthorized")||m.includes("401")||m.includes("invalid token")) return "Your session has expired. Please log in again.";
  if(m.includes("forbidden")||m.includes("403")) return "You do not have permission to do this.";
  if(m.includes("not found")||m.includes("404")) return "This record no longer exists.";
  if(m.includes("duplicate")||m.includes("unique")||m.includes("already exists")) return "This already exists.";
  if(msg.includes("bind message")) return "Could not save. Please check all fields and try again.";
  if(msg.includes("network")) return "No internet connection. Please check and try again.";
  return msg.length > 80 ? "Something went wrong. Please try again." : msg;
}

// ══════════════════════════════════════════════════════════════
// QUICK ATTENDANCE CARDS (dismissable, shown on Home tab)
// ══════════════════════════════════════════════════════════════
function QuickAttCards({todayDk,todayDW,activeStaff,att,setAtt,hid,isWO}) {
  const todayStr=new Date().toISOString().slice(0,10);
  const storageKey=`gs_att_dismissed_${todayStr}`;
  const [dismissed,setDismissed] = useState(()=>{
    try{return JSON.parse(sessionStorage.getItem(storageKey)||"[]");}catch{return [];}
  });
  const dismiss = key => {
    const next=[...dismissed,key];
    setDismissed(next);
    sessionStorage.setItem(storageKey,JSON.stringify(next));
  };

  const yest = new Date(); yest.setDate(yest.getDate()-1);
  const yy=yest.getFullYear(),ym=yest.getMonth(),yd=yest.getDate();
  const yesterdayDk=`${yy}-${String(ym+1).padStart(2,"0")}-${String(yd).padStart(2,"0")}`;
  const yDW=yest.getDay();

  const unmarkedFor = (dateDk, dw) => activeStaff.filter(s=>{
    if(s.joinDate&&dateDk<s.joinDate) return false;
    const v=(att[s.id]||{})[dateDk];
    return !v&&!isWO(s,dw)&&v!=="W";
  });

  const todayUnmarked=unmarkedFor(todayDk,todayDW);
  const yesterdayUnmarked=unmarkedFor(yesterdayDk,yDW);

  const onMark=async(sid,dateKey,status)=>{
    setAtt(prev=>({...prev,[sid]:{...(prev[sid]||{}),[dateKey]:status}}));
    try{await api("/attendance","POST",{staff_id:sid,date:dateKey,session_index:0,status},hid);}catch(e){}
  };

  const cards=[];
  if(yesterdayUnmarked.length>0&&!dismissed.includes(yesterdayDk)){
    const dLabel=yest.toLocaleDateString("en-IN",{day:"numeric",month:"short"});
    cards.push({key:yesterdayDk,title:`Yesterday has unmarked staff — ${dLabel}`,list:yesterdayUnmarked,dateKey:yesterdayDk});
  }
  if(todayUnmarked.length>0&&!dismissed.includes(todayDk)){
    cards.push({key:todayDk,title:"Mark Today's Attendance",list:todayUnmarked,dateKey:todayDk});
  }

  if(cards.length===0) return null;

  return <>{cards.map(card=>(
    <QuickAttCard key={card.key} card={card} att={att} onMark={onMark} onDismiss={()=>dismiss(card.key)}/>
  ))}</>;
}

function QuickAttCard({card,att,onMark,onDismiss}) {
  const [markedIds,setMarkedIds]=useState([]);
  const visibleList=card.list.filter(s=>!markedIds.includes(s.id)&&!(att[s.id]||{})[card.dateKey]);

  if(visibleList.length===0) return null;

  return(
    <div style={{background:C.white,borderRadius:"16px",border:`1.5px solid ${C.border}`,marginBottom:"10px",overflow:"hidden"}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px 10px",borderBottom:`1px solid ${C.border}`}}>
        <div style={{fontSize:"13px",fontWeight:700,color:C.text}}>{card.title}</div>
        <button onClick={onDismiss} style={{width:"24px",height:"24px",borderRadius:"50%",background:C.bg,border:"none",color:C.light,fontSize:"14px",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>×</button>
      </div>
      {visibleList.map(s=>(
        <div key={s.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"10px 14px",borderBottom:`1px solid ${C.border}`}}>
          <div>
            <div style={{fontSize:"13px",fontWeight:600,color:C.text}}>{s.name}</div>
            <div style={{fontSize:"11px",color:C.xlight,marginTop:"1px"}}>{s.role}</div>
          </div>
          <div style={{display:"flex",gap:"6px"}}>
            {[{st:"P",c:"#10b981",b:"#dcfce7"},{st:"A",c:"#f43f5e",b:"#ffe4e6"},{st:"L",c:"#8b5cf6",b:"#ede9fe"}].map(({st,c,b})=>(
              <button key={st} onClick={()=>{setMarkedIds(prev=>[...prev,s.id]);onMark(s.id,card.dateKey,st);}} style={{width:"32px",height:"32px",borderRadius:"9px",background:b,color:c,border:"none",fontWeight:800,fontSize:"12px",display:"flex",alignItems:"center",justifyContent:"center"}}>{st}</button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// AUTH
// ══════════════════════════════════════════════════════════════
function AuthScreen({onAuth}) {
  const [mode,setMode] = useState("welcome");
  const [ph,setPh]=useState(""), [nm,setNm]=useState(""), [pw,setPw]=useState(""), [pw2,setPw2]=useState("");
  const [hn,setHn]=useState(""), [ha,setHa]=useState(""), [hc,setHc]=useState(""), [ht,setHt]=useState("apartment");
  const [code,setCode]=useState(""), [invInfo,setInvInfo]=useState(null);
  const [loading,setL]=useState(false), [err,setE]=useState("");

  useEffect(()=>{
    const m=window.location.pathname.match(/\/join\/([A-Z0-9]+)/);
    if(m){setCode(m[1]);setMode("join");api(`/auth/invite-info/${m[1]}`).then(setInvInfo).catch(()=>{});}
  },[]);

  const inp=(ph,val,set,type="text",ac)=><input value={val} onChange={e=>set(e.target.value)} placeholder={ph} type={type} autoComplete={ac} style={{...IS,marginBottom:"12px"}}/>;

  async function login(){
    if(!ph||!pw){setE("Phone and password required");return;}
    setL(true);setE("");
    try{const r=await api("/auth/login","POST",{phone:ph,password:pw});setToken(r.token);onAuth(r.user,r.households);}
    catch(e){setE(e.message);}finally{setL(false);}
  }
  async function register(){
    if(!ph||!nm||!pw||!hn){setE("All starred fields required");return;}
    if(pw.length<6){setE("Password min 6 characters");return;}
    if(pw!==pw2){setE("Passwords don't match");return;}
    setL(true);setE("");
    try{
      const r=await api("/auth/register","POST",{phone:ph,name:nm,password:pw,householdName:hn,householdAddress:ha,householdCity:hc,householdType:ht});
      setToken(r.token);
      onAuth(r.user,[{household_id:r.household.id,household_name:r.household.name,role:"admin",city:r.household.city,type:r.household.type,staff_count:0,...r.household}]);
    }catch(e){setE(e.message);}finally{setL(false);}
  }
  async function join(){
    if(!code||!ph||!nm||!pw){setE("All fields required");return;}
    setL(true);setE("");
    try{
      const r=await api("/auth/join","POST",{code,phone:ph,name:nm,password:pw});
      setToken(r.token);
      onAuth(r.user,[{household_id:r.household.id,household_name:r.household.name,role:"coadmin",city:r.household.city,type:r.household.type,staff_count:0,...r.household}]);
    }catch(e){setE(e.message);}finally{setL(false);}
  }

  const Back = ({to}) => <button onClick={()=>{setMode(to);setE("");}} style={{color:C.gold,background:"none",border:"none",fontSize:"22px",lineHeight:1}}>←</button>;

  if(mode==="welcome") return(
    <div style={{minHeight:"100vh",maxWidth:"480px",margin:"0 auto",display:"flex",flexDirection:"column"}}>
      <style>{CSS}</style>
      <div style={{background:HDR,padding:"80px 32px 60px",display:"flex",flexDirection:"column",alignItems:"center",textAlign:"center"}}>
        <Logo size={72}/>
        <div style={{fontFamily:"'DM Serif Display',serif",color:C.gold,fontSize:"40px",marginTop:"20px",letterSpacing:"-1px"}}>GharSaathi</div>
        <div style={{color:"rgba(255,255,255,.4)",fontSize:"14px",marginTop:"10px"}}>For every home that runs on people</div>
      </div>
      <div style={{flex:1,padding:"40px 24px"}}>
        <Btn label="Log In" onClick={()=>setMode("login")} full/>
        <div style={{height:"10px"}}/>
        <Btn label="Create New Household" onClick={()=>setMode("register")} variant="ghost" full/>
        <button onClick={()=>setMode("join")} style={{width:"100%",marginTop:"12px",padding:"14px",background:"none",border:"none",color:C.light,fontSize:"14px",fontWeight:600}}>I have an invite link →</button>
      </div>
    </div>
  );

  if(mode==="login") return(
    <div style={{minHeight:"100vh",maxWidth:"480px",margin:"0 auto"}}>
      <style>{CSS}</style>
      <div style={{background:HDR,padding:"28px 20px",display:"flex",alignItems:"center",gap:"12px"}}><Back to="welcome"/><div style={{fontFamily:"'DM Serif Display',serif",color:C.gold,fontSize:"22px"}}>Log In</div></div>
      <div style={{padding:"28px 20px"}}>
        <Fld label="Phone Number" req>{inp("Phone number",ph,setPh,"tel","tel")}</Fld>
        <Fld label="Password" req>{inp("Your password",pw,setPw,"password","current-password")}</Fld>
        {err&&<InfoBox color={C.red} bg="#fff0f0" icon="⚠️">{err}</InfoBox>}
        <Btn label="Log In" onClick={login} loading={loading} full/>
      </div>
    </div>
  );

  if(mode==="register") return(
    <div style={{minHeight:"100vh",maxWidth:"480px",margin:"0 auto",overflowY:"auto"}}>
      <style>{CSS}</style>
      <div style={{background:HDR,padding:"28px 20px",display:"flex",alignItems:"center",gap:"12px"}}><Back to="welcome"/><div style={{fontFamily:"'DM Serif Display',serif",color:C.gold,fontSize:"22px"}}>Create Household</div></div>
      <div style={{padding:"28px 20px"}}>
        <div style={{fontSize:"11px",color:C.light,fontWeight:700,letterSpacing:"1px",marginBottom:"12px",textTransform:"uppercase"}}>Your Details</div>
        <Fld label="Your Name" req>{inp("e.g. Shradha",nm,setNm,"text","name")}</Fld>
        <Fld label="Phone Number" req>{inp("Phone number",ph,setPh,"tel","tel")}</Fld>
        <Fld label="Password" req hint="Min 6 characters">{inp("Set a password",pw,setPw,"password","new-password")}</Fld>
        <Fld label="Confirm Password" req>{inp("Confirm password",pw2,setPw2,"password","new-password")}</Fld>
        <div style={{fontSize:"11px",color:C.light,fontWeight:700,letterSpacing:"1px",margin:"20px 0 12px",textTransform:"uppercase"}}>Your Household</div>
        <Fld label="Household Name" req>{inp("e.g. Sharma Residence",hn,setHn)}</Fld>
        <Fld label="Address">{inp("e.g. B-42, Sector 15",ha,setHa)}</Fld>
        <Fld label="City">{inp("e.g. Raipur",hc,setHc)}</Fld>
        <Fld label="Home Type"><Seg opts={[{v:"apartment",l:"🏢 Flat"},{v:"house",l:"🏠 House"},{v:"villa",l:"🏡 Villa"}]} val={ht} onChange={setHt}/></Fld>
        {err&&<InfoBox color={C.red} bg="#fff0f0" icon="⚠️">{err}</InfoBox>}
        <Btn label="Create Household →" onClick={register} loading={loading} full/>
      </div>
    </div>
  );

  if(mode==="join") return(
    <div style={{minHeight:"100vh",maxWidth:"480px",margin:"0 auto"}}>
      <style>{CSS}</style>
      <div style={{background:HDR,padding:"28px 20px",display:"flex",alignItems:"center",gap:"12px"}}><Back to="welcome"/><div style={{fontFamily:"'DM Serif Display',serif",color:C.gold,fontSize:"22px"}}>Join Household</div></div>
      <div style={{padding:"28px 20px"}}>
        {invInfo&&<InfoBox color={C.green} bg="#f0fdf4" icon="🏠">Invited to join <strong>{invInfo.householdName}</strong>{invInfo.city?`, ${invInfo.city}`:""}</InfoBox>}
        <Fld label="Invite Code" req>
          <input value={code} onChange={e=>{setCode(e.target.value.toUpperCase());if(e.target.value.length===8)api(`/auth/invite-info/${e.target.value.toUpperCase()}`).then(setInvInfo).catch(()=>{});}} placeholder="8-character code" maxLength={8} style={{...IS,fontSize:"22px",letterSpacing:"6px",fontWeight:700,textAlign:"center"}}/>
        </Fld>
        <Fld label="Your Name" req>{inp("e.g. Bhabhi",nm,setNm,"text","name")}</Fld>
        <Fld label="Phone Number" req>{inp("Phone number",ph,setPh,"tel","tel")}</Fld>
        <Fld label="Password" req hint="If you have an account already, use your existing password">{inp("Password",pw,setPw,"password","current-password")}</Fld>
        {err&&<InfoBox color={C.red} bg="#fff0f0" icon="⚠️">{err}</InfoBox>}
        <Btn label="Join Household →" onClick={join} loading={loading} full variant="green"/>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// HOUSEHOLD SWITCHER SCREEN
// ══════════════════════════════════════════════════════════════
function HHScreen({user,hhs,onSelect,onCreateNew}) {
  return(
    <div style={{minHeight:"100vh",maxWidth:"480px",margin:"0 auto",background:C.bg}}>
      <style>{CSS}</style>
      <div style={{background:HDR,padding:"48px 24px 32px"}}>
        <div style={{display:"flex",alignItems:"center",gap:"12px",marginBottom:"4px"}}><Logo size={28}/><span style={{fontFamily:"'DM Serif Display',serif",color:C.gold,fontSize:"20px"}}>GharSaathi</span></div>
        <div style={{color:"rgba(255,255,255,.5)",fontSize:"13px",marginTop:"16px"}}>Good {TODAY.getHours()<12?"morning":TODAY.getHours()<17?"afternoon":"evening"}, <strong style={{color:"rgba(255,255,255,.8)"}}>{user.name}</strong></div>
        <div style={{color:"rgba(255,255,255,.3)",fontSize:"12px",marginTop:"2px"}}>Choose a household</div>
      </div>
      <div style={{padding:"20px"}}>
        {hhs.map(h=>(
          <button key={h.household_id||h.id} onClick={()=>onSelect(h)} style={{width:"100%",background:C.white,borderRadius:"18px",padding:"16px 18px",marginBottom:"12px",textAlign:"left",border:`1px solid ${C.border}`,display:"block",boxShadow:"0 2px 12px rgba(0,0,0,.05)"}}>
            <div style={{display:"flex",alignItems:"center",gap:"14px"}}>
              <div style={{width:"48px",height:"48px",borderRadius:"14px",background:HDR,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"22px",flexShrink:0}}>{h.type==="villa"?"🏡":h.type==="house"?"🏠":"🏢"}</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:700,color:C.text,fontSize:"16px",marginBottom:"2px",display:"flex",alignItems:"center",gap:"8px"}}>
                  {h.household_name||h.name}
                  <Chip label={h.role==="admin"?"👑 Admin":"⭐ Co-Admin"} color={h.role==="admin"?"#92400e":"#1e40af"} bg={h.role==="admin"?"#fef3c7":"#dbeafe"} sm/>
                </div>
                <div style={{fontSize:"12px",color:C.xlight}}>{h.city&&`${h.city} · `}{h.staff_count||0} staff</div>
              </div>
              <span style={{color:C.xlight,fontSize:"20px"}}>›</span>
            </div>
          </button>
        ))}
        <button onClick={onCreateNew} style={{width:"100%",background:"none",border:`2px dashed ${C.border}`,borderRadius:"18px",padding:"16px",color:C.light,fontWeight:600,fontSize:"14px",display:"flex",alignItems:"center",justifyContent:"center",gap:"8px"}}>+ Create New Household</button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ONBOARDING
// ══════════════════════════════════════════════════════════════
function Onboarding({hid,onDone}) {
  const [F,setF]=useState({time_tracking:false,sessions:false,multi_role:false,overtime:false,pf_esic:false,on_call_log:false});
  const [step,setStep]=useState(0);
  const [loading,setL]=useState(false);
  const tog=k=>setF(f=>({...f,[k]:!f[k]}));
  const steps=[
    {q:"What does your household need?",sub:"You can change these anytime in Settings.",content:<>
      <Tog on={F.sessions} toggle={()=>tog("sessions")} label="Multiple sessions per day" sub="Staff who come morning and evening"/>
      <Tog on={F.time_tracking} toggle={()=>tog("time_tracking")} label="Time in/out tracking" sub="For drivers and time-based staff"/>
      <Tog on={F.multi_role} toggle={()=>tog("multi_role")} label="Multi-role staff" sub="Different rates for different jobs"/>
      <Tog on={F.on_call_log} toggle={()=>tog("on_call_log")} label="On-Call services" sub="Dhobi, Masseuse, Priest etc."/>
    </>},
    {q:"Formal payroll?",sub:"Only needed for statutory deductions.",content:<>
      <Tog on={F.overtime} toggle={()=>tog("overtime")} label="Overtime pay" sub="OT calculated from time logs"/>
      <Tog on={F.pf_esic} toggle={()=>tog("pf_esic")} label="PF / ESIC deductions" sub="Statutory payroll deductions"/>
      <InfoBox icon="ℹ️">These can be turned on per staff from their profile.</InfoBox>
    </>}
  ];
  const cur=steps[step];
  return(
    <div style={{minHeight:"100vh",maxWidth:"480px",margin:"0 auto",display:"flex",flexDirection:"column",background:C.bg}}>
      <style>{CSS}</style>
      <div style={{background:HDR,padding:"28px 24px 20px"}}>
        <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"20px"}}><Logo size={28}/><span style={{fontFamily:"'DM Serif Display',serif",color:C.gold,fontSize:"18px"}}>GharSaathi</span></div>
        <div style={{display:"flex",gap:"6px"}}>{steps.map((_,i)=><div key={i} style={{flex:1,height:"3px",borderRadius:"3px",background:i<=step?C.gold:"rgba(255,255,255,.15)",transition:"background .3s"}}/>)}</div>
      </div>
      <div style={{flex:1,padding:"28px 24px"}}>
        <div style={{fontFamily:"'DM Serif Display',serif",fontSize:"24px",color:C.text,marginBottom:"6px"}}>{cur.q}</div>
        <div style={{color:C.light,fontSize:"14px",marginBottom:"24px"}}>{cur.sub}</div>
        {cur.content}
      </div>
      <div style={{padding:"0 24px 40px",display:"flex",gap:"10px"}}>
        {step>0&&<Btn label="Back" onClick={()=>setStep(s=>s-1)} variant="ghost" full/>}
        <Btn label={step<steps.length-1?"Next →":"Let's go →"} loading={loading} onClick={async()=>{
          if(step<steps.length-1){setStep(s=>s+1);}
          else{setL(true);try{await api("/auth/features","PUT",F,hid);}catch(e){console.error('Feature save failed:',e.message);}finally{setL(false);}onDone(F);}
        }} full/>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// SPLASH
// ══════════════════════════════════════════════════════════════
function Splash() {
  return <div style={{position:"fixed",inset:0,background:`linear-gradient(135deg,${C.indD},${C.ind})`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",zIndex:999}}>
    <style>{`${CSS}@keyframes r{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:translateY(0)}}.a1{animation:r .5s ease both}.a2{animation:r .5s .2s ease both;opacity:0}.a3{animation:r .5s .4s ease both;opacity:0}
@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    <div className="a1"><Logo size={76}/></div>
    <div className="a2" style={{fontFamily:"'DM Serif Display',serif",color:C.gold,fontSize:"42px",marginTop:"22px",letterSpacing:"-1px"}}>GharSaathi</div>
    <div className="a3" style={{color:"rgba(255,255,255,.35)",fontSize:"13px",marginTop:"10px",letterSpacing:"1px"}}>For every home that runs on people</div>
  </div>;
}

// ══════════════════════════════════════════════════════════════
// MAIN APP
// ══════════════════════════════════════════════════════════════
export default function App() {
  const [splash,setSpl]   = useState(true);
  const [authUser,setAU]  = useState(null);   // null=loading false=logged out object=user
  const [hhs,setHHs]      = useState([]);
  const [activeHH,setAHH] = useState(null);
  const [hhD,setHHD]      = useState(null);   // {household,features,members,pendingRequests}
  const [needOB,setNeedOB]= useState(false);
  const [creatingHH,setCHH]= useState(false);

  // App data
  const [staff,setStaff]   = useState([]);
  const [att,setAtt]       = useState({});
  const [sl,setSL]         = useState({});
  const [tlog,setTlog]     = useState({});
  const [remarks,setRem]   = useState({});
  const [pays,setPays]     = useState([]);
  const [advs,setAdvs]     = useState([]);
  const [adjs,setAdjs]     = useState([]);
  const [advSummary,setAdvSummary] = useState([]); // [{staff_id,balance}]
  const [notifs,setNotifs] = useState([]);

  // View state
  const [tab,setTab]     = useState(()=>sessionStorage.getItem('gs_tab')||'today');
  useEffect(()=>{sessionStorage.setItem('gs_tab',tab);},[tab]);
  const [sy,setSY]       = useState(TY);
  const [sm,setSM]       = useState(TM);
  const [sd,setSD_]      = useState(TD);
  const [attView,setAV]  = useState("day");
  const [editMode,setEM] = useState(false);
  const [showLeft,setShL]= useState(false);
  const [salDet,setSalDet]= useState(null);
  const [detY,setDetY]    = useState(TY);
  const [detM,setDetM]    = useState(TM);
  const [profileSheet,setProfileSheet] = useState(null); // staff id for profile bottom sheet
  const [guidanceDismissed,setGuidanceDismissed] = useState(()=>{try{const s=localStorage.getItem("gs_att_guidance_dismissed");return s?JSON.parse(s):[];}catch(e){return [];}});
  const [loadingData,setLD]= useState(false);
  // Salary calc-on-demand: track which staff have been "calculated" this session
  const [salCalcState,setSalCalcState] = useState(()=>{try{return JSON.parse(sessionStorage.getItem('gs_sal_calc')||'{}');}catch{return {};}});
  const [showSalAmt,setShowSalAmt] = useState(()=>localStorage.getItem('gs_show_sal_amt')==='true');
  const [toast,setToast] = useState(null);

  // Modals
  const [staffMod,setStaffMod]   = useState(null);
  const [advMod,setAdvMod]       = useState(null);
  const [timeMod,setTimeMod]     = useState(null);
  const [remMod,setRemMod]       = useState(null);
  const [incrMod,setIncrMod]     = useState(null);
  const [payMod,setPayMod]       = useState(null);
  const [adjMod,setAdjMod]       = useState(null);
  const [editAdjMod, setEditAdjMod] = useState(null); // holds the adj object being edited
  const [deleteMod,setDeleteMod] = useState(null);  // staff object to confirm delete
  const [restoreMod,setRestoreMod] = useState(null); // staff object for rejoin date modal
  const [settMod,setSettMod]     = useState(false);
  const [settleCyc,setSettleCyc] = useState(null); // {sid,year,month} → SettleScreen
  const [advSheet,setAdvSheet]   = useState(null);  // staff id → AdvLedgerSheet
  const [notifMod,setNotifMod]   = useState(false);
  const [hhSwitch,setHHSwitch]   = useState(false);
  const [overdueMod,setOverdueMod] = useState(false);
  const [advBreakMod,setAdvBreakMod] = useState(false);

  const stripRef = useRef(null);
  const pollRef  = useRef(null);
  const isWriting = useRef(false);
  const currentMonth = useRef({y:TY,m:TM});
  const [expandedCell,setExpandedCell] = useState(null); // {staffId,date} for multi-slot calendar
  const [openSlot,setOpenSlot] = useState({}); // {staffId: slotId|null} for calendar slot toggle
  const [addOncall,setAddOncall] = useState(false); // trigger StaffModal for oncall staff

  useEffect(()=>{const t=setTimeout(()=>setSpl(false),1800);return()=>clearTimeout(t);},[]);

  // Auth check
  useEffect(()=>{
    if(splash) return;
    const tok=getToken();
    if(!tok){setAU(false);return;}
    api("/auth/me").then(r=>{
      setAU(r.user); setHHs(r.households||[]);
      if(r.households?.length===1) enterHousehold(r.households[0]);
    }).catch(()=>{clearAuth();setAU(false);});
  },[splash]);

  useEffect(()=>{
    const h=()=>{
      clearAuth(); setAU(false);
      setToast({msg:friendlyError("unauthorized"), type:"error"});
    };
    window.addEventListener("gs:session-expired",h);
    return ()=>window.removeEventListener("gs:session-expired",h);
  },[]);

  // Scroll strip to today
  useEffect(()=>{
    if(stripRef.current){
      const el=stripRef.current.querySelector('[data-today="true"]');
      if(el) setTimeout(()=>el.scrollIntoView({inline:"center",block:"nearest",behavior:"smooth"}),150);
    }
  },[activeHH,tab,attView]);

  async function enterHousehold(mem) {
    setAHH(mem);
    setLD(true);
    setTab("today");
    const hid=mem.household_id||mem.id;
    try {
      // Build basic hhD from membership data immediately
      setHHD({
        household:{id:hid,name:mem.household_name||mem.name||"",city:mem.city||"",address:mem.address||"",state:mem.state||"",type:mem.type||"apartment",photo_url:mem.photo_url||""},
        features:{time_tracking:mem.time_tracking||false,sessions:mem.sessions||false,multi_role:mem.multi_role||false,overtime:mem.overtime||false,pf_esic:mem.pf_esic||false,on_call_log:mem.on_call_log||false,onboarding_done:mem.onboarding_done||false},
        members:[],pendingRequests:0
      });

      const role=mem.role||"admin";
      let prevM=TM-1,prevY=TY;if(prevM<0){prevM=11;prevY--;}
      const [staffData,attData,attPrev,salData,salPrev,notifsData,advSum] = await Promise.all([
        api("/staff","GET",null,hid),
        api(`/attendance/household/${TY}/${TM+1}`,"GET",null,hid),
        api(`/attendance/household/${prevY}/${prevM+1}`,"GET",null,hid),
        api(`/salary/month/${TY}/${TM}`,"GET",null,hid),
        api(`/salary/month/${prevY}/${prevM}`,"GET",null,hid),
        api("/staff/change-requests","GET",null,hid).catch(()=>[]),
        api("/salary/advance-summary","GET",null,hid).catch(()=>[]),
      ]);

      setNeedOB(!mem.onboarding_done && staffData.length===0);
      setStaff(staffData.map(norm));
      buildAtt(attPrev); // load previous month first (merge), then current
      buildAtt(attData);
      setPays([...(salPrev.payments||[]),...(salData.payments||[])]);
      setAdvs(salData.advances||[]);
      setAdjs([...(salPrev.adjustments||[]),...(salData.adjustments||[])]);
      setAdvSummary(Array.isArray(advSum)?advSum:[]);
      const pending=Array.isArray(notifsData)?notifsData:[];
      setNotifs(pending);
      setHHD(prev=>({...prev,pendingRequests:pending.filter(n=>n.status==="pending").length}));
      startPolling(hid,role);
    } catch(e){console.error(e);}
    finally{setLD(false);}
  }

  function buildAtt(d) {
    const a={},s={},t={},r={};
    (d.attendance||[]).forEach(x=>{if(!a[x.staff_id])a[x.staff_id]={};const k=x.session_index>0?`${x.date}_s${x.session_index}`:x.date;a[x.staff_id][k]=x.status;});
    (d.scheduledLeaves||[]).forEach(x=>{if(!s[x.staff_id])s[x.staff_id]={};s[x.staff_id][x.date]="L";});
    (d.timeLogs||[]).forEach(x=>{if(!t[x.staff_id])t[x.staff_id]={};t[x.staff_id][x.date]={inTime:x.in_time,outTime:x.out_time};});
    (d.remarks||[]).forEach(x=>{if(!r[x.staff_id])r[x.staff_id]={};r[x.staff_id][x.date]=x.text;});
    // Always merge — preserves cross-month data needed for non-standard cycles
    const merge=(prev,next)=>{const n={...prev};Object.entries(next).forEach(([sid,dates])=>{n[sid]={...(n[sid]||{}),...dates};});return n;};
    setAtt(prev=>merge(prev,a));setSL(prev=>merge(prev,s));setTlog(prev=>merge(prev,t));setRem(prev=>merge(prev,r));
  }

  function startPolling(hid,role) {
    if(pollRef.current) clearInterval(pollRef.current);
    pollRef.current=setInterval(async()=>{
      try{
        const d=await api(`/attendance/household/${TY}/${TM+1}`,"GET",null,hid);
        if(!isWriting.current&&currentMonth.current.y===TY&&currentMonth.current.m===TM) buildAtt(d);
        if(role==="admin"){
          const n=await api("/staff/change-requests","GET",null,hid).catch(()=>[]);
          if(Array.isArray(n)){setNotifs(n);setHHD(prev=>prev?{...prev,pendingRequests:n.filter(x=>x.status==="pending").length}:prev);}
        }
      }catch(e){}
    },5000);
  }

  const showT = (msg, type="success") => {
    if(type==="error" && Date.now()-_sessionExpiredAt < 1500) return;
    setToast({msg, type});
  };
  const hid   = activeHH?.household_id||activeHH?.id;
  const F     = hhD?.features||{};
  const H     = hhD?.household||{};
  const isAdmin = (activeHH?.role||"admin")==="admin";
  const activeStaff  = staff.filter(s=>!s.left && s.staffType!=="oncall");
  const oncallStaff  = staff.filter(s=>!s.left && s.staffType==="oncall");
  const leftStaff    = staff.filter(s=>s.left===true);
  const dIM   = dim(sy,sm);

  // ── Month nav ─────────────────────────────────────────────────
  async function navM(dir) {
    let m=sm+dir,y=sy;
    if(m<0){m=11;y--;} if(m>11){m=0;y++;}
    setSM(m);setSY(y);setSD_(1);setEM(false);setSalDet(null);
    currentMonth.current={y,m};
    if(!hid) return;
    try {
      let pm=m-1,py=y;if(pm<0){pm=11;py--;}
      const [a,ap,s,sp]=await Promise.all([
        api(`/attendance/household/${y}/${m+1}`,"GET",null,hid),
        api(`/attendance/household/${py}/${pm+1}`,"GET",null,hid),
        api(`/salary/month/${y}/${m}`,"GET",null,hid),
        api(`/salary/month/${py}/${pm}`,"GET",null,hid),
      ]);
      buildAtt(ap); buildAtt(a); // merge prev then current
      setPays(prev=>{const keep=prev.filter(p=>!(Number(p.year)===y&&Number(p.month)===m)&&!(Number(p.year)===py&&Number(p.month)===pm));return [...keep,...(sp.payments||[]),...(s.payments||[])];});
      setAdvs(s.advances||[]);
      setAdjs(prev=>{const keep=prev.filter(p=>!(Number(p.year)===y&&Number(p.month)===m)&&!(Number(p.year)===py&&Number(p.month)===pm));return [...keep,...(sp.adjustments||[]),...(s.adjustments||[])];});
    }catch(e){}
  }

  // ── Salary detail cycle navigation ───────────────────────────
  async function navDetCycle(dir) {
    let m=detM+dir,y=detY;
    if(m<0){m=11;y--;} if(m>11){m=0;y++;}
    setDetM(m); setDetY(y);
    if(!hid||!salDet) return;
    try {
      let pm=m-1,py=y;if(pm<0){pm=11;py--;}
      const [a,ap,s,sp]=await Promise.all([
        api(`/attendance/household/${y}/${m+1}`,"GET",null,hid),
        api(`/attendance/household/${py}/${pm+1}`,"GET",null,hid),
        api(`/salary/month/${y}/${m}`,"GET",null,hid),
        api(`/salary/month/${py}/${pm}`,"GET",null,hid),
      ]);
      buildAtt(ap); buildAtt(a);
      setPays(prev=>{const keep=prev.filter(p=>!(Number(p.year)===y&&Number(p.month)===m)&&!(Number(p.year)===py&&Number(p.month)===pm));return [...keep,...(sp.payments||[]),...(s.payments||[])];});
      setAdvs(s.advances||[]);
      setAdjs(prev=>{const keep=prev.filter(p=>!(Number(p.year)===y&&Number(p.month)===m)&&!(Number(p.year)===py&&Number(p.month)===pm));return [...keep,...(sp.adjustments||[]),...(s.adjustments||[])];});
    }catch(e){}
  }

  // Open salary detail for a staff, setting correct default cycle
  const [refreshing,setRefreshing] = useState(false);
  const [pullY,setPullY] = useState(0);
  const [pulling,setPulling] = useState(false);

  async function refreshData() {
    if(!hid||refreshing)return;
    setRefreshing(true);
    try{
      let pm=sm-1,py=sy;if(pm<0){pm=11;py--;}
      const [staffData,attData,attPrev,salData,salPrev,notifsData]=await Promise.all([
        api("/staff","GET",null,hid),
        api(`/attendance/household/${sy}/${sm+1}`,"GET",null,hid),
        api(`/attendance/household/${py}/${pm+1}`,"GET",null,hid),
        api(`/salary/month/${sy}/${sm}`,"GET",null,hid),
        api(`/salary/month/${py}/${pm}`,"GET",null,hid),
        api("/staff/change-requests","GET",null,hid).catch(()=>[]),
      ]);
      setStaff(staffData.map(norm));
      buildAtt(attPrev);buildAtt(attData);
      setPays([...(salPrev.payments||[]),...(salData.payments||[])]);
      setAdvs(salData.advances||[]);
      setAdjs([...(salPrev.adjustments||[]),...(salData.adjustments||[])]);
      const pending=Array.isArray(notifsData)?notifsData:[];
      setNotifs(pending);
      setHHD(prev=>({...prev,pendingRequests:pending.filter(n=>n.status==="pending").length}));
      showT("Updated");
    }catch(e){showT("Refresh failed","error");}
    finally{setRefreshing(false);}
  }

  function openSalDet(staffId) {
    const s=staff.find(x=>x.id===staffId);
    const dc=s?getDefaultCycle(s,new Date()):{year:TY,month:TM};
    setDetY(dc.year); setDetM(dc.month);
    setSalDet(staffId);
  }

  // ── Phase-3 salary window loader ─────────────────────────────
  // Cycle boxes can reach back several months; calcSal + cycle state need that
  // attendance/payment data. Load a rolling window of recent months once the
  // salary tab is opened (idempotent merges, keyed off TODAY not sy/sm).
  const salWindowLoaded = useRef(false);
  async function loadSalaryWindow(){
    if(!hid) return;
    const months=[];
    for(let i=0;i<=6;i++){let m=TM-i,y=TY;while(m<0){m+=12;y--;}months.push({y,m});}
    try{
      const results=await Promise.all(months.map(({y,m})=>Promise.all([
        api(`/attendance/household/${y}/${m+1}`,"GET",null,hid).catch(()=>({})),
        api(`/salary/month/${y}/${m}`,"GET",null,hid).catch(()=>({})),
      ])));
      results.forEach(([att])=>buildAtt(att));
      const allPays=[],allAdjs=[];
      results.forEach(([,sal])=>{(sal.payments||[]).forEach(p=>allPays.push(p));(sal.adjustments||[]).forEach(a=>allAdjs.push(a));});
      // merge by replacing any (year,month) covered by the window
      const covered=new Set(months.map(({y,m})=>`${y}_${m}`));
      setPays(prev=>[...prev.filter(p=>!covered.has(`${Number(p.year)}_${Number(p.month)}`)),...allPays]);
      setAdjs(prev=>[...prev.filter(a=>!covered.has(`${Number(a.year)}_${Number(a.month)}`)),...allAdjs]);
      const advSum=await api("/salary/advance-summary","GET",null,hid).catch(()=>[]);
      setAdvSummary(Array.isArray(advSum)?advSum:[]);
    }catch(e){}
  }
  useEffect(()=>{
    if(tab==="salary"&&hid&&!salWindowLoaded.current){salWindowLoaded.current=true;loadSalaryWindow();}
  },[tab,hid]);

  // Re-fetch one cycle's salary month + advance summary (after settle/ledger change).
  async function refreshCycle(year,month){
    if(!hid) return;
    try{
      const [sal,advSum]=await Promise.all([
        api(`/salary/month/${year}/${month}`,"GET",null,hid),
        api("/salary/advance-summary","GET",null,hid).catch(()=>[]),
      ]);
      setPays(prev=>[...prev.filter(p=>!(Number(p.year)===year&&Number(p.month)===month)),...(sal.payments||[])]);
      setAdjs(prev=>[...prev.filter(a=>!(Number(a.year)===year&&Number(a.month)===month)),...(sal.adjustments||[])]);
      setAdvSummary(Array.isArray(advSum)?advSum:[]);
    }catch(e){}
  }

  // Settle a cycle via the two-ledger endpoint.
  async function settleCycle(sid,year,month,netPayable,amountPaid,opts={}){
    await api("/salary/settle","POST",{staff_id:sid,year,month,net_payable:netPayable,amount_paid:amountPaid,...opts},hid);
    await refreshCycle(year,month);
    showT("Cycle settled");
  }
  // Advance-ledger entry (given|repaid|written_off).
  async function addLedgerEntry(sid,entryType,amount,note,entryDate){
    const r=await api("/salary/advance-ledger","POST",{staff_id:sid,entry_type:entryType,amount,note,entry_date:entryDate},hid);
    setAdvSummary(prev=>{const next=prev.filter(x=>x.staff_id!==sid);return [...next,{staff_id:sid,balance:r.balance}];});
    return r;
  }
  const advBalanceOf=sid=>{const r=(advSummary||[]).find(x=>x.staff_id===sid);return r?Number(r.balance||0):0;};

  // ── Salary calc-on-demand helpers ────────────────────────────
  function markCalcd(staffId) {
    setSalCalcState(prev=>{const k=`${staffId}_${sy}_${sm}`;const n={...prev,[k]:true};try{sessionStorage.setItem('gs_sal_calc',JSON.stringify(n));}catch{}return n;});
  }
  function isCalcd(staffId) { return !!salCalcState[`${staffId}_${sy}_${sm}`]; }
  function salStatus(s) {
    const hasPay=(pays||[]).some(p=>p.staff_id===s.id&&Number(p.year)===sy&&Number(p.month)===sm);
    if(hasPay) return 'paid';
    if(isSalaryOverdue(s,pays,sy,sm)) return 'overdue';
    if(isCalcd(s.id)) return 'calculated';
    return 'none';
  }

  // ── Attendance actions ────────────────────────────────────────
  async function cycleAtt(sid,d,si=0) {
    const [y2,m2s,d2]=d.split("-");const m2=Number(m2s)-1;
    const fut=isFut(Number(y2),m2,Number(d2));
    const s=staff.find(x=>x.id===sid);
    if(!s) return;
    // Join date guard
    if(s.joinDate && new Date(d) < new Date(s.joinDate)){showT("Can't mark before join date","error");return;}
    if(fut) {
      const has=(sl[sid]||{})[d];
      setSL(prev=>{const n={...prev,[sid]:{...(prev[sid]||{})}};if(has)delete n[sid][d];else n[sid][d]="L";return n;});
      api("/attendance/scheduled-leave","POST",{staff_id:sid,date:d,remove:!!has},hid).catch(()=>{});
      return;
    }
    if(!editMode) return;
    const dw=dow(Number(y2),m2,Number(d2));
    const isWOD=isWO(s,dw);
    const attKey=s.sessions>1?sk(d,si):d;
    const cur=(att[sid]||{})[attKey]||null;
    const cycle=[null,"P","A","H","L"];
    const next=cycle[(cycle.indexOf(cur)+1)%cycle.length];
    setAtt(prev=>{const n={...prev,[sid]:{...(prev[sid]||{})}};if(!next)delete n[sid][attKey];else n[sid][attKey]=next;return n;});
    isWriting.current=true; api("/attendance","POST",{staff_id:sid,date:d,session_index:si,status:next},hid).catch(e=>showT("Failed to save attendance","error")).finally(()=>{isWriting.current=false;});
  }

  async function saveTime(sid,d,inT,outT){
    setTlog(prev=>({...prev,[sid]:{...(prev[sid]||{}),[d]:{inTime:inT,outTime:outT}}}));
    if(inT&&!(att[sid]||{})[d]) setAtt(prev=>({...prev,[sid]:{...(prev[sid]||{}),[d]:"P"}}));
    await api("/attendance/time-log","POST",{staff_id:sid,date:d,in_time:inT,out_time:outT},hid);
    showT("Time log saved");
  }

  async function saveRem(sid,d,text){
    setRem(prev=>({...prev,[sid]:{...(prev[sid]||{}),[d]:text}}));
    await api("/attendance/remark","POST",{staff_id:sid,date:d,text},hid);
    showT("Remark saved successfully");
  }

  async function saveStaff(s,isNew) {
    const body={
      name:s.name,role:s.role,salary:Number(s.salary)||0,salary_type:s.salaryType||"monthly",
      week_off_days:s.weekOffDays||[],cycle_start:s.cycleStart||1,sessions:s.sessions||1,slots:s.slots||['M'],
      time_tracking:s.timeTracking||false,start_time:s.startTime||"09:00",std_hours:s.stdHours||8,
      ot_enabled:s.otEnabled||false,ot_type:s.otType||"fixed",ot_rate:s.otRate||0,ot_multiplier:s.otMultiplier||1.5,
      pf_enabled:s.pfEnabled||false,esic_enabled:s.esicEnabled||false,full_time:s.fullTime||false,
      paid_leave_days:s.paidLeaveDays||0,aadhaar:s.aadhaar||null,phone:s.phone||null,
      date_of_birth:s.dateOfBirth||null,join_date:s.joinDate||null,join_month:s.joinMonth||null,join_year:s.joinYear||null,
      emergency_contact:s.emergencyContact||null,emergency_phone:s.emergencyPhone||null,
      notes:s.notes||null,is_left:s.left||false,left_date:s.leftDate||null,roles:s.roles||[],staff_type:s.staffType||"regular",tracking_start:s.trackingStart||null
    };
    if(isNew){
      const r=await api("/staff","POST",body,hid);
      if(r.pending){setHHD(prev=>({...prev,pendingRequests:(prev.pendingRequests||0)+1}));showT("Request sent to admin for approval");}
      else{setStaff(prev=>[...prev,norm(r)]);showT("Staff member added");}
    } else {
      const r=await api(`/staff/${s.id}`,"PUT",body,hid);
      if(r&&r.pending){setHHD(prev=>({...prev,pendingRequests:(prev.pendingRequests||0)+1}));showT("Edit request sent to admin for approval");}
      else{setStaff(prev=>prev.map(x=>x.id===s.id?norm(r):x));showT("Changes saved");}
    }
  }

  async function saveIncr(sid,date,amount,reason){
    await api(`/staff/${sid}/increment`,"POST",{amount,effective_date:date,reason},hid);
    setStaff(prev=>prev.map(s=>{if(s.id!==sid)return s;const h=[...(s.salaryHistory||[]),{date,amount:Number(amount),reason}].sort((a,b)=>a.date.localeCompare(b.date));return{...s,salary:Number(amount),salaryHistory:h};}));
    showT("Salary increment saved");
  }

  async function recordPayment(sid,amtPaid,method,paidBy,note){
    const s=staff.find(x=>x.id===sid);
    const c=calcSal(s,att,sl,tlog,pays,advs,adjs,sy,sm);
    const diff=amtPaid-c.net;
    const advDed=c.dedLines.map(a=>({advance_id:a.id,amount:a.thisMonth}));
    await api("/salary/payment","POST",{staff_id:sid,year:sy,month:sm,amount_paid:amtPaid,net_calc:c.net,diff,method,paid_by:paidBy,note,adv_deductions:advDed},hid);
    let _pm=sm-1,_py=sy;if(_pm<0){_pm=11;_py--;}
    const [_sp,_sc]=await Promise.all([api(`/salary/month/${_py}/${_pm}`,"GET",null,hid),api(`/salary/month/${sy}/${sm}`,"GET",null,hid)]);
    setPays(prev=>{const keep=prev.filter(p=>!(Number(p.year)===sy&&Number(p.month)===sm)&&!(Number(p.year)===_py&&Number(p.month)===_pm));return[...keep,...(_sp.payments||[]),...(_sc.payments||[])]});
    setAdvs(prev=>prev.map(a=>{const dl=c.dedLines.find(x=>x.id===a.id);if(!dl||dl.thisMonth===0)return a;const nr=Math.max(0,Number(a.remaining||a.total_amount)-dl.thisMonth);return{...a,remaining:nr,cleared:nr<=0};}));
    showT("Payment recorded successfully");
  }

  async function addAdj(sid,amount,note){
    const r=await api("/salary/adjustment","POST",{staff_id:sid,year:detY,month:detM,amount,note},hid);
    setAdjs(prev=>[...prev,r]);showT("Adjustment saved");
  }

  async function editAdj(id,amount,note){
    const r=await api(`/salary/adjustment/${id}`,"PUT",{amount,note},hid);
    setAdjs(prev=>prev.map(a=>a.id===id?{...a,...r,amount,note}:a));
    showT("Adjustment saved");
  }

  async function deleteAdj(id){
    await api(`/salary/adjustment/${id}`,"DELETE",null,hid);
    setAdjs(prev=>prev.filter(a=>a.id!==id));
    showT("Adjustment removed successfully");
  }

  async function addAdvance(adv){
    const r=await api("/salary/advance","POST",{staff_id:adv.staffId,total_amount:adv.totalAmount||adv.amount,date_given:adv.dateGiven,note:adv.note,ref_year:sy,ref_month:sm},hid);
    setAdvs(prev=>[...prev,{...r,skipped_months:[]}]);showT("Advance recorded");
  }

  async function deleteStaff(sid){
    await api(`/staff/${sid}`,"DELETE",null,hid);
    setStaff(prev=>prev.filter(x=>x.id!==sid));
    setDeleteMod(null);
    showT("Staff member removed permanently");
  }

  function whatsApp(s){
    const c=calcSal(s,att,sl,tlog,pays,advs,adjs,sy,sm);
    const msg=`*GharSaathi — Salary Summary*\n*${H.name||""}${H.city?`, ${H.city}`:""} *\n\n*${s.name}* (${s.role})\n*Period:* ${c.cycleLabel}\n\nPresent: ${c.P} · Absent: ${c.A} · Half: ${c.H}\n${c.otAmt>0?`Overtime: ${fmtC(c.otAmt)}\n`:""}\n*Net Payable: ${fmtC(c.net)}*\n${c.paidRec?`✅ Paid ${fmtC(c.paidRec.amount_paid)} via ${c.paidRec.method}`:"⏳ Pending"}\n\n_Powered by GharSaathi_`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`);
  }

  // ── Today tab computed data ───────────────────────────────────
  const todayDk = dk(TY,TM,TD);
  const todayAtt = activeStaff.filter(s=>{
    if(s.joinDate && new Date(todayDk)<new Date(s.joinDate)) return false;
    return true;
  });
  const todayDW     = dow(TY,TM,TD);
  const todayCame   = todayAtt.filter(s=>{const v=(att[s.id]||{})[todayDk];return v==="P"||v==="H";}).length;
  const todayAbsent = todayAtt.filter(s=>{const v=(att[s.id]||{})[todayDk];return v==="A"||v==="L";}).length;
  const todayUnmarked = todayAtt.filter(s=>{const v=(att[s.id]||{})[todayDk];return !v&&!isWO(s,todayDW)&&v!=="W";}).length;


  const totalAdvThisMonth = advs.filter(a=>{
    const s=staff.find(x=>x.id===a.staff_id);
    return s&&!s.left;
  }).reduce((sum,a)=>sum+Number(a.total_amount||0),0);




  // Render guards
  if(splash) return <Splash/>;
  if(authUser===null) return <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",background:C.ind,color:C.gold,fontFamily:"'DM Serif Display',serif",fontSize:"22px"}}>GharSaathi</div>;
  if(authUser===false) return <AuthScreen onAuth={(user,hhs)=>{setAU(user);setHHs(hhs);if(hhs.length===1)enterHousehold(hhs[0]);else setHHs(hhs);}}/>;
  if(!activeHH&&hhs.length>0) return <HHScreen user={authUser} hhs={hhs} onSelect={enterHousehold} onCreateNew={()=>setCHH(true)}/>;
  if(!activeHH) return <AuthScreen onAuth={(user,hhs)=>{setAU(user);setHHs(hhs);if(hhs.length===1)enterHousehold(hhs[0]);}}/>;
  if(needOB&&isAdmin) return <Onboarding hid={hid} onDone={feats=>{setHHD(prev=>({...prev,features:feats}));setNeedOB(false);}}/>;
  if(loadingData) return <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",background:C.bg,flexDirection:"column",gap:"16px"}}><Spin size={40}/><div style={{color:C.xlight,fontSize:"14px"}}>Loading…</div></div>;

  // ── RENDER ────────────────────────────────────────────────────
  return (
    <div style={{minHeight:"100vh",background:C.bg,maxWidth:"480px",margin:"0 auto",paddingBottom:"96px"}}>
      <style>{CSS}</style>
      {toast&&<Toast msg={toast.msg} type={toast.type} onDone={()=>setToast(null)}/>}

      {/* HEADER */}
      <div style={{background:HDR,position:"sticky",top:0,zIndex:100,boxShadow:"0 4px 30px rgba(0,0,0,.3)"}}>
        <div style={{padding:"12px 16px 0"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"10px"}}>
            <button onClick={()=>setHHSwitch(true)} style={{display:"flex",alignItems:"center",gap:"10px",background:"none",border:"none",textAlign:"left",flex:1}}>
              <Logo size={24}/>
              <div>
                <div style={{fontFamily:"'DM Serif Display',serif",color:C.gold,fontSize:"17px",lineHeight:1,display:"flex",alignItems:"center",gap:"5px"}}>
                  {H.name||"GharSaathi"}<span style={{color:"rgba(245,197,24,.4)",fontSize:"11px"}}>▾</span>
                </div>
                {H.city&&<div style={{color:"rgba(255,255,255,.3)",fontSize:"10px",marginTop:"1px"}}>{H.city}</div>}
              </div>
            </button>
            <div style={{display:"flex",alignItems:"center",gap:"6px"}}>
              {/* Bell — always visible */}
              <button onClick={()=>setNotifMod(true)} style={{position:"relative",width:"34px",height:"34px",borderRadius:"50%",background:"rgba(255,255,255,.07)",border:"none",display:"flex",alignItems:"center",justifyContent:"center",fontSize:"15px"}}>
                🔔
                {(()=>{const salDue=activeStaff.filter(s=>{if(!s.salary||s.salary===0)return false;const dc=getDefaultCycle(s,new Date());if(isCycleRunning(s,dc.year,dc.month))return false;const paid=(pays||[]).some(p=>p.staff_id===s.id&&Number(p.year)===dc.year&&Number(p.month)===dc.month&&p.locked);if(paid)return false;return new Date()>=payDueDate(s,dc.year,dc.month);}).length;const total=(hhD?.pendingRequests||0)+salDue;return total>0?<span style={{position:"absolute",top:"-2px",right:"-2px",width:"16px",height:"16px",background:C.red,borderRadius:"50%",fontSize:"9px",color:"#fff",fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center"}}>{total}</span>:null;})()}
              </button>
              <button onClick={refreshData} style={{width:"34px",height:"34px",borderRadius:"50%",background:"rgba(255,255,255,.07)",border:"none",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <svg viewBox="0 0 24 24" width="16" height="16" style={{animation:refreshing?"spin 1s linear infinite":"none"}} fill="none" stroke="rgba(255,255,255,.6)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                </svg>
              </button>
              <button onClick={()=>setSettMod(true)} style={{width:"34px",height:"34px",borderRadius:"50%",background:"rgba(255,255,255,.07)",border:"none",color:C.xlight,fontSize:"15px",display:"flex",alignItems:"center",justifyContent:"center"}}>⚙️</button>
            </div>
          </div>

          {/* Month nav — only on Attendance and Salary */}
          {(tab==="attendance"||tab==="salary")&&(
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:"rgba(255,255,255,.06)",borderRadius:"12px",padding:"7px 14px",marginBottom:"10px"}}>
              <button onClick={()=>navM(-1)} style={{color:C.gold,fontSize:"22px",background:"none",border:"none",lineHeight:1,padding:"0 4px"}}>‹</button>
              <span style={{color:"#fff",fontWeight:700,fontSize:"15px"}}>{FULLM[sm]} {sy}</span>
              <button onClick={()=>navM(1)} style={{color:C.gold,fontSize:"22px",background:"none",border:"none",lineHeight:1,padding:"0 4px"}}>›</button>
            </div>
          )}

          {/* Date strip + controls — Attendance day view */}
          {tab==="attendance"&&attView==="day"&&(
            <>
              <div ref={stripRef} style={{display:"flex",gap:"4px",overflowX:"auto",paddingBottom:"2px",WebkitOverflowScrolling:"touch"}}>
                {Array.from({length:dIM},(_,i)=>i+1).map(d=>{
                  const dw=dow(sy,sm,d),sel=d===sd,tod=isToday(sy,sm,d),fut=isFut(sy,sm,d);
                  return <button key={d} data-today={tod?"true":undefined} onClick={()=>setSD_(d)} style={{minWidth:"44px",borderRadius:"13px",padding:"6px 0 7px",flexShrink:0,background:sel?C.gold:tod?"rgba(245,197,24,.14)":"rgba(255,255,255,.05)",border:`1.5px solid ${sel?"transparent":tod?"rgba(245,197,24,.4)":"transparent"}`,display:"flex",flexDirection:"column",alignItems:"center",gap:"2px",opacity:fut?.6:1}}>
                    <span style={{fontSize:"9px",color:sel?C.ind:"rgba(255,255,255,.4)",fontWeight:700}}>{WDAYS[dw]}</span>
                    <span style={{fontSize:"17px",fontWeight:800,color:sel?C.ind:tod?C.gold:"#fff",lineHeight:1}}>{d}</span>
                  </button>;
                })}
              </div>
              {/* Controls row */}
              <div style={{display:"flex",alignItems:"center",gap:"6px",margin:"8px 0 0"}}>
                <div style={{fontSize:"12px",color:"rgba(255,255,255,.5)",fontWeight:600,flex:1}}>{WDAYS[dow(sy,sm,sd)]} · {sd} {MONTHS[sm]}</div>
                <button onClick={()=>setAV("calendar")} style={{padding:"6px 12px",borderRadius:"9px",border:"none",fontWeight:600,fontSize:"11px",background:"rgba(255,255,255,.07)",color:"rgba(255,255,255,.5)"}}>📅 Calendar</button>
                <button onClick={()=>setEM(v=>!v)} style={{padding:"6px 12px",borderRadius:"9px",border:"none",fontWeight:700,fontSize:"11px",background:editMode?C.gold:"rgba(255,255,255,.07)",color:editMode?C.ind:"rgba(255,255,255,.5)"}}>✏️ {editMode?"Done":"Edit"}</button>
                {editMode&&<BatchMenu sy={sy} sm={sm} sd={sd} activeStaff={activeStaff} att={att} setAtt={setAtt} hid={hid} showT={showT} api={api}/>}
              </div>
            </>
          )}

          {/* Calendar controls */}
          {tab==="attendance"&&attView==="calendar"&&(
            <div style={{display:"flex",alignItems:"center",gap:"6px",marginBottom:"2px"}}>
              <button onClick={()=>setAV("day")} style={{padding:"6px 12px",borderRadius:"9px",border:"none",fontWeight:600,fontSize:"11px",background:"rgba(255,255,255,.07)",color:"rgba(255,255,255,.5)"}}>📋 Day View</button>
              <button onClick={()=>setEM(v=>!v)} style={{padding:"6px 12px",borderRadius:"9px",border:"none",fontWeight:700,fontSize:"11px",background:editMode?C.gold:"rgba(255,255,255,.07)",color:editMode?C.ind:"rgba(255,255,255,.5)"}}>✏️ {editMode?"Done":"Edit"}</button>
            </div>
          )}
          <div style={{height:"10px"}}/>
        </div>
      </div>

      {/* BOTTOM NAV — SVG icons, WhatsApp/Spotify style */}
      <div style={{position:"fixed",bottom:0,left:"50%",transform:"translateX(-50%)",width:"100%",maxWidth:"480px",background:"rgba(15,15,26,.97)",backdropFilter:"blur(20px)",WebkitBackdropFilter:"blur(20px)",borderTop:"1px solid rgba(255,255,255,.08)",zIndex:200,display:"flex",alignItems:"stretch",paddingBottom:"calc(env(safe-area-inset-bottom,0px) + 8px)"}}>
        {/* Attendance */}
        {(()=>{const active=tab==="attendance";return(
          <button onClick={()=>{setTab("attendance");setSalDet(null);setEM(false);setExpandedCell(null);}} style={{flex:1,minWidth:"60px",paddingTop:"12px",paddingBottom:"14px",display:"flex",flexDirection:"column",alignItems:"center",gap:"3px",background:"none",border:"none",cursor:"pointer"}}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill={active?"#f5c518":"none"} stroke={active?"#f5c518":"rgba(255,255,255,.4)"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              {active?<><rect x="3" y="4" width="18" height="18" rx="2" ry="2" fill="#f5c518"/><line x1="16" y1="2" x2="16" y2="6" stroke="#fff" strokeWidth="2"/><line x1="8" y1="2" x2="8" y2="6" stroke="#fff" strokeWidth="2"/><line x1="3" y1="10" x2="21" y2="10" stroke="#fff" strokeWidth="2"/></>:<><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>}
            </svg>
            <span style={{fontSize:"10px",fontWeight:active?700:500,color:active?"#f5c518":"rgba(255,255,255,.4)",whiteSpace:"nowrap"}}>Attend</span>
          </button>
        );})()}
        {/* Salary */}
        {(()=>{const active=tab==="salary";return(
          <button onClick={()=>{setTab("salary");setSalDet(null);setEM(false);setExpandedCell(null);}} style={{flex:1,minWidth:"60px",paddingTop:"12px",paddingBottom:"14px",display:"flex",flexDirection:"column",alignItems:"center",gap:"3px",background:"none",border:"none",cursor:"pointer"}}>
            <svg width="22" height="22" viewBox="0 0 24 24">
              <text x="12" y="19" fontSize="20" fontWeight="700" textAnchor="middle" fill={active?"#f5c518":"rgba(255,255,255,.38)"} fontFamily="sans-serif">₹</text>
            </svg>
            <span style={{fontSize:"10px",fontWeight:active?700:500,color:active?"#f5c518":"rgba(255,255,255,.4)",whiteSpace:"nowrap"}}>Salary</span>
          </button>
        );})()}
        {/* Home — flat, same height as others */}
        {(()=>{const active=tab==="today";return(
          <button onClick={()=>{setTab("today");setSalDet(null);setEM(false);setExpandedCell(null);}} style={{flex:1,minWidth:"60px",paddingTop:"12px",paddingBottom:"14px",display:"flex",flexDirection:"column",alignItems:"center",gap:"3px",background:"none",border:"none",cursor:"pointer",minHeight:"60px"}}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill={active?"#f5c518":"none"} stroke={active?"#f5c518":"rgba(255,255,255,.4)"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              {active?<><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" fill="#f5c518"/><polyline points="9 22 9 12 15 12 15 22" stroke="#1a1a2e" strokeWidth="1.8"/></>:<><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>}
            </svg>
            <span style={{fontSize:"10px",fontWeight:active?700:500,color:active?"#f5c518":"rgba(255,255,255,.4)",whiteSpace:"nowrap"}}>Home</span>
          </button>
        );})()}
        {/* Staff */}
        {(()=>{const active=tab==="staff";return(
          <button onClick={()=>{setTab("staff");setSalDet(null);setEM(false);setExpandedCell(null);}} style={{flex:1,minWidth:"60px",paddingTop:"12px",paddingBottom:"14px",display:"flex",flexDirection:"column",alignItems:"center",gap:"3px",background:"none",border:"none",cursor:"pointer"}}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill={active?"#f5c518":"none"} stroke={active?"#f5c518":"rgba(255,255,255,.4)"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              {active?<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" fill="#f5c518"/><circle cx="12" cy="7" r="4" fill="#f5c518"/></>:<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></>}
            </svg>
            <span style={{fontSize:"10px",fontWeight:active?700:500,color:active?"#f5c518":"rgba(255,255,255,.4)",whiteSpace:"nowrap"}}>Staff</span>
          </button>
        );})()}
        {/* On-Call */}
        {F.on_call_log&&(()=>{const active=tab==="oncall";return(
          <button onClick={()=>{setTab("oncall");setSalDet(null);setEM(false);setExpandedCell(null);}} style={{flex:1,minWidth:"60px",paddingTop:"12px",paddingBottom:"14px",display:"flex",flexDirection:"column",alignItems:"center",gap:"3px",background:"none",border:"none",cursor:"pointer"}}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill={active?"#f5c518":"none"} stroke={active?"#f5c518":"rgba(255,255,255,.4)"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.14 13.5 19.79 19.79 0 0 1 1.07 4.9 2 2 0 0 1 3.05 2.7h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21 16.92z" fill={active?"#f5c518":"none"}/>
            </svg>
            <span style={{fontSize:"10px",fontWeight:active?700:500,color:active?"#f5c518":"rgba(255,255,255,.4)",whiteSpace:"nowrap"}}>On-Call</span>
          </button>
        );})()}
      </div>

      {/* ══ TODAY TAB ══ */}
      {tab==="today"&&(
        <div className="su" style={{padding:"14px"}}
          onTouchStart={e=>{if(e.currentTarget.scrollTop===0)setPullY(e.touches[0].clientY);}}
          onTouchMove={e=>{if(pullY>0){const dy=e.touches[0].clientY-pullY;if(dy>0){setPulling(dy>70);e.currentTarget.style.transform="translateY("+Math.min(dy*0.4,40)+"px)";}}}}
          onTouchEnd={e=>{if(pulling)refreshData();setPullY(0);setPulling(false);e.currentTarget.style.transform="";}}>
          {/* Top dark header card */}
          <div style={{background:HDR,borderRadius:"18px",padding:"18px",marginBottom:"14px"}}>
            <div style={{color:"rgba(255,255,255,.5)",fontSize:"13px",fontWeight:600,marginBottom:"14px"}}>
              Today · {["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][todayDW]}, {TD} {MONTHS[TM]} {TY}
            </div>
            {/* 3 tappable stat tiles */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"8px"}}>
              {[
                {label:"Came",val:todayCame,color:C.green},
                {label:"Absent",val:todayAbsent,color:C.red},
                {label:"Unmarked",val:todayUnmarked,color:C.amber},
              ].map(x=><button key={x.label} onClick={()=>{setTab("attendance");setAV("day");setSM(TM);setSY(TY);setSD_(TD);}} style={{background:"rgba(255,255,255,.07)",borderRadius:"14px",padding:"14px 10px",border:"none",textAlign:"center",cursor:"pointer"}}>
                <div style={{fontWeight:800,fontSize:"28px",color:x.color,lineHeight:1}}>{x.val}</div>
                <div style={{fontSize:"10px",color:"rgba(255,255,255,.5)",fontWeight:700,letterSpacing:".5px",marginTop:"4px",textTransform:"uppercase"}}>{x.label}</div>
              </button>)}
            </div>
          </div>

          {/* Quick Attendance cards */}
          <QuickAttCards todayDk={todayDk} todayDW={todayDW} activeStaff={activeStaff} att={att} setAtt={setAtt} hid={hid} isWO={isWO}/>

          {/* Advances card (single row) */}
          <button onClick={()=>setAdvBreakMod(true)} style={{width:"100%",background:totalAdvThisMonth>0?"#fffbeb":C.white,borderRadius:"16px",padding:"13px 18px",border:`1.5px solid ${totalAdvThisMonth>0?"#fde68a":C.border}`,textAlign:"left",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"14px"}}>
            <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
              <div style={{fontSize:"20px"}}>💸</div>
              <div style={{fontSize:"13px",fontWeight:700,color:C.mid}}>Active Advances</div>
            </div>
            <div style={{fontWeight:800,fontSize:"18px",color:totalAdvThisMonth>0?C.amber:C.light}}>{fmtC(totalAdvThisMonth)}</div>
          </button>

          {/* Salary Reminders */}
          {(()=>{
            const dismissTs=sessionStorage.getItem('gs_remind_dismiss');
            const visible=!dismissTs||(Date.now()-Number(dismissTs))>24*60*60*1000;
            const dueList=activeStaff.filter(s=>{
              if(!s.salary||s.salary===0) return false;
              const dc=getDefaultCycle(s,new Date());
              if(isCycleRunning(s,dc.year,dc.month)) return false;
              const paid=(pays||[]).some(p=>p.staff_id===s.id&&Number(p.year)===dc.year&&Number(p.month)===dc.month&&p.locked);
              if(paid) return false;
              const due=payDueDate(s,dc.year,dc.month);
              return new Date()>=due;
            });
            if(!visible||dueList.length===0) return null;
            function reminderMsg(s){
              const dc=getDefaultCycle(s,new Date());
              const due=payDueDate(s,dc.year,dc.month);
              const daysOver=Math.floor((Date.now()-due.getTime())/(1000*60*60*24));
              const dueStr=`${due.getDate()} ${MONTHS[due.getMonth()]}`;
              if(s.joinDate){const ms=Math.floor((Date.now()-new Date(s.joinDate))/(1000*60*60*24*30.4));if(ms>=6){const jd=new Date(s.joinDate);return `${s.name} has been with you since ${MONTHS[jd.getMonth()]} ${jd.getFullYear()}. Salary due since ${dueStr}.`;}}
              if(daysOver>7) return `${s.name}'s salary has been pending for ${daysOver} days. Settle when you're ready.`;
              const c=calcSal(s,att,sl,tlog,pays,advs,adjs,dc.year,dc.month);
              if(c.P>0&&c.A===0) return `${s.name} worked all ${c.P} days this cycle. Salary of ${fmtC(c.net)} is due since ${dueStr}.`;
              return `${s.name} worked ${c.P} of ${c.workingDaysCount} days this cycle. Salary of ${fmtC(c.net)} is due since ${dueStr}.`;
            }
            const shown=dueList.slice(0,3);
            const extra=dueList.length-shown.length;
            return (
              <div style={{background:C.white,borderRadius:"18px",padding:"14px 16px",marginBottom:"14px",border:`1px solid #fecaca`,boxShadow:"0 2px 8px rgba(0,0,0,.04)"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"12px"}}>
                  <div style={{fontWeight:700,fontSize:"14px",color:C.text}}>💰 Salary Reminders</div>
                  <button onClick={()=>{sessionStorage.setItem('gs_remind_dismiss',String(Date.now()));}} style={{background:"none",border:"none",color:C.xlight,fontSize:"18px",cursor:"pointer",lineHeight:1,padding:"0 2px"}}>×</button>
                </div>
                {shown.map(s=>(
                  <div key={s.id} style={{padding:"10px 0",borderTop:`1px solid ${C.border}`}}>
                    <div style={{fontSize:"13px",color:C.mid,lineHeight:1.5,marginBottom:"8px"}}>{reminderMsg(s)}</div>
                    <button onClick={()=>{setTab("salary");openSalDet(s.id);}} style={{background:C.ind,color:C.gold,border:"none",borderRadius:"10px",padding:"6px 14px",fontSize:"12px",fontWeight:700}}>Settle now</button>
                  </div>
                ))}
                {extra>0&&<button onClick={()=>setTab("salary")} style={{display:"block",width:"100%",paddingTop:"10px",borderTop:`1px solid ${C.border}`,background:"none",border:"none",color:C.light,fontSize:"12px",fontWeight:600,textAlign:"center",cursor:"pointer"}}>and {extra} more →</button>}
              </div>
            );
          })()}

          {/* ── Backdated attendance guidance ── */}
          {(()=>{
            const today=new Date();
            const guidance=[];
            for(const s of activeStaff){
              let cy,cm;
              const dc=getDefaultCycle(s,today);
              let ncm=dc.month+1,ncy=dc.year;if(ncm>11){ncm=0;ncy++;}
              if(isCycleRunning(s,ncy,ncm,today)){cy=ncy;cm=ncm;}
              else if(isCycleRunning(s,dc.year,dc.month,today)){cy=dc.year;cm=dc.month;}
              else continue;
              const days=cycleDays(s,cy,cm);if(!days.length)continue;
              const cycleStartD=new Date(days[0].y,days[0].m,days[0].d);
              if(Math.floor((today-cycleStartD)/(864e5))<3)continue;
              const wDays=days.filter(({y,m,d})=>{if(isWO(s,dow(y,m,d)))return false;if(new Date(y,m,d)>today)return false;if(s.joinDate&&dk(y,m,d)<s.joinDate)return false;return true;});
              if(!wDays.length)continue;
              const marked=wDays.filter(({y,m,d})=>!!(att[s.id]||{})[dk(y,m,d)]).length;
              if(marked/wDays.length>=0.3)continue;
              const gKey=`${s.id}_${cy}_${cm}`;
              if(guidanceDismissed.includes(gKey))continue;
              guidance.push({s,cy,cm,startStr:`${days[0].d} ${MONTHS[days[0].m]} ${days[0].y}`});
            }
            if(!guidance.length)return null;
            const show=guidance.slice(0,2);const extra=guidance.length-2;
            return <div style={{marginBottom:"14px"}}>
              {show.map(({s,cy,cm,startStr})=>{
                const gKey=`${s.id}_${cy}_${cm}`;
                const dismiss=()=>{const next=[...guidanceDismissed,gKey];setGuidanceDismissed(next);try{localStorage.setItem("gs_att_guidance_dismissed",JSON.stringify(next));}catch(e){}};
                return <div key={s.id} style={{background:C.white,borderRadius:"14px",marginBottom:"8px",borderLeft:"4px solid #93c5fd",padding:"12px 14px",boxShadow:"0 1px 6px rgba(0,0,0,.05)"}}>
                  <div style={{fontWeight:700,fontSize:"14px",color:C.text,marginBottom:"4px"}}>Mark attendance for {s.name}</div>
                  <div style={{fontSize:"12px",color:C.light,marginBottom:"10px"}}>{s.name}'s current cycle started on {startStr}. Mark attendance from that date so salary can be calculated correctly.</div>
                  <div style={{display:"flex",gap:"8px",alignItems:"center"}}>
                    <button onClick={()=>{setTab("attendance");setAV("calendar");setSY(cy);setSM(cm);}} style={{background:C.ind,color:C.gold,border:"none",borderRadius:"9px",padding:"6px 12px",fontSize:"12px",fontWeight:700,cursor:"pointer"}}>Mark Attendance</button>
                    <button onClick={dismiss} style={{background:"none",border:"none",color:C.xlight,fontSize:"12px",cursor:"pointer"}}>Dismiss</button>
                  </div>
                </div>;
              })}
              {extra>0&&<div style={{fontSize:"12px",color:C.light,textAlign:"center",paddingTop:"4px"}}>and {extra} more staff need attendance</div>}
            </div>;
          })()}

          {activeStaff.length===0&&<Empty icon="🏠" title="No staff yet" sub="Go to Staff tab to add your first team member"/>}
        </div>
      )}

      {/* ══ ATTENDANCE DAY ══ */}
      {tab==="attendance"&&attView==="day"&&(
        <div className="su" style={{padding:"14px"}}>
          {isFut(sy,sm,sd)&&<InfoBox icon="📅" color={C.purple} bg="#ede9fe">Future date — tap a staff card to plan leave</InfoBox>}
          {activeStaff.length===0?<Empty icon="📅" title="No staff" sub="Add staff from the Staff tab"/>:
            activeStaff.map(s=>{
              // Hide staff before join date
              if(s.joinDate && new Date(dk(sy,sm,sd))<new Date(s.joinDate)) return null;
              return <DayCard key={s.id} staff={s} att={att} sl={sl} tlog={tlog} remarks={remarks} sd={sd} sm={sm} sy={sy} editMode={editMode} F={F} onCycle={si=>cycleAtt(s.id,dk(sy,sm,sd),si)} onTime={()=>setTimeMod({sid:s.id,d:dk(sy,sm,sd)})} onRem={()=>setRemMod({sid:s.id,d:dk(sy,sm,sd)})}/>;
            })
          }
        </div>
      )}

      {/* ══ ATTENDANCE CALENDAR ══ */}
      {tab==="attendance"&&attView==="calendar"&&(
        <div className="su" style={{padding:"14px"}}>
          {activeStaff.length===0?<Empty icon="📅" title="No staff" sub="Add staff first"/>:
            activeStaff.map(s=>{
              // Hide staff in months before they joined
              if(s.joinDate){const jd=new Date(s.joinDate);if(sy<jd.getFullYear()||(sy===jd.getFullYear()&&sm<jd.getMonth()))return null;}
              const fd=dow(sy,sm,1);
              const c=calcSal(s,att,sl,tlog,pays,advs,adjs,sy,sm);
              return <div key={s.id} style={{background:C.white,borderRadius:"20px",marginBottom:"16px",overflow:"hidden",boxShadow:"0 2px 16px rgba(0,0,0,.06)",border:`1px solid ${C.border}`}}>
                <div style={{padding:"12px 16px",background:HDR,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <div style={{display:"flex",alignItems:"center",gap:"10px"}}>
                    <Avatar name={s.name} photo={s.photoUrl} size={34} fs={13}/>
                    <div><div style={{color:"#fff",fontWeight:700,fontSize:"15px"}}>{s.name}</div><div style={{color:"rgba(255,255,255,.45)",fontSize:"11px"}}>{ri(s.role)} {s.role}</div></div>
                  </div>
                  <div style={{display:"flex",gap:"12px"}}>
                    {[{v:c.P,cl:C.green,l:"P"},{v:c.A,cl:C.red,l:"A"},{v:c.H,cl:C.amber,l:"½"}].map(x=><div key={x.l} style={{textAlign:"center"}}><div style={{fontWeight:800,fontSize:"20px",color:x.cl}}>{x.v}</div><div style={{fontSize:"9px",color:"rgba(255,255,255,.4)",fontWeight:600}}>{x.l}</div></div>)}
                  </div>
                </div>
                <div style={{padding:"12px 14px"}}>
                  {(()=>{
                    const isMS=(s.slots||[]).length>1;
                    const SLBL={M:"Morning",A:"Afternoon",E:"Evening",N:"Night"};
                    const GHead=<div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:"2px",marginBottom:"4px"}}>{WS.map((d,i)=><div key={i} style={{textAlign:"center",fontSize:"9px",color:C.xlight,fontWeight:700}}>{d}</div>)}</div>;
                    if(!isMS) return <>{GHead}<div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:"3px"}}>
                      {Array.from({length:fd}).map((_,i)=><div key={`e${i}`}/>)}
                      {Array.from({length:dIM},(_,i)=>i+1).map(d=>{
                        const k=dk(sy,sm,d),dw=dow(sy,sm,d);
                        const eff=getEff(att,sl,s.id,k);
                        const cfg=eff?STATUS[eff]:null;
                        const tod=isToday(sy,sm,d),fut=isFut(sy,sm,d),isWOD=isWO(s,dw);
                        const beforeJoin=s.joinDate&&new Date(k)<new Date(s.joinDate);
                        return <button key={d} onClick={()=>{if(!editMode||beforeJoin)return;cycleAtt(s.id,k,0);}} style={{aspectRatio:"1",borderRadius:"8px",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",border:`2px solid ${tod?C.gold:"transparent"}`,background:beforeJoin?"#f1f5f9":cfg?cfg.bg:isWOD?C.bg:fut?"#fafafa":C.bg,cursor:editMode&&!beforeJoin?"pointer":"default",opacity:beforeJoin?.4:1}}>
                          <span style={{fontSize:"10px",color:beforeJoin?C.xlight:cfg?"#fff":isWOD?C.xlight:C.light,lineHeight:1}}>{d}</span>
                          {cfg&&!beforeJoin&&<span style={{fontSize:"7px",color:"#fff",fontWeight:800,lineHeight:1}}>{cfg.l}</span>}
                          {!cfg&&isWOD&&!beforeJoin&&<span style={{fontSize:"7px",color:C.xlight,fontWeight:600,lineHeight:1}}>W</span>}
                        </button>;
                      })}
                    </div></>;
                    // Multi-slot: view-only main grid + per-slot editable calendars
                    return <>{GHead}
                      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:"3px"}}>
                        {Array.from({length:fd}).map((_,i)=><div key={`e${i}`}/>)}
                        {Array.from({length:dIM},(_,i)=>i+1).map(d=>{
                          const k=dk(sy,sm,d),dw=dow(sy,sm,d);
                          const tod=isToday(sy,sm,d),isWOD=isWO(s,dw);
                          const beforeJoin=s.joinDate&&new Date(k)<new Date(s.joinDate);
                          return <div key={d} style={{aspectRatio:"1",borderRadius:"8px",overflow:"hidden",border:`2px solid ${tod?C.gold:"transparent"}`,position:"relative",opacity:beforeJoin?.35:1}}>
                            <span style={{position:"absolute",top:"2px",left:0,right:0,textAlign:"center",fontSize:"9px",fontWeight:700,color:"rgba(0,0,0,.45)",zIndex:1,lineHeight:1}}>{d}</span>
                            <div style={{display:"flex",flexDirection:"column",width:"100%",height:"100%"}}>
                              {[...( s.slots||[])].sort((a,b)=>["M","A","E","N"].indexOf(a)-["M","A","E","N"].indexOf(b)).map((slotId)=>{const si2=(s.slots||[]).indexOf(slotId);const sk2=sk(k,si2>=0?si2:0);const eff2=(att[s.id]||{})[sk2]||null;const cfg2=eff2?STATUS[eff2]:null;return <div key={slotId} style={{flex:1,background:cfg2?cfg2.bg:isWOD?"#e2e8f0":"#f1f5f9"}}/>;})}</div>
                          </div>;
                        })}
                      </div>
                      {(()=>{
                        const SLOT_ORDER=["M","A","E","N"];
                        const sortedSlots=[...( s.slots||[])].sort((a,b)=>SLOT_ORDER.indexOf(a)-SLOT_ORDER.indexOf(b));
                        const activeSlot=openSlot[s.id]??null;
                        const slotM=SLOT_META[activeSlot]||{short:activeSlot||"",active:"#475569",calBg:"#f8fafc"};
                        return <>
                        <div style={{display:"flex",gap:"4px",background:"#f1f5f9",borderRadius:"12px",padding:"4px",marginBottom:"10px",marginTop:"12px"}}>
                          {sortedSlots.map(slotId=>{
                            const sm2=SLOT_META[slotId]||{short:slotId,bg:"#f1f5f9",active:"#475569"};
                            const isActive=activeSlot===slotId;
                            return <button key={slotId} onClick={()=>setOpenSlot(prev=>({...prev,[s.id]:prev[s.id]===slotId?null:slotId}))}
                              style={{flex:1,padding:"8px 4px",borderRadius:"9px",fontSize:"12px",fontWeight:700,border:"none",
                                background:isActive?sm2.active:sm2.bg,color:isActive?"#fff":sm2.active,
                                transition:"all .2s",letterSpacing:".3px"}}>
                              {sm2.short}
                            </button>;
                          })}
                        </div>
                        {activeSlot&&<div style={{background:slotM.calBg,borderRadius:"12px",padding:"10px",marginTop:"4px"}}>
                          <div style={{fontSize:"10px",color:slotM.active,fontWeight:700,letterSpacing:"1px",textTransform:"uppercase",marginBottom:"6px"}}>{slotM.short} Attendance</div>
                          {GHead}
                          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:"3px"}}>
                            {Array.from({length:fd}).map((_,i)=><div key={`e${i}`}/>)}
                            {Array.from({length:dIM},(_,i)=>i+1).map(d=>{
                              const k=dk(sy,sm,d),dw=dow(sy,sm,d);
                              const si=(s.slots||[]).indexOf(activeSlot);
                              const slotK=sk(k,si>=0?si:0);
                              const eff=(att[s.id]||{})[slotK]||null;
                              const cfg=eff?STATUS[eff]:null;
                              const tod=isToday(sy,sm,d),isWOD=isWO(s,dw);
                              const beforeJoin=s.joinDate&&new Date(k)<new Date(s.joinDate);
                              return <button key={d} onClick={()=>{if(editMode&&!beforeJoin&&!isWOD)cycleAtt(s.id,k,si>=0?si:0);}}
                                style={{aspectRatio:"1",borderRadius:"8px",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                                  border:`2px solid ${tod?C.gold:"transparent"}`,
                                  background:beforeJoin?"#f8fafc":cfg?cfg.bg:isWOD?"#f1f5f9":"transparent",
                                  cursor:editMode&&!beforeJoin&&!isWOD?"pointer":"default",opacity:beforeJoin?.35:1,gap:"1px"}}>
                                <span style={{fontSize:"10px",fontWeight:600,color:cfg?"#fff":isWOD?C.xlight:C.mid,lineHeight:1}}>{d}</span>
                                {cfg&&<span style={{fontSize:"7px",color:"#fff",fontWeight:800,lineHeight:1}}>{cfg.l}</span>}
                                {!cfg&&isWOD&&<span style={{fontSize:"7px",color:C.xlight,lineHeight:1}}>W</span>}
                              </button>;
                            })}
                          </div>
                        </div>}
                        </>;
                      })()}
                    </>;
                  })()}
                </div>
                <div style={{padding:"10px 14px 12px",display:"flex",justifyContent:"space-between",alignItems:"center",borderTop:`1px solid ${C.bg}`}}>
                  <span style={{fontSize:"12px",color:C.xlight}}>{c.cycleLabel}</span>
                  <span style={{fontWeight:800,color:C.text,fontSize:"15px"}}>{fmtC(c.net)} est.</span>
                </div>
              </div>;
            })
          }
        </div>
      )}

      {/* ══ STAFF ══ */}
      {tab==="staff"&&(
        <div className="su" style={{padding:"14px"}}>
          <button onClick={()=>setStaffMod("add")} style={{width:"100%",background:HDR,color:C.gold,borderRadius:"16px",padding:"16px",fontWeight:700,fontSize:"15px",display:"flex",alignItems:"center",justifyContent:"center",gap:"8px",marginBottom:"18px",border:"none",boxShadow:"0 4px 20px rgba(26,26,46,.25)"}}>
            + Add Staff Member
          </button>
          {activeStaff.length===0&&<Empty icon="👤" title="No staff yet" sub="Add your first team member"/>}
          {activeStaff.map(s=><StaffCard key={s.id} staff={s} att={att} onTap={()=>setProfileSheet(s.id)}/>)}
          {leftStaff.length>0&&<>
            <button onClick={()=>setShL(v=>!v)} style={{color:C.xlight,fontSize:"13px",fontWeight:600,display:"flex",alignItems:"center",gap:"6px",background:"none",border:"none",padding:"8px 0",marginTop:"4px"}}>
              {showLeft?"▲":"▼"} {showLeft?"Hide":"Show"} Past Staff ({leftStaff.length})
            </button>
            {showLeft&&leftStaff.map(s=><div key={s.id} style={{background:C.white,borderRadius:"18px",padding:"14px 16px",marginBottom:"10px",border:`1px solid ${C.border}`,opacity:0.6}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{display:"flex",alignItems:"center",gap:"10px",flex:1,minWidth:0}}>
                  <Avatar name={s.name} photo={s.photoUrl} size={36} fs={14}/>
                  <div style={{minWidth:0}}>
                    <div style={{fontWeight:700,color:C.mid,fontSize:"15px"}}>{s.name}</div>
                    <div style={{fontSize:"11px",color:C.xlight}}>{ri(s.role)} {s.role} · Left {fmtDate(s.leftDate)}</div>
                  </div>
                </div>
                <div style={{display:"flex",gap:"6px",flexShrink:0}}>
                  <button onClick={()=>setRestoreMod(s)} style={{background:"#dcfce7",borderRadius:"10px",padding:"7px 14px",color:C.green,fontSize:"12px",fontWeight:700,border:"none"}}>Restore</button>
                  {isAdmin&&<button onClick={()=>setDeleteMod(s)} style={{background:"#fff0f0",borderRadius:"10px",padding:"7px 10px",color:C.red,fontSize:"13px",fontWeight:700,border:"none"}}>🗑️</button>}
                </div>
              </div>
            </div>)}
          </>}
        </div>
      )}

      {/* ══ SALARY ══ */}
      {tab==="salary"&&(
        <div className="su" style={{padding:"14px"}}>
          {salDet?(
            <SalDetail
              staff={staff.find(s=>s.id===salDet)}
              calc={calcSal(staff.find(s=>s.id===salDet),att,sl,tlog,pays,advs,adjs,detY,detM)}
              advBalance={advBalanceOf(salDet)}
              pays={pays}
              isAdmin={isAdmin} H={H} sy={detY} sm={detM}
              onBack={()=>setSalDet(null)}
              onNavCycle={navDetCycle}
              onSettleCycle={()=>setSettleCyc({sid:salDet,year:detY,month:detM})}
              onOpenAdvLedger={()=>setAdvSheet(salDet)}
              onAdj={()=>setAdjMod(salDet)}
              onEditAdj={a=>setEditAdjMod(a)}
              onDeleteAdj={async id=>{try{await deleteAdj(id);showT("Adjustment deleted");}catch(e){showT(friendlyError(e.message),"error");}}}
              onWhatsApp={()=>whatsApp(staff.find(s=>s.id===salDet))}
            />
          ):(
            <>
              {/* ── Household roll-up header ── */}
              {(()=>{
                const today=new Date();
                let dueNow=0;
                for(const s of activeStaff){
                  if(!s.salary) continue;
                  const {boxes}=openCycleBoxes(s,pays,today);
                  for(const b of boxes){
                    if(b.accruing) continue;
                    if(b.state==='unpaid') dueNow+=calcSal(s,att,sl,tlog,pays,advs,adjs,b.year,b.month).net;
                    else if(b.state==='pending'){const p=payFor(pays,s.id,b.year,b.month);dueNow+=Number(p?.shortfall||0);}
                  }
                }
                const advOut=activeStaff.reduce((t,s)=>t+advBalanceOf(s.id),0);
                return <div style={{background:HDR,borderRadius:"18px",padding:"16px 18px",marginBottom:"14px",display:"flex",gap:"14px"}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:"10px",color:"rgba(255,255,255,.45)",fontWeight:700,letterSpacing:".6px",textTransform:"uppercase",marginBottom:"4px"}}>Due now</div>
                    <div style={{fontWeight:800,color:dueNow>0?C.gold:"#86efac",fontSize:"26px",lineHeight:1}}>{dueNow>0?fmtC(dueNow):"All clear ✓"}</div>
                  </div>
                  <div style={{width:"1px",background:"rgba(255,255,255,.12)"}}/>
                  <div style={{flex:1}}>
                    <div style={{fontSize:"10px",color:"rgba(255,255,255,.45)",fontWeight:700,letterSpacing:".6px",textTransform:"uppercase",marginBottom:"4px"}}>Advances out</div>
                    <div style={{fontWeight:800,color:advOut>0?"#fff":"rgba(255,255,255,.5)",fontSize:"26px",lineHeight:1}}>{advOut>0?fmtC(advOut):"₹0"}</div>
                  </div>
                </div>;
              })()}

              {activeStaff.length===0&&<Empty icon="💰" title="No staff" sub="Add staff first"/>}
              {activeStaff.map(s=>(
                <StaffSalaryCard key={s.id} s={s} att={att} sl={sl} tlog={tlog}
                  pays={pays} advs={advs} adjs={adjs} advBalance={advBalanceOf(s.id)}
                  onOpenCycle={(y,m)=>{setDetY(y);setDetM(m);setSalDet(s.id);}}
                  onSettle={(y,m)=>setSettleCyc({sid:s.id,year:y,month:m})}
                  onOpenAdv={()=>setAdvSheet(s.id)}
                />
              ))}
            </>
          )}
        </div>
      )}

      {/* ══ ON-CALL ══ */}
      {tab==="oncall"&&F.on_call_log&&<OnCallTab hid={hid} isAdmin={isAdmin} onToast={showT} oncallStaff={oncallStaff} setTab={setTab} onAddStaff={()=>setAddOncall(true)} onStaffDeleted={id=>setStaff(prev=>prev.filter(s=>s.id!==id))}/>}

      {/* ── Modals ── */}
      {overdueMod&&<Modal onClose={()=>setOverdueMod(false)} title="Salary Overdue" accent={C.red}>
        {(()=>{const overdueStaff=activeStaff.filter(s=>isSalaryOverdue(s,pays,TY,TM));
        return overdueStaff.length===0?<div style={{textAlign:"center",padding:"32px",color:C.xlight,fontSize:"14px"}}>No overdue salaries</div>:
        overdueStaff.map(s=>{const due=payDueDate(s,TY,TM);const dueLbl=`${due.getDate()} ${MONTHS[due.getMonth()]}`;return(
          <div key={s.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 0",borderBottom:`1px solid ${C.border}`}}>
            <div><div style={{fontWeight:600,fontSize:"14px",color:C.text}}>{s.name}</div><div style={{fontSize:"12px",color:C.red,marginTop:"2px"}}>Salary due since {dueLbl}</div></div>
            <button onClick={()=>{setOverdueMod(false);setTab("salary");openSalDet(s.id);}} style={{padding:"7px 14px",borderRadius:"10px",background:C.ind,color:C.gold,border:"none",fontWeight:700,fontSize:"12px"}}>Pay Now</button>
          </div>
        );})})()}
      </Modal>}
      {advBreakMod&&<Modal onClose={()=>setAdvBreakMod(false)} title="Active Advances" accent={C.amber}>
        {(()=>{
          // Group by staff: one row per staff with active advances
          const byStaff={};
          advs.filter(a=>{const s=staff.find(x=>x.id===a.staff_id);return s&&!s.left&&!a.cleared;}).forEach(a=>{
            if(!byStaff[a.staff_id]) byStaff[a.staff_id]={advs:[],staffObj:staff.find(x=>x.id===a.staff_id)||{}};
            byStaff[a.staff_id].advs.push(a);
          });
          const rows=Object.values(byStaff);
          if(rows.length===0) return <div style={{textAlign:"center",padding:"32px",color:C.xlight,fontSize:"14px"}}>No active advances</div>;
          return rows.map(({staffObj:s,advs:sAdvs})=>{
            const totalGiven=sAdvs.reduce((sum,a)=>sum+Number(a.total_amount||0),0);
            const totalRem=sAdvs.reduce((sum,a)=>sum+Number(a.remaining||0),0);
            const monthlyInst=sAdvs.filter(a=>a.recovery_type!=="full").reduce((sum,a)=>sum+Number(a.installment_amount||0),0);
            return <div key={s.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 0",borderBottom:`1px solid ${C.border}`}}>
              <div><div style={{fontWeight:600,fontSize:"14px",color:C.text}}>{s.name||"—"}</div><div style={{fontSize:"12px",color:C.xlight}}>{s.role||""}</div></div>
              <div style={{textAlign:"right"}}>
                <div style={{fontWeight:800,fontSize:"16px",color:C.amber}}>{fmtC(totalRem)}</div>
                <div style={{fontSize:"11px",color:C.xlight}}>of {fmtC(totalGiven)} given{monthlyInst>0?` · ${fmtC(monthlyInst)}/mo`:""}</div>
              </div>
            </div>;
          });
        })()}
      </Modal>}
      {profileSheet&&(()=>{
        const ps=staff.find(x=>x.id===profileSheet);
        if(!ps) return null;
        const markLeft=async()=>{try{await api(`/staff/${ps.id}`,"PUT",{name:ps.name,role:ps.role,salary:ps.salary,salary_type:ps.salaryType,week_off_days:ps.weekOffDays,cycle_start:ps.cycleStart,sessions:ps.sessions,time_tracking:ps.timeTracking,start_time:ps.startTime,std_hours:ps.stdHours,ot_enabled:ps.otEnabled,ot_type:ps.otType,ot_rate:ps.otRate,ot_multiplier:ps.otMultiplier,pf_enabled:ps.pfEnabled,esic_enabled:ps.esicEnabled,full_time:ps.fullTime,paid_leave_days:ps.paidLeaveDays,join_date:ps.joinDate,join_month:ps.joinMonth,join_year:ps.joinYear,is_left:true,left_date:new Date().toISOString().slice(0,10),phone:ps.phone},hid);setStaff(prev=>prev.map(x=>x.id===ps.id?{...x,left:true,leftDate:new Date().toISOString().slice(0,10)}:x));setProfileSheet(null);showT("Staff marked as no longer active");}catch(e){showT(friendlyError(e.message),"error");}};
        return <StaffProfileSheet staff={ps} isAdmin={isAdmin} pays={pays} hid={hid} api={api}
          onClose={()=>setProfileSheet(null)}
          onEdit={()=>{setProfileSheet(null);setStaffMod(ps);}}
          onIncr={()=>{setProfileSheet(null);setIncrMod(ps.id);}}
          onWhatsApp={()=>{setProfileSheet(null);whatsApp(ps);}}
          onMarkLeft={()=>{if(window.confirm(`Mark ${ps.name} as no longer working here?`))markLeft();}}
          onDelete={()=>{setProfileSheet(null);setDeleteMod(ps);}}
          onGoSalary={()=>{setProfileSheet(null);setTab("salary");openSalDet(ps.id);}}
          onGoAtt={()=>{setProfileSheet(null);setTab("attendance");setAV("calendar");}}
        />;
      })()}
      {staffMod&&<StaffModal staff={staffMod==="add"?null:staffMod} F={F} isAdmin={isAdmin} onClose={()=>setStaffMod(null)} onSave={async s=>{try{await saveStaff(s,staffMod==="add");setStaffMod(null);}catch(e){showT(friendlyError(e.message),"error");}}}/>}
      {addOncall&&<StaffModal defaultType="oncall" F={F} isAdmin={isAdmin} onClose={()=>setAddOncall(false)} onSave={async s=>{try{await saveStaff(s,true);setAddOncall(false);}catch(e){showT(friendlyError(e.message),"error");}}}/>}
      {deleteMod&&<Modal onClose={()=>setDeleteMod(null)} title="Delete Staff?" sub={deleteMod.name} accent={C.red}>
        <InfoBox color={C.red} bg="#ffe4e6" icon="⚠️">This will permanently remove <strong>{deleteMod.name}</strong> and all their records. This cannot be undone.</InfoBox>
        <div style={{display:"flex",gap:"10px",marginTop:"16px"}}>
          <button onClick={()=>setDeleteMod(null)} style={{flex:1,padding:"14px",borderRadius:"14px",border:`1.5px solid ${C.border}`,background:C.white,fontWeight:600,fontSize:"15px",color:C.text}}>Cancel</button>
          <button onClick={async()=>{try{await deleteStaff(deleteMod.id);}catch(e){showT(friendlyError(e.message),"error");setDeleteMod(null);}}} style={{flex:1,padding:"14px",borderRadius:"14px",border:"none",background:C.red,fontWeight:700,fontSize:"15px",color:"#fff"}}>Delete</button>
        </div>
      </Modal>}
      {restoreMod&&<RestoreModal staff={restoreMod} onClose={()=>setRestoreMod(null)} onRestore={async date=>{try{const s=restoreMod;await api(`/staff/${s.id}`,"PUT",{name:s.name,role:s.role,salary:s.salary,salary_type:s.salaryType,week_off_days:s.weekOffDays,cycle_start:s.cycleStart,sessions:s.sessions,time_tracking:s.timeTracking,start_time:s.startTime,std_hours:s.stdHours,ot_enabled:s.otEnabled,ot_type:s.otType,ot_rate:s.otRate,ot_multiplier:s.otMultiplier,pf_enabled:s.pfEnabled,esic_enabled:s.esicEnabled,full_time:s.fullTime,paid_leave_days:s.paidLeaveDays,join_date:date,is_left:false,left_date:null,phone:s.phone},hid);setStaff(prev=>prev.map(x=>x.id===s.id?{...x,left:false,leftDate:"",joinDate:date}:x));setRestoreMod(null);showT(`${s.name} restored to active`);}catch(e){showT(friendlyError(e.message),"error");}}}/>}
      {advMod&&<AdvModal name={(staff.find(s=>s.id===advMod)||{}).name} sid={advMod} onClose={()=>setAdvMod(null)} onSave={async a=>{try{await addAdvance(a);setAdvMod(null);}catch(e){showT(friendlyError(e.message),"error");}}}/>}
      {timeMod&&<TimeModal sid={timeMod.sid} d={timeMod.d} staffMember={staff.find(s=>s.id===timeMod.sid)} existing={(tlog[timeMod.sid]||{})[timeMod.d]} onClose={()=>setTimeMod(null)} onSave={async(i,o)=>{try{await saveTime(timeMod.sid,timeMod.d,i,o);setTimeMod(null);}catch(e){showT(friendlyError(e.message),"error");}}}/>}
      {remMod&&<RemModal name={(staff.find(s=>s.id===remMod.sid)||{}).name} d={remMod.d} existing={(remarks[remMod.sid]||{})[remMod.d]} onClose={()=>setRemMod(null)} onSave={async t=>{try{await saveRem(remMod.sid,remMod.d,t);setRemMod(null);}catch(e){showT(friendlyError(e.message),"error");}}}/>}
      {incrMod&&<IncrModal name={(staff.find(s=>s.id===incrMod)||{}).name} cur={(staff.find(s=>s.id===incrMod)||{}).salary||0} onClose={()=>setIncrMod(null)} onSave={async(dt,amt,rsn)=>{try{await saveIncr(incrMod,dt,amt,rsn);setIncrMod(null);}catch(e){showT(friendlyError(e.message),"error");}}}/>}
      {payMod&&<PayModal name={(staff.find(s=>s.id===payMod)||{}).name} calc={calcSal(staff.find(s=>s.id===payMod)||null,att,sl,tlog,pays,advs,adjs,detY,detM)} onClose={()=>setPayMod(null)} onSave={async(amt,meth,by,note)=>{try{await recordPayment(payMod,amt,meth,by,note);setPayMod(null);setSalDet(null);}catch(e){showT(friendlyError(e.message),"error");}}}/>}
      {settleCyc&&(()=>{
        const s=staff.find(x=>x.id===settleCyc.sid); if(!s) return null;
        const c=calcSal(s,att,sl,tlog,pays,advs,adjs,settleCyc.year,settleCyc.month);
        const gate=cycleUnmarked(s,att,sl,settleCyc.year,settleCyc.month,new Date());
        return <SettleScreen staff={s} calc={c} advBalance={advBalanceOf(settleCyc.sid)}
          unmarked={gate.unmarked} requiredDays={gate.required}
          year={settleCyc.year} month={settleCyc.month} isAdmin={isAdmin}
          onClose={()=>setSettleCyc(null)}
          onSettle={async(amt,opts)=>{try{await settleCycle(settleCyc.sid,settleCyc.year,settleCyc.month,c.net,amt,opts);setSettleCyc(null);}catch(e){showT(friendlyError(e.message),"error");}}}
        />;
      })()}
      {advSheet&&(()=>{
        const s=staff.find(x=>x.id===advSheet); if(!s) return null;
        return <AdvLedgerSheet staff={s} hid={hid} isAdmin={isAdmin} api={api}
          onClose={()=>setAdvSheet(null)}
          onEntry={async(type,amt,note,date)=>{const r=await addLedgerEntry(advSheet,type,amt,note,date);showT(type==='given'?"Advance recorded":type==='repaid'?"Repayment recorded":"Written off");return r;}}
          friendlyError={friendlyError}
        />;
      })()}
      {adjMod&&<AdjModal name={(staff.find(s=>s.id===adjMod)||{}).name} onClose={()=>setAdjMod(null)} onSave={async(amt,note)=>{try{await addAdj(adjMod,amt,note);setAdjMod(null);}catch(e){showT(friendlyError(e.message),"error");}}}/>}
      {editAdjMod&&<AdjModal name={(staff.find(s=>s.id===salDet)||{}).name} existing={editAdjMod} onClose={()=>setEditAdjMod(null)} onSave={async(amt,note)=>{try{await editAdj(editAdjMod.id,amt,note);setEditAdjMod(null);}catch(e){showT(friendlyError(e.message),"error");}}}/>}
      {settMod&&<SettingsModal F={F} H={H} hid={hid} isAdmin={isAdmin} user={authUser} activeHH={activeHH} onClose={()=>setSettMod(false)} onToast={showT}
        showSalAmt={showSalAmt} onToggleSalAmt={v=>{setShowSalAmt(v);try{localStorage.setItem('gs_show_sal_amt',v?'true':'false');}catch{}}}
        staff={activeStaff}
        onSaveF={async f=>{await api("/auth/features","PUT",f,hid);setHHD(prev=>({...prev,features:f}));if(!f.on_call_log&&tab==="oncall")setTab("today");showT("Settings saved");}}
        onSaveH={async h=>{await api(`/auth/household/${hid}`,"PUT",h,hid);setHHD(prev=>({...prev,household:{...prev.household,...h}}));showT("Household details saved");}}
        onSignOut={()=>{clearAuth();if(pollRef.current)clearInterval(pollRef.current);window.location.reload();}}
        hhs={hhs} onSwitchHH={m=>{setSettMod(false);enterHousehold(m);}}
        onCreateHH={async(nm,addr,city,type)=>{const r=await api("/auth/household","POST",{name:nm,address:addr,city,type});const newMem={household_id:r.id,household_name:r.name,role:"admin",city:r.city,type:r.type,staff_count:0,...r};setHHs(prev=>[...prev,newMem]);setSettMod(false);enterHousehold(newMem);showT("Household created");}}
      />}
      {notifMod&&(()=>{
        // Compute salary due alerts (frontend-only, not from DB)
        const salDueAlerts=activeStaff.filter(s=>{
          if(!s.salary||s.salary===0) return false;
          const dc=getDefaultCycle(s,new Date());
          if(isCycleRunning(s,dc.year,dc.month)) return false;
          const paid=(pays||[]).some(p=>p.staff_id===s.id&&Number(p.year)===dc.year&&Number(p.month)===dc.month&&p.locked);
          if(paid) return false;
          const due=payDueDate(s,dc.year,dc.month);
          return new Date()>=due;
        }).map(s=>{
          const dc=getDefaultCycle(s,new Date());
          const due=payDueDate(s,dc.year,dc.month);
          return {staffId:s.id,name:s.name,dueDate:due,cycle:dc,label:`${FULLM[dc.month]} cycle`};
        });
        return <NotifModal notifs={notifs} salDueAlerts={salDueAlerts} hid={hid} isAdmin={isAdmin} onClose={()=>setNotifMod(false)} onToast={showT}
          onGoSalary={sid=>{setNotifMod(false);setTab("salary");openSalDet(sid);}}
          onResolved={async()=>{const n=await api("/staff/change-requests","GET",null,hid).catch(()=>[]);if(Array.isArray(n)){setNotifs(n);setHHD(prev=>prev?{...prev,pendingRequests:n.filter(x=>x.status==="pending").length}:prev);if(!activeHH)return;const sd=await api("/staff","GET",null,hid);setStaff(sd.map(norm));}}
          }/>;
      })()}
      {hhSwitch&&<HHSwitchModal hhs={hhs} currentId={hid} onClose={()=>setHHSwitch(false)} onSelect={m=>{setHHSwitch(false);enterHousehold(m);}}
        onCreateNew={async(nm,addr,city,type)=>{const r=await api("/auth/household","POST",{name:nm,address:addr,city,type});const m={household_id:r.id,household_name:r.name,role:"admin",city:r.city,type:r.type,staff_count:0,...r};setHHs(prev=>[...prev,m]);setHHSwitch(false);enterHousehold(m);showT("Household created");}}
      />}
      {creatingHH&&<CreateHHModal onClose={()=>setCHH(false)} onSave={async(nm,addr,city,type)=>{const r=await api("/auth/household","POST",{name:nm,address:addr,city,type});const m={household_id:r.id,household_name:r.name,role:"admin",city:r.city,type:r.type,staff_count:0,...r};setHHs(prev=>[...prev,m]);setCHH(false);enterHousehold(m);}}/>}
    </div>
  );
}

// ── Day Card ──────────────────────────────────────────────────
function DayCard({staff:s,att,sl,tlog,remarks,sd,sm,sy,editMode,F,onCycle,onTime,onRem}) {
  const d=dk(sy,sm,sd);
  const slots=s.slots||(s.sessions>1?["M","E"]:["M"]);
  const isMulti=slots.length>1;
  const eff=isMulti?null:getEff(att,sl,s.id,d);
  const hasSL=(sl[s.id]||{})[d];
  const cfg=eff?STATUS[eff]:null;
  const tEntry=(tlog[s.id]||{})[d];
  const rem=(remarks[s.id]||{})[d];
  const dw=dow(sy,sm,sd);
  const isWOD=isWO(s,dw);
  const fut=isFut(sy,sm,sd);
  const lateBy=tEntry?.inTime&&s.startTime?Math.max(0,t2m(tEntry.inTime)-t2m(s.startTime)):0;
  const isFTP=s.fullTime&&!eff&&!hasSL&&!isWOD&&!fut;

  return <div style={{background:C.white,borderRadius:"18px",padding:"14px 16px",marginBottom:"10px",boxShadow:"0 2px 12px rgba(0,0,0,.05)",border:`1px solid ${C.border}`}}>
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
      <div style={{display:"flex",alignItems:"center",gap:"12px",flex:1,minWidth:0}}>
        <Avatar name={s.name} photo={s.photoUrl} size={42} fs={16}/>
        <div style={{minWidth:0}}>
          <div style={{fontWeight:700,fontSize:"15px",color:C.text}}>{s.name}</div>
          <div style={{fontSize:"11px",color:C.xlight,marginTop:"1px"}}>
            {ri(s.role)} {s.role}
            {isWOD&&!eff&&!isFTP&&<span style={{color:C.light}}> · Week off</span>}
            {hasSL&&!eff&&<span style={{color:C.purple}}> · Leave planned</span>}
            {tEntry?.inTime&&<span style={{color:C.green}}> · {tEntry.inTime}{tEntry.outTime?`–${tEntry.outTime}`:""}</span>}
          </div>
        </div>
      </div>
      <div style={{display:"flex",alignItems:"center",gap:"5px",flexShrink:0}}>
        {s.timeTracking&&!fut&&(
          <button onClick={onTime} style={{height:"32px",borderRadius:"9px",padding:"0 8px",background:"#f0f4ff",border:"1px solid #c7d2fe",fontSize:"11px",fontWeight:700,color:"#4f46e5",display:"flex",alignItems:"center",gap:"3px"}}>
            ⏱{tEntry?.inTime?"Edit":"Log"}
          </button>
        )}
        <div style={{display:"flex",flexDirection:"column",gap:"3px"}}>
          {slots.map((slCode,si)=>{
            const k=isMulti?sk(d,si):d;
            const sa=isMulti?(att[s.id]||{})[k]||null:eff;
            const sc=sa?STATUS[sa]:null;
            return <div key={slCode} style={{display:"flex",alignItems:"center",gap:"3px"}}>
              {isMulti&&<span style={{fontSize:"9px",color:C.xlight,width:"10px",textAlign:"center"}}>{slCode}</span>}
              <button onClick={()=>onCycle(si)} style={{width:"46px",height:"26px",borderRadius:"8px",background:sc?sc.bg:isMulti?(editMode?C.bg:"#f8fafc"):(hasSL&&!sa?C.purple:isFTP?C.green:isWOD?C.light:fut?"#f5f3ff":(editMode?C.bg:"#f8fafc")),color:sc?sc.fg:isMulti?(editMode?C.light:C.xlight):(hasSL&&!sa?"#fff":isFTP?"#fff":isWOD?"#fff":(editMode?C.light:C.border)),fontWeight:800,fontSize:"12px",border:fut?`1.5px dashed ${C.purple}`:"none",cursor:editMode||fut?"pointer":"default"}}>
                {sc?sc.l:isMulti?"—":(hasSL&&!sa?"L":isFTP?"P":isWOD?"W":fut?"?":"—")}
              </button>
            </div>;
          })}
        </div>
      </div>
    </div>
    {(rem||lateBy>0)&&(
      <div style={{marginTop:"8px",display:"flex",gap:"5px",flexWrap:"wrap"}}>
        {lateBy>0&&<Chip label={`⚠ Late ${lateBy}m`} color="#c2410c" bg="#fff7ed" sm/>}
        {rem&&<Chip label={`📝 ${rem.slice(0,40)}${rem.length>40?"…":""}`} color="#92400e" bg="#fef3c7" sm/>}
      </div>
    )}
  </div>;
}

// ── Staff Card (minimal, tappable) ────────────────────────────
function StaffCard({staff:s,att,onTap}) {
  const todayDk=dk(TY,TM,TD);
  const todayDW=dow(TY,TM,TD);
  const isWOD=isWO(s,todayDW);
  const todayStatus=(att[s.id]||{})[todayDk];
  const attLabel=isWOD?"Week Off":todayStatus==="P"?"Present":todayStatus==="A"?"Absent":todayStatus==="H"?"Half Day":todayStatus==="L"?"On Leave":"Not marked";
  const attColor=isWOD?C.light:todayStatus==="P"?C.green:todayStatus==="A"?C.red:todayStatus==="H"?C.amber:C.xlight;
  const attBg=isWOD?"#f1f5f9":todayStatus==="P"?"#dcfce7":todayStatus==="A"?"#fee2e2":todayStatus==="H"?"#fef3c7":"#f1f5f9";
  const cs=s.cycleStart||1;
  return <button onClick={onTap} style={{width:"100%",background:C.white,borderRadius:"16px",padding:"14px 16px",marginBottom:"10px",display:"flex",alignItems:"center",gap:"12px",border:`1px solid ${C.border}`,boxShadow:"0 1px 6px rgba(0,0,0,.04)",textAlign:"left"}}>
    <Avatar name={s.name} photo={s.photoUrl} size={44} fs={17}/>
    <div style={{flex:1,minWidth:0}}>
      <div style={{fontWeight:700,fontSize:"15px",color:C.text}}>{s.name}</div>
      <div style={{fontSize:"12px",color:C.xlight,marginTop:"1px"}}>{ri(s.role)} {s.role}</div>
      {s.phone&&<div style={{fontSize:"11px",color:C.light,marginTop:"1px"}}>{s.phone}</div>}
    </div>
    <div style={{display:"flex",flexDirection:"column",gap:"5px",alignItems:"flex-end",flexShrink:0}}>
      <span style={{background:C.bg,color:C.light,fontSize:"10px",fontWeight:600,borderRadius:"6px",padding:"2px 8px",whiteSpace:"nowrap"}}>Settles {ord(cs)}</span>
      <span style={{background:attBg,color:attColor,fontSize:"10px",fontWeight:700,borderRadius:"20px",padding:"2px 8px",whiteSpace:"nowrap"}}>{attLabel}</span>
    </div>
  </button>;
}

// ── Staff Profile Sheet ────────────────────────────────────────
function StaffProfileSheet({staff:s,isAdmin,pays,hid,api,onClose,onEdit,onIncr,onWhatsApp,onMarkLeft,onDelete,onGoSalary,onGoAtt}) {
  const [ledger,setLedger]=useState(null); // {balance,entries}
  useEffect(()=>{if(hid&&api)api(`/salary/advance-ledger/${s.id}`,"GET",null,hid).then(setLedger).catch(()=>{});},[s.id]); // eslint-disable-line
  const offDays=(s.weekOffDays||[]).map(d=>WDAYS[d]).join(", ")||"None";
  const hist=(s.salaryHistory||[]).slice().sort((a,b)=>b.date.localeCompare(a.date));
  const joinStr=s.joinDate?fmtDate(s.joinDate):s.joinMonth?`${MONTHS[s.joinMonth-1]} ${s.joinYear||""}`:null;
  const cs=s.cycleStart||1;
  function ActionBtn({icon,label,onClick,danger,color}){
    return <button onClick={onClick} style={{background:danger?"#fff0f0":C.bg,border:`1px solid ${danger?"#fecaca":C.border}`,borderRadius:"14px",padding:"12px 10px",display:"flex",flexDirection:"column",alignItems:"center",gap:"5px",cursor:"pointer",flex:1}}>
      <span style={{fontSize:"20px"}}>{icon}</span>
      <span style={{fontSize:"11px",fontWeight:700,color:danger?C.red:C.mid,textAlign:"center",lineHeight:1.2}}>{label}</span>
    </button>;
  }
  return <Modal onClose={onClose} title="" accent={C.gold}>
    {/* Header */}
    <div style={{display:"flex",alignItems:"center",gap:"14px",marginBottom:"20px"}}>
      <Avatar name={s.name} photo={s.photoUrl} size={64} fs={24}/>
      <div>
        <div style={{fontWeight:800,fontSize:"18px",color:C.text}}>{s.name}</div>
        <div style={{fontSize:"13px",color:C.light,marginTop:"2px"}}>{ri(s.role)} {s.role}</div>
        {s.phone&&<div style={{fontSize:"12px",color:C.light,marginTop:"2px"}}>{s.phone}</div>}
        {joinStr&&<div style={{fontSize:"11px",color:C.xlight,marginTop:"2px"}}>Joined {joinStr}</div>}
        <div style={{marginTop:"5px"}}><span style={{background:"#f0f4ff",color:"#4f46e5",fontSize:"10px",fontWeight:700,borderRadius:"20px",padding:"2px 9px"}}>Settles {ord(cs)}</span></div>
      </div>
    </div>
    {/* Actions grid */}
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"8px",marginBottom:"20px"}}>
      <ActionBtn icon="💰" label="View Salary" onClick={onGoSalary}/>
      <ActionBtn icon="📅" label="Attendance" onClick={onGoAtt}/>
      <ActionBtn icon="📱" label="WhatsApp Slip" onClick={onWhatsApp}/>
      {isAdmin&&<ActionBtn icon="✏️" label="Edit Profile" onClick={onEdit}/>}
      {isAdmin&&<ActionBtn icon="↑" label="Increment" onClick={onIncr}/>}
      {isAdmin&&<ActionBtn icon="👋" label="Mark as Left" onClick={onMarkLeft} danger/>}
    </div>
    {/* Info grid */}
    <div style={{fontSize:"11px",color:C.xlight,fontWeight:700,letterSpacing:".8px",marginBottom:"8px"}}>DETAILS</div>
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"7px",marginBottom:"16px"}}>
      {[
        {l:"Salary",v:`${fmtC(s.salary||0)}/${s.salaryType==="monthly"?"mo":"day"}`},
        {l:"Week Off",v:offDays},
        {l:"Slots",v:(s.slots||["M"]).join(", ")},
        {l:"Cycle",v:`Settles ${ord(cs)}`},
        ...(s.paidLeaveDays>0?[{l:"Paid Leave",v:`${s.paidLeaveDays} days`}]:[]),
        ...(s.pfEnabled?[{l:"PF",v:"12% deducted"}]:[]),
        ...(s.esicEnabled?[{l:"ESIC",v:"0.75% if ≤₹21k"}]:[]),
      ].map(x=><div key={x.l} style={{background:C.bg,borderRadius:"10px",padding:"8px 12px",border:`1px solid ${C.border}`}}>
        <div style={{fontSize:"9px",color:C.xlight,fontWeight:700,letterSpacing:".5px",marginBottom:"2px"}}>{x.l.toUpperCase()}</div>
        <div style={{fontWeight:600,color:C.text,fontSize:"12px"}}>{x.v}</div>
      </div>)}
    </div>
    {/* Payment History — read-only audit trail */}
    <div style={{fontSize:"11px",color:C.xlight,fontWeight:700,letterSpacing:".8px",marginBottom:"8px",marginTop:"4px"}}>PAYMENT HISTORY</div>
    {(()=>{
      const pmts=(pays||[]).filter(p=>p.staff_id===s.id).slice().sort((a,b)=>b.year!==a.year?Number(b.year)-Number(a.year):Number(b.month)-Number(a.month));
      if(!pmts.length)return <div style={{fontSize:"12px",color:C.xlight,textAlign:"center",padding:"8px 0"}}>No settled cycles yet.</div>;
      return pmts.map((p,i)=>{
        const cyLbl=cyclePeriodLabel(s,Number(p.year),Number(p.month));
        const pending=p.state==='pending';
        const sf=Number(p.shortfall||0);
        return <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
          <div style={{minWidth:0}}>
            <div style={{fontWeight:600,fontSize:"13px",color:C.text}}>{MONTHS[Number(p.month)]} {p.year}</div>
            <div style={{fontSize:"10px",color:C.xlight}}>{cyLbl} · {p.method||"—"}{p.date_paid?` · ${fmtDate(p.date_paid)}`:""}</div>
          </div>
          <div style={{textAlign:"right",flexShrink:0,marginLeft:"8px"}}>
            <div style={{fontWeight:700,fontSize:"13px",color:pending?C.red:C.green}}>{fmtC(Number(p.amount_paid||0))}</div>
            <div style={{fontSize:"10px",fontWeight:700,color:pending?C.red:C.green}}>{pending?`Pending · owe ${fmtC(sf)}`:"Settled ✓"}</div>
          </div>
        </div>;
      });
    })()}
    {/* Advance movements */}
    {ledger&&ledger.entries?.length>0&&<>
      <div style={{fontSize:"11px",color:C.xlight,fontWeight:700,letterSpacing:".8px",marginBottom:"8px",marginTop:"16px"}}>ADVANCE MOVEMENTS{ledger.balance>0?` · ${fmtC(ledger.balance)} outstanding`:""}</div>
      {ledger.entries.slice().reverse().map(e=>{
        const LBL={given:"Given",cycle_overpay:"Cycle overpay",recovered:"Recovered",repaid:"Repaid",written_off:"Written off"};
        const neg=Number(e.amount)<0;
        return <div key={e.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"7px 0",borderBottom:`1px dashed ${C.bg}`}}>
          <div style={{minWidth:0}}><div style={{fontSize:"12px",color:C.text,fontWeight:600}}>{LBL[e.entry_type]||e.entry_type}{e.note?` · ${e.note}`:""}</div><div style={{fontSize:"10px",color:C.xlight}}>{fmtDate(e.entry_date)}</div></div>
          <div style={{textAlign:"right",flexShrink:0,marginLeft:"8px"}}><div style={{fontSize:"13px",fontWeight:700,color:neg?C.green:C.amber}}>{neg?"–":"+"}{fmtC(Math.abs(Number(e.amount)))}</div><div style={{fontSize:"10px",color:C.xlight}}>Bal {fmtC(Number(e.running))}</div></div>
        </div>;
      })}
    </>}
    {/* Delete — admin only */}
    {isAdmin&&<div style={{marginTop:"20px"}}>
      <button onClick={onDelete} style={{width:"100%",background:"#fff0f0",border:`1px solid #fecaca`,borderRadius:"12px",padding:"12px",color:C.red,fontWeight:700,fontSize:"13px"}}>Permanently Delete Staff</button>
    </div>}
  </Modal>;
}

// ── Salary Detail ─────────────────────────────────────────────
function SalDetail({staff:s,calc:c,advs,advBalance=0,isAdmin,H,sy,sm,pays,onBack,onNavCycle,onSettleCycle,onOpenAdvLedger,onAdj,onEditAdj,onDeleteAdj,onWhatsApp}) {
  if(!s) return null;
  const [adjOpen,setAdjOpen] = useState(false);
  const DR=c.dailyRate||0;
  const hasPF=s.pfEnabled; const hasESIC=s.esicEnabled;
  const absDeduction=Math.round(DR*(Math.max(0,c.A-(c.woCredit||0))+c.H*0.5));
  const pay=payFor(pays,s.id,sy,sm);
  const settled=pay&&pay.state==='settled';
  const pending=pay&&pay.state==='pending';

  function SRow({label,value,sub,color,bold,divider}){
    return <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
      padding:"8px 0",borderTop:divider?`1px solid ${C.border}`:undefined,marginTop:divider?"6px":undefined}}>
      <span style={{fontSize:"13px",color:sub?C.xlight:C.mid,fontWeight:bold?700:400}}>{label}</span>
      <span style={{fontSize:"13px",fontWeight:bold?700:600,color:color||C.text,textAlign:"right"}}>{value}</span>
    </div>;
  }
  function SecHdr({title}){
    return <div style={{fontSize:"10px",fontWeight:700,letterSpacing:"1px",textTransform:"uppercase",color:C.xlight,marginBottom:"6px",marginTop:"4px"}}>{title}</div>;
  }

  const running=isCycleRunning(s,sy,sm);
  const periodLbl=cyclePeriodLabel(s,sy,sm);
  // Disable next arrow if the next cycle would be in the future
  const nextRunning=(()=>{let m=sm+1,y=sy;if(m>11){m=0;y++;}return isCycleRunning(s,y,m);})();
  return <div>
    <button onClick={onBack} style={{display:"flex",alignItems:"center",gap:"6px",color:C.text,fontWeight:700,fontSize:"14px",marginBottom:"12px",background:"none",border:"none"}}>← Back</button>

    {/* ── Cycle navigation ── */}
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",background:C.white,borderRadius:"14px",padding:"10px 14px",marginBottom:"10px",border:`1px solid ${C.border}`}}>
      <button onClick={()=>onNavCycle&&onNavCycle(-1)} style={{background:C.bg,border:"none",borderRadius:"8px",width:"30px",height:"30px",fontSize:"18px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:C.light}}>‹</button>
      <div style={{textAlign:"center"}}>
        <div style={{fontWeight:700,fontSize:"13px",color:C.text}}>{FULLM[sm]} {sy}</div>
        <div style={{fontSize:"11px",color:C.xlight}}>{periodLbl}</div>
      </div>
      <button onClick={()=>onNavCycle&&onNavCycle(1)} disabled={nextRunning} style={{background:nextRunning?"#f1f5f9":C.bg,border:"none",borderRadius:"8px",width:"30px",height:"30px",fontSize:"18px",cursor:nextRunning?"default":"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:nextRunning?C.xlight:C.light,opacity:nextRunning?.4:1}}>›</button>
    </div>
    {running&&<div style={{background:"#eff6ff",borderRadius:"12px",padding:"10px 14px",marginBottom:"10px",fontSize:"12px",color:"#1d4ed8",fontWeight:600,display:"flex",alignItems:"center",gap:"6px"}}>🔄 This cycle is still running — salary shown is partial.</div>}
  {(()=>{
    const today=new Date();
    const days=cycleDays(s,sy,sm);
    const wDays=days.filter(({y,m,d})=>{if(isWO(s,dow(y,m,d)))return false;if(new Date(y,m,d)>today)return false;if(s.joinDate&&dk(y,m,d)<s.joinDate)return false;return true;});
    if(!wDays.length)return null;
    const skipDays=s.joinDate?wDays.filter(({y,m,d})=>dk(y,m,d)<s.joinDate).length:0;
      const marked=c.P+c.A+c.H;
      const unmarked=Math.max(0,wDays.length-marked-skipDays);
    if(unmarked===0)return null;
    const pct=unmarked/wDays.length;
    if(pct>0.25)return <div style={{background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:"12px",padding:"12px 14px",marginBottom:"10px"}}>
      <div style={{fontWeight:700,fontSize:"13px",color:"#92400e",marginBottom:"4px"}}>⚠ {unmarked} days have no attendance marked</div>
      <div style={{fontSize:"12px",color:"#b45309",marginBottom:"8px"}}>These days are counted as absent. Mark attendance first for an accurate calculation.</div>
      <button onClick={()=>onBack&&onBack()} style={{background:"#f59e0b",color:"#fff",border:"none",borderRadius:"8px",padding:"6px 12px",fontSize:"12px",fontWeight:700,cursor:"pointer"}}>← Go Mark Attendance</button>
    </div>;
    return <div style={{fontSize:"11px",color:C.xlight,marginBottom:"8px"}}>ⓘ {unmarked} day{unmarked>1?"s":""} unmarked — counted as absent</div>;
  })()}

    {/* ── 1. Header ── */}
    <div style={{background:HDR,borderRadius:"20px",padding:"20px",marginBottom:"14px"}}>
      <div style={{display:"flex",alignItems:"center",gap:"12px",marginBottom:"16px"}}>
        <Avatar name={s.name} photo={s.photoUrl} size={46} fs={18}/>
        <div style={{flex:1}}>
          <div style={{fontFamily:"'DM Serif Display',serif",fontSize:"22px",color:C.gold}}>{s.name}</div>
          <div style={{color:"rgba(255,255,255,.4)",fontSize:"12px"}}>{ri(s.role)} {s.role} · {c.cycleLabel}</div>
        </div>
        {c.locked&&<Chip label="🔒" color="#34d399" bg="rgba(52,211,153,.15)"/>}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"8px"}}>
        {[{v:c.P,cl:C.green,l:"Present"},{v:c.A,cl:C.red,l:"Absent"},{v:c.H,cl:C.amber,l:"Half"},{v:c.W,cl:"rgba(255,255,255,.5)",l:"Week Off"}].map(x=><div key={x.l} style={{background:"rgba(255,255,255,.07)",borderRadius:"10px",padding:"8px 6px",textAlign:"center"}}>
          <div style={{fontWeight:800,fontSize:"20px",color:x.cl,lineHeight:1}}>{x.v}</div>
          <div style={{fontSize:"9px",color:"rgba(255,255,255,.3)",fontWeight:600,marginTop:"2px"}}>{x.l.toUpperCase()}</div>
        </div>)}
      </div>
    </div>

    {/* ── 2. Tabular Breakdown ── */}
    <div style={{background:C.white,borderRadius:"18px",padding:"16px 18px",marginBottom:"12px",border:`1px solid ${C.border}`}}>
      <SecHdr title="Attendance"/>
      {s.salaryType==="monthly"&&<SRow label="Present" value={`${c.P} day${c.P!==1?"s":""}`}/>}
      {c.W>0&&<SRow label="Week Off" value={`${c.W} days`} sub color={C.light}/>}
      {c.A>0&&<SRow label="Absent" value={`${c.A} day${c.A!==1?"s":""}`} color={C.red}/>}
      {c.H>0&&<SRow label="Half Day" value={`${c.H} day${c.H!==1?"s":""}`} color={C.amber}/>}
      {c.paidLeavesUsed>0&&<SRow label="Paid Leave" value={`${c.paidLeavesUsed} of ${s.paidLeaveDays||0} quota`} color={C.green}/>}
      {c.woWorked>0&&<SRow label="WO Day Worked" value={`${c.woWorked} day${c.woWorked!==1?"s":""}`} color={C.purple}/>}
      {c.woCredit>0&&<SRow label="WO Credit" value={`offset ${c.woCredit} absence${c.woCredit!==1?"s":""}`} sub color={C.green}/>}

      <SecHdr title="Earnings" />
      <SRow label={`Monthly Salary`} value={fmtC(s.salary||0)}/>
      {s.salaryType==="monthly"&&<SRow label={`Daily Rate (÷${c.daysInCycle})`} value={`₹${DR.toFixed(2)}`} sub/>}
      {s.salaryType==="monthly"&&<SRow label="Days counted" value={`${c.daysInCycle}`} sub/>}
      <SRow label="Salary Earned" value={fmtC(c.earned)} bold color={C.green}/>
      {s.salaryType==="monthly"&&c.daysInCycle>0&&<div style={{fontSize:"11px",color:C.xlight,marginTop:"-4px",marginBottom:"6px"}}>
        {`(${c.P} present + ${c.W} week off${c.paidLeavesUsed>0?` + ${c.paidLeavesUsed} leave`:""}${c.woWorked>c.woCredit?` + ${c.woWorked-c.woCredit} OT`:""}) × ₹${DR.toFixed(0)}`}
      </div>}
      {c.otAmt>0&&<SRow label="Overtime" value={`+${fmtC(c.otAmt)}`} color={C.purple}/>}

      {(c.A>0||c.H>0||hasPF||hasESIC||c.advDed>0)&&<>
        <SecHdr title="Deductions"/>
        {(c.A>0||c.H>0)&&<SRow label={`Absent deduction${c.woCredit>0?` (${c.A}A - ${c.woCredit} credit)`:""}`} value={absDeduction>0?`–${fmtC(absDeduction)}`:fmtC(0)} color={absDeduction>0?C.red:C.text}/>}
        {hasPF&&<SRow label="PF 12%" value={`–${fmtC(c.pfDed)}`} color={C.red}/>}
        {hasESIC&&<SRow label="ESIC 0.75%" value={`–${fmtC(c.esicDed)}`} color={C.red}/>}
        {c.advDed>0&&<SRow label="Advance adjusted" value={`–${fmtC(c.advDed)}`} color={C.red}/>}
      </>}

      {c.adjs.length>0&&<>
        <SecHdr title="Adjustments"/>
        {c.adjs.map(a=><SRow key={a.id} label={a.note||"Adjustment"} value={`${Number(a.amount)>0?"+":""}${fmtC(a.amount)}`} color={Number(a.amount)>0?C.green:C.red}/>)}
        <SRow label="Net adjustments" value={`${c.adjNet>0?"+":""}${fmtC(c.adjNet)}`} bold color={c.adjNet>0?C.green:c.adjNet<0?C.red:C.text}/>
      </>}

      <div style={{borderTop:`2px solid ${C.border}`,marginTop:"10px",paddingTop:"10px"}}>
        <SRow label="Gross" value={fmtC(c.gross)} sub/>
        <SRow label="Total Deductions" value={c.totalDed>0?`–${fmtC(c.totalDed)}`:fmtC(0)} sub color={c.totalDed>0?C.red:C.text}/>
        {c.carryFwd!==0&&<SRow label="Carry Forward" value={`${c.carryFwd>0?"+":""}${fmtC(c.carryFwd)}`} sub color={c.carryFwd>0?C.green:C.red}/>}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:"8px",paddingTop:"8px",borderTop:`1px dashed ${C.border}`}}>
          <span style={{fontWeight:700,color:C.text,fontSize:"16px"}}>Net Payable</span>
          <span style={{fontWeight:800,color:C.text,fontSize:"36px",lineHeight:1}}>{fmtC(c.net)}</span>
        </div>
      </div>
    </div>

    {/* ── 3. Settlement ── */}
    <div style={{background:C.white,borderRadius:"18px",padding:"18px",marginBottom:"12px",border:`1px solid ${C.border}`}}>
      {pay&&<div style={{background:pending?"#fff5f5":"#f0fdf4",borderRadius:"12px",padding:"12px 14px",marginBottom:"12px",border:`1px solid ${pending?"#fecaca":"#bbf7d0"}`}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{color:pending?C.red:"#15803d",fontSize:"13px",fontWeight:700}}>{pending?"Pending — you owe":"Settled"}</div>
            <div style={{color:pending?C.red:C.green,fontSize:"11px",marginTop:"2px"}}>Paid {fmtC(pay.amount_paid)}{pay.method?` · ${pay.method}`:""}{pay.date_paid?` · ${fmtDate(pay.date_paid)}`:""}</div>
          </div>
          {pending&&<div style={{fontWeight:800,color:C.red,fontSize:"18px"}}>{fmtC(Number(pay.shortfall||0))}</div>}
        </div>
      </div>}
      <div style={{display:"flex",gap:"8px"}}>
        {running?<Btn label="Cycle still running" variant="ghost" full disabled/>
          :settled?<Btn label="Settled ✓" variant="ghost" full disabled/>
          :isAdmin?<Btn label={pending?"Settle remainder":"Settle this cycle"} onClick={onSettleCycle} variant="gold" full/>
          :<Btn label="Settle (admin only)" variant="ghost" full disabled/>}
        <Btn label="📱 Share" onClick={onWhatsApp} variant="ghost" sm/>
      </div>
    </div>

    {/* ── 4. Adjustments ── */}
    <div style={{background:C.white,borderRadius:"18px",marginBottom:"12px",border:`1px solid ${C.border}`,overflow:"hidden"}}>
      <button onClick={()=>setAdjOpen(o=>!o)} style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",padding:"16px 18px",background:"none",border:"none",cursor:"pointer"}}>
        <div style={{fontWeight:700,color:C.text,fontSize:"15px"}}>Adjustments{c.adjs.length>0&&<span style={{fontSize:"12px",fontWeight:600,color:c.adjNet>0?C.green:c.adjNet<0?C.red:C.xlight,marginLeft:"8px"}}>{c.adjNet>0?"+":""}{fmtC(c.adjNet)}</span>}</div>
        <span style={{color:C.xlight}}>{adjOpen?"▲":"▼"}</span>
      </button>
      {adjOpen&&<div style={{padding:"0 18px 16px"}}>
        {!settled&&isAdmin&&<div style={{marginBottom:"10px"}}><Btn label="+ Add Adjustment" onClick={onAdj} variant="ghost" sm/></div>}
        {c.adjs.length===0?<div style={{color:C.xlight,fontSize:"13px",textAlign:"center",padding:"10px 0"}}>No adjustments this cycle</div>
          :c.adjs.map(a=><div key={a.id} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"9px 0",borderBottom:`1px dashed ${C.bg}`}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:"13px",color:C.text,fontWeight:600}}>{a.note||<span style={{color:C.xlight,fontStyle:"italic"}}>No reason</span>}</div>
              <div style={{fontSize:"11px",color:C.xlight}}>{fmtDate(a.date||a.created_at)}</div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:"6px",flexShrink:0,marginLeft:"8px"}}>
              <span style={{fontWeight:700,fontSize:"14px",color:Number(a.amount)>0?C.green:C.red}}>{Number(a.amount)>0?"+":""}{fmtC(a.amount)}</span>
              {!settled&&isAdmin&&<>
                <button onClick={()=>onEditAdj(a)} style={{background:"#f0f4ff",border:"none",borderRadius:"8px",padding:"5px 9px",color:"#4f46e5",fontSize:"11px",fontWeight:700}}>Edit</button>
                <button onClick={()=>onDeleteAdj(a.id)} style={{background:"#fff0f0",border:"none",borderRadius:"8px",padding:"5px 9px",color:C.red,fontSize:"11px",fontWeight:700}}>✕</button>
              </>}
            </div>
          </div>)}
      </div>}
    </div>

    {/* ── 5. Advance ledger (read summary + open) ── */}
    <button onClick={onOpenAdvLedger} style={{width:"100%",textAlign:"left",background:advBalance>0?"#fffbeb":C.white,border:`1px solid ${advBalance>0?"#fde68a":C.border}`,borderRadius:"18px",padding:"16px 18px",marginBottom:"12px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
      <div style={{fontWeight:700,color:C.text,fontSize:"15px"}}>💰 Advance ledger</div>
      <div style={{fontWeight:700,fontSize:"15px",color:advBalance>0?C.amber:C.xlight}}>{advBalance>0?`${fmtC(advBalance)} outstanding`:"Nil"} ›</div>
    </button>
  </div>;
}

// ── On-Call Tab ───────────────────────────────────────────────
function OnCallTab({hid,isAdmin,onToast,oncallStaff,setTab,onAddStaff,onStaffDeleted}) {
  const [sel,setSel]           = useState(null);
  const [ledger,setLedger]     = useState(null);
  const [loadingLedger,setLL]  = useState(false);
  const [addEntry,setAddEntry] = useState(false);
  const [addPay,setAddPay]     = useState(false);
  const [confirmDelete,setConfirmDelete] = useState(null);

  async function loadLedger(staffId) {
    setSel(staffId); setLL(true);
    try {
      const l = await api(`/service/ledger/${staffId}`,"GET",null,hid);
      setLedger(l);
    } catch(e) {
      onToast(friendlyError(e.message),"error"); setSel(null);
    } finally { setLL(false); }
  }

  if(sel) {
    if(loadingLedger) return <div style={{display:"flex",justifyContent:"center",padding:"40px"}}><Spin/></div>;
    const person = (oncallStaff||[]).find(s=>s.id===sel) || ledger?.staff || {};
    const balance = ledger ? ledger.balance : 0;

    if(addEntry) return <div className="su" style={{padding:"14px",paddingBottom:"96px"}}>
      <button onClick={()=>setAddEntry(false)} style={{display:"flex",alignItems:"center",gap:"6px",color:C.text,fontWeight:700,fontSize:"14px",marginBottom:"16px",background:"none",border:"none"}}>← Back</button>
      <div style={{fontFamily:"'DM Serif Display',serif",fontSize:"20px",color:C.text,marginBottom:"16px"}}>Log Visit</div>
      <OCEntryForm isDhobi={/laundry|dhobi/i.test(person.role||"")} serviceType={person.role} onSave={async e=>{
        try{await api("/service/entries","POST",{...e,staff_id:sel},hid);await loadLedger(sel);setAddEntry(false);onToast("Visit entry saved");}
        catch(err){onToast(err.message,"error");}
      }} onClose={()=>setAddEntry(false)}/>
    </div>;

    if(addPay) return <div className="su" style={{padding:"14px",paddingBottom:"96px"}}>
      <button onClick={()=>setAddPay(false)} style={{display:"flex",alignItems:"center",gap:"6px",color:C.text,fontWeight:700,fontSize:"14px",marginBottom:"16px",background:"none",border:"none"}}>← Back</button>
      <div style={{fontFamily:"'DM Serif Display',serif",fontSize:"20px",color:C.text,marginBottom:"16px"}}>Record Payment</div>
      <OCPayForm balance={balance} onSave={async p=>{
        try{await api("/service/payments","POST",{staff_id:sel,date:p.date,amount:p.amount,method:p.method,note:p.note},hid);await loadLedger(sel);setAddPay(false);onToast("Payment recorded successfully");}
        catch(e){onToast(friendlyError(e.message),"error");}
      }} onClose={()=>setAddPay(false)}/>
    </div>;

    return <div className="su" style={{padding:"14px"}}>
      <button onClick={()=>{setSel(null);setLedger(null);}} style={{display:"flex",alignItems:"center",gap:"6px",color:C.text,fontWeight:700,fontSize:"14px",marginBottom:"16px",background:"none",border:"none"}}>← Back</button>
      <div style={{background:HDR,borderRadius:"18px",padding:"18px",marginBottom:"14px"}}>
        <div style={{fontFamily:"'DM Serif Display',serif",color:C.gold,fontSize:"22px",marginBottom:"2px"}}>{person.name}</div>
        <div style={{color:"rgba(255,255,255,.4)",fontSize:"12px",marginBottom:"16px"}}>{ri(person.role)} {person.role||"On-Call"}</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"10px"}}>
          {[{l:"Total Due",v:fmtC(ledger?.totalDue||0),cl:C.amber},{l:"Total Paid",v:fmtC(ledger?.totalPaid||0),cl:C.green},{l:"Balance",v:fmtC(Math.abs(balance)),cl:balance>0?C.red:C.green}].map(x=><div key={x.l} style={{background:"rgba(255,255,255,.06)",borderRadius:"10px",padding:"10px",textAlign:"center"}}>
            <div style={{fontWeight:800,fontSize:"18px",color:x.cl}}>{x.v}</div>
            <div style={{fontSize:"9px",color:"rgba(255,255,255,.3)",fontWeight:600,marginTop:"2px"}}>{x.l.toUpperCase()}</div>
          </div>)}
        </div>
      </div>
      {balance>0&&<InfoBox icon="💳" color={C.red} bg="#fff0f0">Outstanding: {fmtC(balance)}</InfoBox>}
      {balance<0&&<InfoBox icon="✅" color={C.green} bg="#f0fdf4">Credit of {fmtC(Math.abs(balance))} — adjusts next payment</InfoBox>}
      <div style={{display:"flex",gap:"8px",marginBottom:"16px"}}>
        <Btn label="+ Log Visit" onClick={()=>setAddEntry(true)} variant="ghost" full/>
        <Btn label="💰 Record Payment" onClick={()=>setAddPay(true)} variant="green" full/>
      </div>
      {ledger&&[...(ledger.entries||[]),...(ledger.payments||[])].sort((a,b)=>b.date.localeCompare(a.date)).map((item,i)=>{
        const isPay=!("amount_due" in item);
        return <div key={i} style={{background:C.white,borderRadius:"14px",padding:"14px 16px",marginBottom:"8px",border:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontSize:"11px",color:C.xlight}}>{fmtDate(item.date)}</div>
            <div style={{fontWeight:600,color:C.text,fontSize:"14px",marginTop:"2px"}}>{isPay?"💰 Payment":item.description||"Visit"}{item.quantity&&<span style={{color:C.xlight,fontWeight:400}}> · {item.quantity}</span>}</div>
          </div>
          <div style={{textAlign:"right",display:"flex",alignItems:"center",gap:"8px"}}>
            <div>
              <div style={{fontWeight:700,color:isPay?C.green:C.red,fontSize:"16px"}}>{isPay?"+":"-"}{fmtC(isPay?item.amount_paid:item.amount_due)}</div>
              {item.method&&<div style={{fontSize:"11px",color:C.xlight}}>{item.method}</div>}
            </div>
            {isAdmin&&<button onClick={async()=>{try{
              await api(isPay?`/service/payments/${item.id}`:`/service/entries/${item.id}`,"DELETE",null,hid);
              await loadLedger(sel);onToast("Visit entry removed");
            }catch(e){onToast(friendlyError(e.message),"error");}}} style={{background:"#fff0f0",border:"none",borderRadius:"8px",padding:"6px 8px",color:C.red,fontSize:"12px",fontWeight:700}}>✕</button>}
          </div>
        </div>;
      })}
    </div>;
  }

  return <div className="su" style={{padding:"14px"}}>
    <InfoBox icon="ℹ️">On-Call is for irregular, visit-based services. For regular salaried staff, use the Staff tab.</InfoBox>
    <button onClick={onAddStaff} style={{width:"100%",background:HDR,color:C.gold,borderRadius:"18px",padding:"16px",fontWeight:700,fontSize:"15px",display:"flex",alignItems:"center",justifyContent:"center",gap:"8px",marginBottom:"16px",border:"none",boxShadow:"0 4px 20px rgba(26,26,46,.2)"}}>Add On-Call Staff</button>
    {(oncallStaff||[]).length===0&&<Empty icon="📋" title="No on-call staff yet" sub="Add your Dhobi, Masseuse, Priest or other on-call helpers"/>}
    <div>
      {(oncallStaff||[]).map(s=><div key={s.id} style={{background:C.white,borderRadius:"16px",padding:"14px 16px",marginBottom:"10px",border:`1px solid ${C.border}`,boxShadow:"0 2px 8px rgba(0,0,0,.04)",display:"flex",alignItems:"center",gap:"8px"}}>
        <button onClick={()=>loadLedger(s.id)} style={{flex:1,background:"none",border:"none",textAlign:"left",padding:0,cursor:"pointer"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontWeight:700,color:C.text,fontSize:"15px"}}>{ri(s.role)} {s.name}</div>
              <div style={{fontSize:"12px",color:C.xlight}}>{s.role||"On-Call"}</div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:"11px",color:C.xlight}}>tap for ledger →</div>
            </div>
          </div>
        </button>
        {isAdmin&&<button onClick={()=>setConfirmDelete(s)} style={{background:"#fff0f0",border:"none",borderRadius:"10px",padding:"8px 10px",color:C.red,fontSize:"13px",fontWeight:700,flexShrink:0}}>🗑</button>}
      </div>)}
    </div>
    {confirmDelete&&<div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.55)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:"24px"}}>
      <div style={{background:"#fff",borderRadius:"20px",padding:"28px 24px",maxWidth:"340px",width:"100%",fontFamily:"'Plus Jakarta Sans',sans-serif",boxShadow:"0 8px 40px rgba(0,0,0,.18)"}}>
        <div style={{fontWeight:800,fontSize:"18px",color:"#1a1a2e",marginBottom:"10px"}}>Delete Staff?</div>
        <div style={{fontSize:"14px",color:"#6b7280",marginBottom:"24px",lineHeight:1.5}}>This will permanently delete <strong>{confirmDelete.name}</strong> and all their service entries.</div>
        <div style={{display:"flex",gap:"10px"}}>
          <button onClick={()=>setConfirmDelete(null)} style={{flex:1,padding:"12px",borderRadius:"12px",border:"1.5px solid #e5e7eb",background:"#f3f4f6",color:"#374151",fontWeight:700,fontSize:"14px",fontFamily:"'Plus Jakarta Sans',sans-serif",cursor:"pointer"}}>Cancel</button>
          <button onClick={async()=>{try{await api(`/staff/${confirmDelete.id}`,"DELETE",null,hid);if(onStaffDeleted)onStaffDeleted(confirmDelete.id);onToast("Staff member removed");}catch(e){onToast(friendlyError(e.message),"error");}finally{setConfirmDelete(null);}}} style={{flex:1,padding:"12px",borderRadius:"12px",border:"none",background:"#f43f5e",color:"#fff",fontWeight:700,fontSize:"14px",fontFamily:"'Plus Jakarta Sans',sans-serif",cursor:"pointer"}}>Delete</button>
        </div>
      </div>
    </div>}
  </div>;
}
const OC_SERVICE_TYPES = [
  "Laundry / Dhobi","Masseuse","Priest / Pandit Ji","Plumber","Electrician",
  "Carpenter","Deep Cleaner (One-time)","Pest Control","Baby Nurse / Jappa",
  "Gym Trainer","Yoga Trainer","Teacher / Tutor","Other"
];

function OCProvForm({onSave,onClose}) {
  const [name,setName]=useState(""); const [type,setType]=useState(""); const [custom,setCustom]=useState(""); const [l,setL]=useState(false);
  const finalType = type==="Other" ? (custom.trim()||"Other") : type;
  const canSave = !!name.trim() && !!finalType;
  return <div style={{paddingBottom:"120px"}}>
    <Fld label="Name" req><input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Dhobi Ram, Malishwali" style={IS}/></Fld>
    <Fld label="Service Type" req>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px"}}>
        {OC_SERVICE_TYPES.map(t=>(
          <button key={t} type="button" onClick={()=>setType(t)}
            style={{padding:"12px 10px",borderRadius:"12px",textAlign:"center",fontSize:"13px",
              fontWeight:type===t?700:500,
              background:type===t?"#f0fdf4":"#f8fafc",
              color:type===t?C.green:C.mid,
              border:`1.5px solid ${type===t?C.green:C.border}`,
              lineHeight:1.3}}>
            {t}
          </button>
        ))}
      </div>
      {type==="Other"&&<input value={custom} onChange={e=>setCustom(e.target.value)} placeholder="Describe the service" autoFocus style={{...IS,marginTop:"10px"}}/>}
    </Fld>
    {type==="Laundry / Dhobi"&&<InfoBox icon="👕" color="#4f46e5" bg="#f0f4ff">Clothes tracking is included — you'll be able to log items given and returned each visit.</InfoBox>}
    <SaveRow loading={l} onClose={onClose} label="Add Provider" onSave={async()=>{if(!canSave)return;setL(true);try{await onSave({name:name.trim(),service_type:finalType});}finally{setL(false);}}}/>
  </div>;
}

function OCEntryForm({isDhobi,serviceType,onSave,onClose}) {
  const [f,setF]=useState({date:new Date().toISOString().slice(0,10),description:"",qtyNum:"",qtyUnit:"",clothes_given:"",clothes_returned:"",amount_due:""});
  const [l,setL]=useState(false);
  const s=(k,v)=>setF(p=>({...p,[k]:v}));

  // All available units (always shown)
  const ALL_UNITS = ["Nos","Hours","Minutes","Sessions","Visits","Kg"];
  // Determine default unit based on serviceType
  const defaultUnit = (() => {
    const t = serviceType||"";
    if(/laundry|dhobi/i.test(t))               return "Nos";
    if(/masseuse/i.test(t))                     return "Hours";
    if(/priest|pandit/i.test(t))               return "Sessions";
    if(/plumber|electrician|carpenter/i.test(t)) return "Visits";
    if(/gym|yoga|physio/i.test(t))             return "Sessions";
    return "Nos";
  })();
  const qtyUnit = f.qtyUnit && ALL_UNITS.includes(f.qtyUnit) ? f.qtyUnit : defaultUnit;

  const combinedQty = f.qtyNum ? `${f.qtyNum} ${qtyUnit}` : "";

  return (
    <div style={{paddingBottom:"40px"}}>
      <Fld label="Date"><input type="date" value={f.date} onChange={e=>s("date",e.target.value)} style={IS}/></Fld>
      {isDhobi?<>
        <Fld label="Clothes Given"><input value={f.clothes_given} onChange={e=>s("clothes_given",e.target.value)} type="number" placeholder="e.g. 15" style={IS}/></Fld>
        <Fld label="Clothes Returned"><input value={f.clothes_returned} onChange={e=>s("clothes_returned",e.target.value)} type="number" placeholder="e.g. 12" style={IS}/></Fld>
      </>:<>
        <Fld label="Description"><input value={f.description} onChange={e=>s("description",e.target.value)} placeholder="e.g. Full massage, Puja" style={IS}/></Fld>
        <Fld label="Quantity">
          <div style={{display:"flex",gap:"8px"}}>
            <input
              value={f.qtyNum}
              onChange={e=>s("qtyNum",e.target.value)}
              type="number"
              min="0"
              placeholder="e.g. 2"
              style={{...IS,flex:"0 0 90px",width:"90px"}}
            />
            <select
              value={qtyUnit}
              onChange={e=>s("qtyUnit",e.target.value)}
              style={{...IS,flex:1}}
            >
              {ALL_UNITS.map(u=><option key={u} value={u}>{u}</option>)}
            </select>
          </div>
        </Fld>
      </>}
      <Fld label="Amount Due (₹)"><input value={f.amount_due} onChange={e=>s("amount_due",e.target.value)} type="number" placeholder="0" style={IS}/></Fld>
      <SaveRow loading={l} onClose={onClose} label="Log Visit" onSave={async()=>{setL(true);try{
        const desc=isDhobi?`Given: ${f.clothes_given||0}, Returned: ${f.clothes_returned||0}`:f.description;
        const qty=isDhobi?`${f.clothes_given||0} given / ${f.clothes_returned||0} returned`:combinedQty;
        await onSave({date:f.date,description:desc,quantity:qty,amount_due:Number(f.amount_due)||0});
      }finally{setL(false);}}}/>
    </div>
  );
}

function OCPayForm({balance,onSave,onClose}) {
  const [f,setF]=useState({date:new Date().toISOString().slice(0,10),amount:balance>0?String(Math.round(balance)):"",method:"Cash",note:""});
  const [l,setL]=useState(false);
  const s=(k,v)=>setF(p=>({...p,[k]:v}));
  return <div style={{paddingBottom:"120px"}}>{balance>0&&<InfoBox icon="💳">Outstanding: {fmtC(balance)}</InfoBox>}
    <Fld label="Date"><input type="date" value={f.date} onChange={e=>s("date",e.target.value)} style={IS}/></Fld>
    <Fld label="Amount Paid (₹)"><input value={f.amount} onChange={e=>s("amount",e.target.value)} type="number" placeholder="0" style={IS}/></Fld>
    <Fld label="Method"><Seg opts={[{v:"Cash",l:"Cash"},{v:"UPI",l:"UPI"},{v:"Bank",l:"Bank"}]} val={f.method} onChange={v=>s("method",v)}/></Fld>
    <Fld label="Note"><input value={f.note} onChange={e=>s("note",e.target.value)} placeholder="Optional" style={IS}/></Fld>
    <SaveRow loading={l} onClose={onClose} label="Record Payment" onSave={async()=>{
      if(!f.amount)return;
      setL(true);
      try{await onSave({date:f.date,amount:Number(f.amount),method:f.method,note:f.note});}
      finally{setL(false);}
    }}/>
  </div>;
}

// ── Notification Modal ────────────────────────────────────────
function NotifModal({notifs,salDueAlerts,hid,isAdmin,onClose,onToast,onResolved,onGoSalary}) {
  const [loading,setL] = useState({});

  async function review(id,action) {
    setL(p=>({...p,[id]:true}));
    try{
      await api(`/staff/change-requests/${id}`,"PUT",{action},hid);
      onToast(`Request ${action==="approve"?"approved":"rejected"}`);
      onResolved();
      onClose();
    }catch(e){onToast(friendlyError(e.message),"error");}
    finally{setL(p=>({...p,[id]:false}));}
  }

  const pendingList=notifs.filter(n=>n.status==="pending");
  const doneList=notifs.filter(n=>n.status!=="pending").slice(0,10);

  const fmt=n=>{
    try{
      const nv=typeof n.new_value==="string"?JSON.parse(n.new_value):n.new_value;
      const name=nv?.name||n.staff_name||"staff member";
      const role=nv?.role||"";
      const type=nv?.staff_type||nv?.staffType||"regular";
      const typeLabel=type==="oncall"?"On-Call":"Regular";
      const by=n.requested_by_name||"Co-admin";
      if(n.change_type==="add_staff")return `${by} wants to add ${name} as ${typeLabel}${role?` · ${role}`:""}`;
      if(n.change_type==="edit_staff")return `${by} wants to update ${name}'s profile`;
      if(n.change_type==="salary_increment"){const amt=nv?.amount||0;return `${by} wants to give ${name} an increment to ${fmtC(amt)}`;}
      if(n.change_type==="delete_staff")return `${by} wants to permanently delete ${name}`;
      return n.change_type;
    }catch{return n.change_type;}
  };

  const salAlerts=salDueAlerts||[];
  return <Modal onClose={onClose} title="Notifications" accent={C.amber}>
    {/* Salary Due section */}
    {salAlerts.length>0&&<>
      <div style={{fontSize:"11px",color:C.xlight,fontWeight:700,letterSpacing:"1px",marginBottom:"10px",textTransform:"uppercase"}}>💰 Salary Due</div>
      {salAlerts.map(a=><div key={a.staffId} style={{background:"#fff7ed",borderRadius:"14px",padding:"12px 14px",marginBottom:"10px",border:`1px solid #fed7aa`,display:"flex",alignItems:"center",justifyContent:"space-between",gap:"10px"}}>
        <div>
          <div style={{fontWeight:600,color:C.text,fontSize:"13px"}}>{a.name} — {a.label}</div>
          <div style={{fontSize:"11px",color:C.amber,marginTop:"2px"}}>Due since {a.dueDate.getDate()} {MONTHS[a.dueDate.getMonth()]}</div>
        </div>
        <button onClick={()=>onGoSalary&&onGoSalary(a.staffId)} style={{background:C.ind,color:C.gold,border:"none",borderRadius:"10px",padding:"6px 12px",fontSize:"12px",fontWeight:700,flexShrink:0}}>Go to salary</button>
      </div>)}
      <div style={{height:"1px",background:C.border,marginBottom:"12px"}}/>
    </>}
    {pendingList.length===0&&doneList.length===0&&salAlerts.length===0&&<Empty icon="🔔" title="All caught up" sub="No notifications yet"/>}
    {pendingList.length>0&&<>
      <div style={{fontSize:"11px",color:C.xlight,fontWeight:700,letterSpacing:"1px",marginBottom:"10px",textTransform:"uppercase"}}>Pending {isAdmin?"— Needs Your Approval":""}</div>
      {pendingList.map(n=><div key={n.id} style={{background:C.bg,borderRadius:"14px",padding:"14px",marginBottom:"10px",border:`1px solid ${C.border}`}}>
        <div style={{fontWeight:600,color:C.text,fontSize:"14px",marginBottom:"2px"}}>{fmt(n)}</div>
        <div style={{fontSize:"12px",color:C.xlight,marginBottom:isAdmin?"12px":"0"}}>By {n.requested_by_name||"Co-admin"} · {fmtDate(n.created_at)}</div>
        {isAdmin&&<div style={{display:"flex",gap:"8px"}}>
          <Btn label="✓ Approve" onClick={()=>review(n.id,"approve")} variant="green" sm full loading={loading[n.id]}/>
          <Btn label="✕ Reject" onClick={()=>review(n.id,"reject")} variant="danger" sm full loading={loading[n.id]}/>
        </div>}
        {!isAdmin&&<Chip label="⏳ Waiting for admin" color="#92400e" bg="#fef3c7" sm/>}
      </div>)}
    </>}
    {doneList.length>0&&<>
      <div style={{fontSize:"11px",color:C.xlight,fontWeight:700,letterSpacing:"1px",marginBottom:"10px",marginTop:"4px",textTransform:"uppercase"}}>Recent Activity</div>
      {doneList.map(n=><div key={n.id} style={{background:C.white,borderRadius:"12px",padding:"12px 14px",marginBottom:"8px",border:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:"10px"}}>
        <span style={{fontSize:"18px"}}>{n.status==="approved"?"✅":"❌"}</span>
        <div style={{flex:1}}>
          <div style={{fontSize:"13px",color:C.text,fontWeight:600}}>{fmt(n)}</div>
          <div style={{fontSize:"11px",color:C.xlight}}>{n.status==="approved"?"Approved":"Rejected"} · {fmtDate(n.reviewed_at||n.created_at)}</div>
        </div>
      </div>)}
    </>}
  </Modal>;
}

// ── Settings Modal ────────────────────────────────────────────
function SettingsModal({F,H,hid,isAdmin,user,activeHH,hhs,onClose,onToast,onSaveF,onSaveH,onSignOut,onSwitchHH,onCreateHH,staff,showSalAmt,onToggleSalAmt}) {
  const [tab,setT]=useState("household");
  const [Fs,setFs]=useState({...F});
  const [Hs,setHs]=useState({name:H.name||"",address:H.address||"",city:H.city||"",state:H.state||"",type:H.type||"apartment"});
  const [loading,setL]=useState(false);
  const [invite,setInvite]=useState(null);
  const [invL,setInvL]=useState(false);
  const [newHH,setNewHH]=useState(false);
  const [nhn,setNhn]=useState(""),[nha,setNha]=useState(""),[nhc,setNhc]=useState(""),[nht,setNht]=useState("apartment");
  const [saveErr,setSaveErr]=useState("");
  const [pendingOff,setPendingOff]=useState(null);

  const inUseCount={
    sessions:(staff||[]).filter(s=>!s.left&&(s.slots||[]).length>1).length,
    time_tracking:(staff||[]).filter(s=>!s.left&&s.timeTracking).length,
    multi_role:(staff||[]).filter(s=>!s.left&&(s.roles||[]).length>0).length,
    overtime:(staff||[]).filter(s=>!s.left&&s.otEnabled).length,
    pf_esic:(staff||[]).filter(s=>!s.left&&(s.pfEnabled||s.esicEnabled)).length,
    on_call_log:0,
  };
  function handleToggle(k){
    if(!isAdmin)return;
    if(!!Fs[k]&&inUseCount[k]>0){setPendingOff(k);return;}
    setFs(p=>({...p,[k]:!p[k]}));setPendingOff(null);
  }

  async function genInvite(){setInvL(true);try{const r=await api("/auth/invite","POST",{householdId:hid},hid);setInvite(r);}catch(e){onToast("Failed to generate invite","error");}finally{setInvL(false);}};

  const tabs=[{id:"household",l:"🏠 Home"},{id:"features",l:"⚙️ Features"},{id:"access",l:"🔑 Access"},{id:"data",l:"📦 Data"}];

  return <Modal onClose={onClose} title="Settings" accent={C.ind}>
    <div style={{display:"flex",gap:"4px",marginBottom:"20px",background:C.bg,borderRadius:"12px",padding:"4px",overflowX:"auto"}}>
      {tabs.map(t=><button key={t.id} onClick={()=>setT(t.id)} style={{flex:1,padding:"8px 6px",borderRadius:"9px",fontWeight:600,fontSize:"12px",border:"none",background:tab===t.id?C.ind:"transparent",color:tab===t.id?C.gold:C.light,whiteSpace:"nowrap"}}>{t.l}</button>)}
    </div>

    {tab==="household"&&<>
      {!isAdmin&&<InfoBox icon="ℹ️">Only admins can edit household details.</InfoBox>}
      <Fld label="Household Name"><input value={Hs.name} onChange={e=>setHs(p=>({...p,name:e.target.value}))} style={IS} disabled={!isAdmin}/></Fld>
      <Fld label="Address"><input value={Hs.address} onChange={e=>setHs(p=>({...p,address:e.target.value}))} style={IS} disabled={!isAdmin}/></Fld>
      <Fld label="City"><input value={Hs.city} onChange={e=>setHs(p=>({...p,city:e.target.value}))} style={IS} disabled={!isAdmin}/></Fld>
      <Fld label="State"><input value={Hs.state} onChange={e=>setHs(p=>({...p,state:e.target.value}))} style={IS} disabled={!isAdmin}/></Fld>
      <Fld label="Type"><Seg opts={[{v:"apartment",l:"🏢 Flat"},{v:"house",l:"🏠 House"},{v:"villa",l:"🏡 Villa"}]} val={Hs.type} onChange={v=>setHs(p=>({...p,type:v}))}/></Fld>
      {isAdmin&&<>{saveErr&&<InfoBox color={C.red} bg="#fff0f0" icon="⚠️">{saveErr}</InfoBox>}<Btn label="Save Household" loading={loading} onClick={async()=>{setSaveErr("");setL(true);try{await onSaveH(Hs);onClose();}catch(e){setSaveErr(e.message||"Failed to save. Please try again.");}finally{setL(false);}}} full/></>}
    </>}

    {tab==="features"&&<>
      {!isAdmin&&<InfoBox icon="ℹ️">Only admins can change features.</InfoBox>}
      <InfoBox icon="💡">Features apply to the whole household. Per-staff options are in each staff profile.</InfoBox>
      {[
        {k:"sessions",l:"Multiple sessions per day",sub:"Morning and evening staff"},
        {k:"time_tracking",l:"Time in/out tracking",sub:"For drivers and time-based staff"},
        {k:"multi_role",l:"Multi-role staff",sub:"Different rates for different jobs"},
        {k:"overtime",l:"Overtime pay",sub:"OT calculated from time logs"},
        {k:"pf_esic",l:"PF / ESIC deductions",sub:"Statutory payroll deductions"},
        {k:"on_call_log",l:"On-Call services tab",sub:"Dhobi, Masseuse, Priest etc."},
      ].map(x=><div key={x.k}>
        <Tog on={!!Fs[x.k]} toggle={()=>handleToggle(x.k)} label={x.l} sub={x.sub} disabled={!isAdmin}/>
        {isAdmin&&pendingOff===x.k&&<div style={{fontSize:"12px",color:"#92400e",background:"#fef3c7",borderRadius:"8px",padding:"10px 12px",marginTop:"-4px",marginBottom:"10px",lineHeight:1.6}}>
          {inUseCount[x.k]>0?`${inUseCount[x.k]} staff use this feature. `:""}Turning it off hides it for new staff but existing staff are not affected.{" "}
          <button onClick={()=>{setFs(p=>({...p,[x.k]:false}));setPendingOff(null);}} style={{color:C.red,fontWeight:700,background:"none",border:"none",padding:0,cursor:"pointer",fontSize:"12px",textDecoration:"underline"}}>Turn off anyway</button>
        </div>}
        {isAdmin&&!Fs[x.k]&&F[x.k]&&pendingOff!==x.k&&<div style={{fontSize:"11px",color:"#92400e",background:"#fef3c7",borderRadius:"8px",padding:"7px 12px",marginTop:"-6px",marginBottom:"10px",lineHeight:1.5}}>Turning this off hides it for new staff but does not affect existing staff already using this feature.</div>}
      </div>)}
      <div style={{fontWeight:700,color:C.text,fontSize:"13px",letterSpacing:"0.5px",margin:"18px 0 8px",textTransform:"uppercase",opacity:.6}}>Salary Preferences</div>
      <Tog on={!!showSalAmt} toggle={()=>onToggleSalAmt&&onToggleSalAmt(!showSalAmt)} label="Show salary amounts on staff list" sub="Display net payable on each staff card"/>
      {isAdmin&&<>{saveErr&&<InfoBox color={C.red} bg="#fff0f0" icon="⚠️">{saveErr}</InfoBox>}<Btn label="Save Features" loading={loading} onClick={async()=>{setSaveErr("");setL(true);try{await onSaveF(Fs);onClose();}catch(e){setSaveErr(e.message||"Failed to save. Please try again.");}finally{setL(false);}}} full/></>}
    </>}

    {tab==="access"&&<>
      <div style={{background:C.white,borderRadius:"14px",padding:"14px",marginBottom:"14px",border:`1px solid ${C.border}`}}>
        <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"12px"}}>
          <Avatar name={user.name} size={40} fs={16}/>
          <div><div style={{fontWeight:700,color:C.text}}>{user.name}</div><div style={{fontSize:"12px",color:C.xlight}}>{user.phone}</div></div>
          <div style={{marginLeft:"auto"}}><Chip label={isAdmin?"👑 Admin":"⭐ Co-Admin"} color={isAdmin?"#92400e":"#1e40af"} bg={isAdmin?"#fef3c7":"#dbeafe"} sm/></div>
        </div>
        <div style={{display:"flex",gap:"8px"}}>
          <Btn label="Switch Household" onClick={()=>{onClose();setT("household");}} variant="ghost" sm full/>
          <Btn label="Sign Out" onClick={onSignOut} variant="danger" sm full/>
        </div>
      </div>
      {isAdmin&&<div style={{background:C.white,borderRadius:"14px",padding:"14px",marginBottom:"14px",border:`1px solid ${C.border}`}}>
        <div style={{fontWeight:700,color:C.text,fontSize:"15px",marginBottom:"6px"}}>Invite Co-Admin</div>
        <div style={{fontSize:"13px",color:C.light,marginBottom:"14px",lineHeight:1.6}}>Share an invite link. They'll get Co-Admin access.</div>
        {invite?<>
          <div style={{background:C.bg,borderRadius:"12px",padding:"14px",textAlign:"center",marginBottom:"12px",border:`1px solid ${C.border}`}}>
            <div style={{fontSize:"11px",color:C.xlight,fontWeight:600,letterSpacing:"1px",marginBottom:"6px"}}>INVITE CODE</div>
            <div style={{fontFamily:"monospace",fontSize:"26px",fontWeight:800,color:C.text,letterSpacing:"6px"}}>{invite.code}</div>
            <div style={{fontSize:"11px",color:C.xlight,marginTop:"6px"}}>Expires in 24 hours</div>
          </div>
          <div style={{display:"flex",gap:"8px"}}>
            <Btn label="📤 Share" onClick={()=>{if(navigator.share)navigator.share({title:"GharSaathi Invite",url:invite.link});else{navigator.clipboard.writeText(invite.link);onToast("Invite link copied");}}} variant="green" full/>
            <Btn label="New Code" onClick={genInvite} variant="ghost"/>
          </div>
        </>:<Btn label="Generate Invite Link" onClick={genInvite} loading={invL} full/>}
      </div>}
      {/* Other households */}
      {hhs.length>1&&<div style={{background:C.white,borderRadius:"14px",padding:"14px",border:`1px solid ${C.border}`}}>
        <div style={{fontWeight:700,color:C.text,fontSize:"15px",marginBottom:"12px"}}>Your Households</div>
        {hhs.map(h=><div key={h.household_id||h.id} style={{display:"flex",alignItems:"center",gap:"10px",padding:"8px 0",borderBottom:`1px solid ${C.bg}`}}>
          <div style={{width:"32px",height:"32px",borderRadius:"8px",background:HDR,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"14px"}}>{h.type==="villa"?"🏡":h.type==="house"?"🏠":"🏢"}</div>
          <div style={{flex:1}}><div style={{fontWeight:600,color:C.text,fontSize:"13px"}}>{h.household_name||h.name}</div><div style={{fontSize:"11px",color:C.xlight}}>{h.city||""}</div></div>
          <Chip label={h.role==="admin"?"👑 Admin":"⭐ Co-Admin"} color={h.role==="admin"?"#92400e":"#1e40af"} bg={h.role==="admin"?"#fef3c7":"#dbeafe"} sm/>
          {(h.household_id||h.id)!==hid&&<button onClick={()=>{onSwitchHH(h);onClose();}} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:"8px",padding:"5px 10px",fontSize:"12px",fontWeight:600,color:C.mid}}>Switch</button>}
        </div>)}
        <button onClick={()=>setNewHH(true)} style={{width:"100%",marginTop:"10px",background:"none",border:`1.5px dashed ${C.border}`,borderRadius:"10px",padding:"10px",color:C.light,fontWeight:600,fontSize:"13px"}}>+ Create New Household</button>
        {newHH&&<div style={{marginTop:"14px",borderTop:`1px solid ${C.border}`,paddingTop:"14px"}}>
          <Fld label="Name" req><input value={nhn} onChange={e=>setNhn(e.target.value)} placeholder="e.g. Parents Home" style={IS}/></Fld>
          <Fld label="City"><input value={nhc} onChange={e=>setNhc(e.target.value)} placeholder="e.g. Indore" style={IS}/></Fld>
          <Fld label="Type"><Seg opts={[{v:"apartment",l:"🏢 Flat"},{v:"house",l:"🏠 House"},{v:"villa",l:"🏡 Villa"}]} val={nht} onChange={setNht}/></Fld>
          <Btn label="Create" onClick={async()=>{if(!nhn)return;setL(true);try{await onCreateHH(nhn,nha,nhc,nht);}finally{setL(false);}}} loading={loading} full/>
        </div>}
      </div>}
    </>}

    {tab==="data"&&<>
      <InfoBox icon="📦">Export your data as a backup, or import from a previous backup.</InfoBox>
      <Btn label="⬇ Export Data" onClick={async()=>{
        try{
          const [staffData,salData]=await Promise.all([
            api("/staff","GET",null,hid),
            api(`/salary/month/${new Date().getFullYear()}/${new Date().getMonth()}`,"GET",null,hid),
          ]);
          const payload={
            exported:new Date().toISOString(),
            household:H,
            staff:staffData,
            payments:salData.payments||[],
            advances:salData.advances||[],
            adjustments:salData.adjustments||[],
          };
          const name=(H.name||"household").toLowerCase().replace(/\s+/g,"-").replace(/[^a-z0-9-]/g,"");
          const date=new Date().toISOString().slice(0,10);
          const b=new Blob([JSON.stringify(payload,null,2)],{type:"application/json"});
          const u=URL.createObjectURL(b);
          const a=document.createElement("a");a.href=u;
          a.download=`gharsaathi-${name}-${date}.json`;
          a.click();URL.revokeObjectURL(u);
          onToast("Data exported successfully");
        }catch(e){onToast("Export failed: "+e.message);}
      }} full/>
      <div style={{height:"8px"}}/>
      <Btn label="⬆ Import Data" onClick={()=>onToast("Contact support to import data safely")} variant="ghost" full/>
      <div style={{marginTop:"20px",textAlign:"center",color:C.xlight,fontSize:"11px"}}>GharSaathi v9.8 · For every home that runs on people</div>
    </>}
  </Modal>;
}

// ── HH Switch Modal ───────────────────────────────────────────
function HHSwitchModal({hhs,currentId,onClose,onSelect,onCreateNew}) {
  const [creating,setCreating]=useState(false);
  const [nm,setNm]=useState(""),[addr,setAddr]=useState(""),[city,setCity]=useState(""),[type,setType]=useState("apartment");
  const [loading,setL]=useState(false);
  return <Modal onClose={onClose} title="Switch Household" accent={C.gold}>
    {hhs.map(h=><button key={h.household_id||h.id} onClick={()=>onSelect(h)} style={{width:"100%",background:(h.household_id||h.id)===currentId?"#f0fdf4":C.white,borderRadius:"14px",padding:"14px 16px",marginBottom:"8px",textAlign:"left",border:`1.5px solid ${(h.household_id||h.id)===currentId?C.green:C.border}`,display:"block"}}>
      <div style={{display:"flex",alignItems:"center",gap:"12px"}}>
        <div style={{width:"38px",height:"38px",borderRadius:"10px",background:HDR,display:"flex",alignItems:"center",justifyContent:"center",fontSize:"16px"}}>{h.type==="villa"?"🏡":h.type==="house"?"🏠":"🏢"}</div>
        <div style={{flex:1}}>
          <div style={{fontWeight:700,color:C.text,fontSize:"15px",display:"flex",alignItems:"center",gap:"6px"}}>{h.household_name||h.name}{(h.household_id||h.id)===currentId&&<Chip label="Current" color={C.green} bg="#dcfce7" sm/>}</div>
          <div style={{fontSize:"11px",color:C.xlight}}>{h.city&&`${h.city} · `}<Chip label={h.role==="admin"?"👑 Admin":"⭐ Co-Admin"} color={h.role==="admin"?"#92400e":"#1e40af"} bg={h.role==="admin"?"#fef3c7":"#dbeafe"} sm/></div>
        </div>
        <span style={{color:C.xlight}}>›</span>
      </div>
    </button>)}
    {!creating&&<button onClick={()=>setCreating(true)} style={{width:"100%",background:"none",border:`2px dashed ${C.border}`,borderRadius:"14px",padding:"14px",color:C.light,fontWeight:600,fontSize:"13px",display:"flex",alignItems:"center",justifyContent:"center",gap:"8px"}}>+ Create New Household</button>}
    {creating&&<div style={{borderTop:`1px solid ${C.border}`,paddingTop:"16px",marginTop:"4px"}}>
      <Fld label="Household Name" req><input value={nm} onChange={e=>setNm(e.target.value)} placeholder="e.g. Parents Home" style={IS}/></Fld>
      <Fld label="City"><input value={city} onChange={e=>setCity(e.target.value)} placeholder="e.g. Indore" style={IS}/></Fld>
      <Fld label="Type"><Seg opts={[{v:"apartment",l:"🏢 Flat"},{v:"house",l:"🏠 House"},{v:"villa",l:"🏡 Villa"}]} val={type} onChange={setType}/></Fld>
      <Btn label="Create Household" onClick={async()=>{if(!nm)return;setL(true);try{await onCreateNew(nm,addr,city,type);}finally{setL(false);}}} loading={loading} full/>
    </div>}
  </Modal>;
}

function CreateHHModal({onClose,onSave}) {
  const [nm,setNm]=useState(""),[addr,setAddr]=useState(""),[city,setCity]=useState(""),[type,setType]=useState("apartment");
  const [loading,setL]=useState(false);
  return <Modal onClose={onClose} title="Create New Household" accent={C.gold}>
    <Fld label="Household Name" req><input value={nm} onChange={e=>setNm(e.target.value)} placeholder="e.g. Parents Home" style={IS}/></Fld>
    <Fld label="Address"><input value={addr} onChange={e=>setAddr(e.target.value)} placeholder="e.g. B-12, MG Road" style={IS}/></Fld>
    <Fld label="City"><input value={city} onChange={e=>setCity(e.target.value)} placeholder="e.g. Indore" style={IS}/></Fld>
    <Fld label="Type"><Seg opts={[{v:"apartment",l:"🏢 Flat"},{v:"house",l:"🏠 House"},{v:"villa",l:"🏡 Villa"}]} val={type} onChange={setType}/></Fld>
    <SaveRow loading={loading} onClose={onClose} label="Create" onSave={async()=>{if(!nm)return;setL(true);try{await onSave(nm,addr,city,type);}finally{setL(false);}}}/>
  </Modal>;
}

// ── Restore Modal ─────────────────────────────────────────────
function RestoreModal({staff:s,onClose,onRestore}) {
  const [date,setDate]=useState(new Date().toISOString().slice(0,10));
  const [l,setL]=useState(false);
  return <Modal onClose={onClose} title="When did they rejoin?" sub={s.name} accent={C.green}>
    <InfoBox icon="📅">Set the date {s.name} rejoined. This will become their new join date.</InfoBox>
    <Fld label="Rejoining Date" req><input type="date" value={date} onChange={e=>setDate(e.target.value)} style={IS}/></Fld>
    <div style={{display:"flex",gap:"10px",marginTop:"16px"}}>
      <button onClick={onClose} style={{flex:1,padding:"14px",borderRadius:"14px",border:`1.5px solid ${C.border}`,background:C.white,fontWeight:600,fontSize:"15px",color:C.text}}>Cancel</button>
      <button onClick={async()=>{if(!date)return;setL(true);try{await onRestore(date);}finally{setL(false);}}} style={{flex:1,padding:"14px",borderRadius:"14px",border:"none",background:C.green,fontWeight:700,fontSize:"15px",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",gap:"8px"}}>
        {l?<Spin size={16} color="#fff"/>:null} Restore
      </button>
    </div>
  </Modal>;
}

// ── Staff Modal ───────────────────────────────────────────────
function StaffModal({staff,F,isAdmin,onClose,onSave,defaultType="regular"}) {
  const Ff=F||{};
  const [staffType,setStaffType]=useState(staff?.staffType||defaultType);
  const [f,setF]=useState(staff||{name:"",role:"",salaryType:"monthly",salary:"",weekOffDays:[],cycleStart:1,paidLeaveDays:0,sessions:1,slots:["M"],roles:[],timeTracking:false,startTime:"09:00",stdHours:8,otEnabled:false,otType:"fixed",otRate:0,otMultiplier:1.5,pfEnabled:false,esicEnabled:false,fullTime:false,aadhaar:"",phone:"",dateOfBirth:"",joinDate:"",joinMonth:"",joinYear:"",emergencyContact:"",emergencyPhone:"",notes:"",trackingStart:`${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}-01`});
  const [useExact,setUE]=useState(!!(staff?.joinDate));
  const [loading,setL]=useState(false);
  const [errors,setErrors]=useState({});
  // "comes more than once a day?" — derive initial state from existing slots
  const [multiDay,setMultiDay]=useState(!!(staff && (staff.slots||[]).length>1));
  const s=(k,v)=>setF(p=>({...p,[k]:v}));
  const cleanPhone=e=>{const d=e.clipboardData.getData("text").replace(/\D/g,"");return d.slice(-10);};
  const joinDateRef=useRef(null);
  useEffect(()=>{if(useExact&&joinDateRef.current){joinDateRef.current.focus();try{joinDateRef.current.showPicker();}catch{}}},[useExact]);
  const togWOD=d=>s("weekOffDays",(f.weekOffDays||[]).includes(d)?(f.weekOffDays||[]).filter(x=>x!==d):[...(f.weekOffDays||[]),d]);
  const handleFT=v=>{s("fullTime",v);if(v)s("sessions",1);};
  const handleSess=v=>{s("sessions",v);if(v>1)s("fullTime",false);};
  const handleMultiDay=v=>{
    setMultiDay(v);
    if(!v){s("slots",["M"]);s("sessions",1);}
    else{// default to Morning+Evening if switching to multi
      const cur=f.slots||["M"];
      const next=cur.length>1?cur:["M","E"];
      s("slots",next);s("sessions",next.length);
    }
  };

  return <Modal onClose={onClose} title={staff?"Edit Staff":"Add Staff Member"} accent={C.gold}>
    {!staff&&<div style={{marginBottom:"20px"}}>
      <div style={{fontSize:"11px",color:C.light,fontWeight:700,letterSpacing:"1px",marginBottom:"10px",textTransform:"uppercase"}}>Staff Type</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px"}}>
        <button type="button" onClick={()=>setStaffType("regular")} style={{padding:"14px 10px",borderRadius:"12px",textAlign:"center",fontWeight:staffType==="regular"?700:500,background:staffType==="regular"?C.ind:"#f8fafc",color:staffType==="regular"?C.gold:C.mid,border:`1.5px solid ${staffType==="regular"?C.gold:C.border}`}}>
          <div style={{fontSize:"18px",marginBottom:"4px"}}>🏠</div><div style={{fontSize:"13px"}}>Regular</div><div style={{fontSize:"10px",opacity:.7,marginTop:"2px"}}>Salary + Attendance</div>
        </button>
        <button type="button" onClick={()=>setStaffType("oncall")} style={{padding:"14px 10px",borderRadius:"12px",textAlign:"center",fontWeight:staffType==="oncall"?700:500,background:staffType==="oncall"?C.ind:"#f8fafc",color:staffType==="oncall"?C.gold:C.mid,border:`1.5px solid ${staffType==="oncall"?C.gold:C.border}`}}>
          <div style={{fontSize:"18px",marginBottom:"4px"}}>📞</div><div style={{fontSize:"13px"}}>On-Call</div><div style={{fontSize:"10px",opacity:.7,marginTop:"2px"}}>Visit-based, no salary</div>
        </button>
      </div>
    </div>}
    <Fld label="Full Name" req><input value={f.name||""} onChange={e=>{s("name",e.target.value);if(errors.name)setErrors(p=>({...p,name:undefined}));}} placeholder="e.g. Radha Devi" style={IS}/></Fld>{errors.name&&<div style={{color:C.red,fontSize:"12px",marginTop:"-10px",marginBottom:"8px",fontWeight:600}}>{errors.name}</div>}
    <Fld label="Role / Category" req><RolePicker value={f.role} onChange={v=>{s("role",v);if(errors.role)setErrors(p=>({...p,role:undefined}));}}/></Fld>{errors.role&&<div style={{color:C.red,fontSize:"12px",marginTop:"-10px",marginBottom:"8px",fontWeight:600}}>{errors.role}</div>}
    <Fld label="Phone Number"><input value={f.phone||""} onChange={e=>s("phone",e.target.value)} onPaste={e=>{e.preventDefault();s("phone",cleanPhone(e));if(errors.phone)setErrors(p=>({...p,phone:undefined}));}} placeholder="XXXXXXXXXX" type="tel" style={IS}/></Fld>{errors.phone&&<div style={{color:C.red,fontSize:"12px",marginTop:"-10px",marginBottom:"8px",fontWeight:600}}>{errors.phone}</div>}
    <Fld label="Notes"><input value={f.notes||""} onChange={e=>s("notes",e.target.value)} placeholder="e.g. Prefers UPI, allergic to dogs" style={IS}/></Fld>

    {staffType==="regular"&&<>
    <div style={{fontSize:"11px",color:C.light,fontWeight:700,letterSpacing:"1px",margin:"18px 0 12px",textTransform:"uppercase"}}>Salary</div>
    <Fld label="Monthly or Daily Rate">
      <Seg opts={[{v:"monthly",l:"Monthly"},{v:"daily",l:"Daily Rate"}]} val={f.salaryType} onChange={v=>s("salaryType",v)}/>
    </Fld>
    <Fld label={f.salaryType==="monthly"?"Monthly Salary (₹)":"Daily Rate (₹)"} req>
      <input value={f.salary===""||f.salary===0?"":f.salary} onChange={e=>{s("salary",e.target.value===""?"":Number(e.target.value));if(errors.salary)setErrors(p=>({...p,salary:undefined}));}} onFocus={e=>{if(e.target.value==="0")s("salary","");}} type="number" placeholder="e.g. 5000" style={IS}/>
    </Fld>
    {errors.salary&&<div style={{color:C.red,fontSize:"12px",marginTop:"-10px",marginBottom:"8px",fontWeight:600}}>{errors.salary}</div>}

    {Ff.multi_role&&<Fld label="Additional Roles" hint="Different rates for different jobs">
      {(f.roles||[]).map((r,i)=><div key={i} style={{display:"flex",gap:"6px",marginBottom:"8px",alignItems:"center"}}>
        <div style={{flex:2}}><RolePicker value={r.role_name} onChange={v=>{const nr=[...(f.roles||[])];nr[i]={...nr[i],role_name:v};s("roles",nr);}}/></div>
        <input value={r.salary||""} onChange={e=>{const nr=[...(f.roles||[])];nr[i]={...nr[i],salary:Number(e.target.value)};s("roles",nr);}} type="number" placeholder="₹/mo" style={{...IS,flex:1}}/>
        <button onClick={()=>s("roles",(f.roles||[]).filter((_,j)=>j!==i))} style={{color:C.red,background:"#fff0f0",border:"none",borderRadius:"8px",padding:"10px",flexShrink:0,fontWeight:700}}>×</button>
      </div>)}
      <button onClick={()=>s("roles",[...(f.roles||[]),{role_name:"",salary:0,salary_type:"monthly"}])} style={{background:"#f0f4ff",color:"#4f46e5",borderRadius:"10px",padding:"8px 16px",fontSize:"12px",fontWeight:700,border:"none",width:"100%"}}>+ Add Role</button>
    </Fld>}

    <div style={{fontSize:"11px",color:C.light,fontWeight:700,letterSpacing:"1px",margin:"18px 0 12px",textTransform:"uppercase"}}>Schedule</div>
    <Fld label="Weekly Off Days">
      <div style={{display:"flex",gap:"5px",flexWrap:"wrap"}}>
        {WDAYS.map((d,i)=><button key={i} onClick={()=>togWOD(i)} style={{padding:"8px 10px",borderRadius:"9px",fontWeight:600,fontSize:"12px",border:"none",background:(f.weekOffDays||[]).includes(i)?C.ind:C.bg,color:(f.weekOffDays||[]).includes(i)?C.gold:C.light}}>{d}</button>)}
      </div>
    </Fld>
    <Fld label="Pay Cycle Start" hint={(()=>{const cs=f.cycleStart||1;const e=ord(cs);const ep=cs===1?"last day of month":ord(cs-1)+" of next month";return cs===1?`Cycle runs: 1st to last day of month. Payment due: 1st of next month.`:`Cycle runs: ${e} to ${ep}. Payment due: ${e} of the following month.`;})()}>
      <select value={f.cycleStart||1} onChange={e=>s("cycleStart",Number(e.target.value))} style={IS}>
        {Array.from({length:28},(_,i)=>i+1).map(d=><option key={d} value={d}>{d===1?"1st — standard month":`${d}th of month`}</option>)}
      </select>
    </Fld>
    <Fld label="Start tracking from" req hint="Salary cycles before this date are hidden. Pre-filled to the 1st of this month — set it to when you began tracking this staff.">
      <input type="date" value={f.trackingStart||""} onChange={e=>{s("trackingStart",e.target.value);if(errors.trackingStart)setErrors(p=>({...p,trackingStart:undefined}));}} style={IS}/>
    </Fld>{errors.trackingStart&&<div style={{color:C.red,fontSize:"12px",marginTop:"-10px",marginBottom:"8px",fontWeight:600}}>{errors.trackingStart}</div>}
    <Fld label="Paid Leaves per Month">
      <input value={f.paidLeaveDays===0||f.paidLeaveDays===""?"":f.paidLeaveDays} onChange={e=>s("paidLeaveDays",e.target.value===""?0:Number(e.target.value))} onFocus={e=>{if(e.target.value==="0")s("paidLeaveDays","");}} type="number" min="0" max="15" placeholder="0" style={IS}/>
    </Fld>

    <div style={{fontSize:"11px",color:C.light,fontWeight:700,letterSpacing:"1px",margin:"18px 0 12px",textTransform:"uppercase"}}>Work Type</div>
    <Tog on={f.fullTime} toggle={()=>handleFT(!f.fullTime)} label="Full-time / Live-in" sub="Defaults to Present — only mark exceptions" disabled={f.sessions>1}/>
    {Ff.sessions&&<Fld label="Does this staff come more than once a day?">
      <div style={{display:"flex",gap:"8px"}}>
        <button type="button" onClick={()=>handleMultiDay(false)} style={{flex:1,padding:"12px",borderRadius:"12px",fontWeight:700,fontSize:"13px",border:`2px solid ${!multiDay?C.ind:C.border}`,background:!multiDay?C.ind:"#fff",color:!multiDay?C.gold:C.light,transition:"all .15s"}}>
          No <span style={{display:"block",fontSize:"10px",fontWeight:500,marginTop:"2px",opacity:.7}}>Single session</span>
        </button>
        <button type="button" onClick={()=>handleMultiDay(true)} style={{flex:1,padding:"12px",borderRadius:"12px",fontWeight:700,fontSize:"13px",border:`2px solid ${multiDay?C.ind:C.border}`,background:multiDay?C.ind:"#fff",color:multiDay?C.gold:C.light,transition:"all .15s",opacity:f.fullTime?.4:1}} disabled={!!f.fullTime}>
          Yes <span style={{display:"block",fontSize:"10px",fontWeight:500,marginTop:"2px",opacity:.7}}>Multiple slots</span>
        </button>
      </div>
      {multiDay&&<div style={{marginTop:"12px"}}>
        <div style={{fontSize:"12px",color:C.light,fontWeight:600,marginBottom:"8px"}}>Which slots? (select at least 2)</div>
        <div style={{display:"flex",gap:"6px"}}>
          {[{v:"M",l:"Morning",icon:"🌅"},{v:"A",l:"Afternoon",icon:"☀️"},{v:"E",l:"Evening",icon:"🌆"},{v:"N",l:"Night",icon:"🌙"}].map(({v,l,icon})=>{
            const cur=f.slots||["M"];
            const sel=cur.includes(v);
            return <button key={v} type="button" onClick={()=>{const next=sel?(cur.length>2?cur.filter(x=>x!==v):cur):[...cur,v].filter((x,i,a)=>a.indexOf(x)===i);s("slots",next);s("sessions",next.length);}} style={{flex:1,padding:"10px 4px",borderRadius:"10px",fontWeight:700,fontSize:"12px",border:`2px solid ${sel?C.ind:C.border}`,background:sel?C.ind:"#fff",color:sel?C.gold:C.light,textAlign:"center",transition:"all .15s"}}>
              <div style={{fontSize:"16px",marginBottom:"2px"}}>{icon}</div>
              <div style={{fontSize:"10px"}}>{l}</div>
            </button>;
          })}
        </div>
        <div style={{fontSize:"11px",color:C.xlight,marginTop:"5px"}}>{(f.slots||["M"]).length} slot{(f.slots||["M"]).length!==1?"s":""} selected</div>
        {errors.slots&&<div style={{color:C.red,fontSize:"12px",marginTop:"4px",fontWeight:600}}>{errors.slots}</div>}
      </div>}
    </Fld>}
    {Ff.time_tracking&&<><Tog on={f.timeTracking} toggle={()=>s("timeTracking",!f.timeTracking)} label="Log time in/out"/>
      {f.timeTracking&&<div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px"}}>
        <Fld label="Start Time"><input type="time" value={f.startTime||"09:00"} onChange={e=>s("startTime",e.target.value)} style={IS}/></Fld>
        <Fld label="Std Hours/Day"><input type="number" value={f.stdHours===8||f.stdHours===""?"":f.stdHours} onChange={e=>{s("stdHours",e.target.value===""?8:Number(e.target.value));if(errors.stdHours)setErrors(p=>({...p,stdHours:undefined}));}} onFocus={e=>{if(e.target.value==="8")s("stdHours","");}} min="1" max="24" placeholder="8" style={IS}/></Fld>
      </div>}
    {errors.stdHours&&<div style={{color:C.red,fontSize:"12px",marginBottom:"8px",fontWeight:600}}>{errors.stdHours}</div>}
    </>}
    {Ff.overtime&&<><Tog on={f.otEnabled} toggle={()=>s("otEnabled",!f.otEnabled)} label="Overtime (OT) applicable"/>
      {f.otEnabled&&<>
        <Fld label="OT Policy"><Seg opts={[{v:"fixed",l:"₹/hour"},{v:"multiplier",l:"Rate ×"},{v:"manual",l:"Manual"}]} val={f.otType} onChange={v=>s("otType",v)}/></Fld>
        {f.otType==="fixed"&&<><Fld label="OT Rate (₹/hour)"><input value={f.otRate||""} onChange={e=>{s("otRate",Number(e.target.value));if(errors.otRate)setErrors(p=>({...p,otRate:undefined}));}} type="number" placeholder="e.g. 50" style={IS}/></Fld>{errors.otRate&&<div style={{color:C.red,fontSize:"12px",marginTop:"-10px",marginBottom:"8px",fontWeight:600}}>{errors.otRate}</div>}</>}
        {f.otType==="multiplier"&&<Fld label="OT Multiplier"><input value={f.otMultiplier||1.5} onChange={e=>s("otMultiplier",Number(e.target.value))} type="number" step="0.25" placeholder="1.5" style={IS}/></Fld>}
        {f.otType==="manual"&&<InfoBox icon="ℹ️">Enter OT amount manually each month in Salary → Adjust.</InfoBox>}
      </>}
    </>}
    {Ff.pf_esic&&<div style={{fontSize:"11px",color:C.light,fontWeight:700,letterSpacing:"1px",margin:"18px 0 12px",textTransform:"uppercase"}}>Statutory</div>}
    {Ff.pf_esic&&<><Tog on={f.pfEnabled} toggle={()=>s("pfEnabled",!f.pfEnabled)} label="PF enrolled (12% employee deduction)"/><Tog on={f.esicEnabled} toggle={()=>s("esicEnabled",!f.esicEnabled)} label="ESIC enrolled (0.75% if salary ≤ ₹21,000)"/></>}

    <div style={{fontSize:"11px",color:C.light,fontWeight:700,letterSpacing:"1px",margin:"18px 0 12px",textTransform:"uppercase"}}>Personal Details</div>
    <Fld label="Date of Birth" hint="Used for birthday reminders"><input type="date" value={f.dateOfBirth||""} onChange={e=>s("dateOfBirth",e.target.value)} style={IS}/></Fld>
    <Fld label="Aadhaar / ID Number"><input value={f.aadhaar||""} onChange={e=>s("aadhaar",e.target.value)} placeholder="XXXX XXXX XXXX" style={IS}/></Fld>
    <Fld label="Emergency Contact Name"><input value={f.emergencyContact||""} onChange={e=>s("emergencyContact",e.target.value)} placeholder="e.g. Suresh (husband)" style={IS}/></Fld>
    <Fld label="Emergency Contact Phone"><input value={f.emergencyPhone||""} onChange={e=>{s("emergencyPhone",e.target.value);if(errors.emergencyPhone)setErrors(p=>({...p,emergencyPhone:undefined}));}} onPaste={e=>{e.preventDefault();s("emergencyPhone",cleanPhone(e));if(errors.emergencyPhone)setErrors(p=>({...p,emergencyPhone:undefined}));}} placeholder="XXXXXXXXXX" type="tel" style={IS}/></Fld>{errors.emergencyPhone&&<div style={{color:C.red,fontSize:"12px",marginTop:"-10px",marginBottom:"8px",fontWeight:600}}>{errors.emergencyPhone}</div>}
    <Fld label="Join Date">
      <Tog on={useExact} toggle={()=>setUE(v=>!v)} label="I know the exact date"/>
      {useExact?<input ref={joinDateRef} type="date" value={f.joinDate||""} onChange={e=>{s("joinDate",e.target.value);if(errors.joinDate)setErrors(p=>({...p,joinDate:undefined}));}} style={IS}/>:
        <div style={{display:"flex",gap:"8px"}}>
          <select value={f.joinMonth||""} onChange={e=>s("joinMonth",Number(e.target.value))} style={{...IS,flex:1}}>
            <option value="">Month</option>
            {FULLM.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
          </select>
          <input value={f.joinYear||""} onChange={e=>s("joinYear",e.target.value)} placeholder="Year" type="number" min="2000" style={{...IS,width:"90px"}}/>
        </div>}
    </Fld>
    {errors.joinDate&&<div style={{color:C.red,fontSize:"12px",marginTop:"-10px",marginBottom:"8px",fontWeight:600}}>{errors.joinDate}</div>}
    </>}
    <SaveRow loading={loading} onClose={onClose} label={staff?"Save Changes":"Add to Roster"} onSave={async()=>{
      const errs={};
      if(!f.name)errs.name="Please enter a name";
      if(!f.role)errs.role="Please select a role";
      if(staffType==="regular"&&(!f.salary||f.salary===0))errs.salary="Please enter a salary amount";
      if(f.otEnabled&&f.otType==="fixed"&&(!f.otRate||f.otRate===0))errs.otRate="Please enter an OT rate";
      if(f.timeTracking&&(Number(f.stdHours)<1||Number(f.stdHours)>24))errs.stdHours="Standard hours must be between 1 and 24";
      if(multiDay&&(f.slots||[]).length<2)errs.slots="Please select at least 2 time slots";
      if(f.phone&&f.phone.replace(/\D/g,"").length!==10)errs.phone="Please enter a valid 10-digit mobile number.";
      if(f.emergencyPhone&&f.emergencyPhone.replace(/\D/g,"").length!==10)errs.emergencyPhone="Please enter a valid 10-digit mobile number.";
      if(useExact&&f.joinDate&&new Date(f.joinDate)>new Date())errs.joinDate="Join date cannot be in the future";
      if(staffType==="regular"&&!f.trackingStart)errs.trackingStart="Please set a tracking start date";
      if(Object.keys(errs).length>0){setErrors(errs);return;}
      setL(true);try{await onSave({...f,staffType});}finally{setL(false);}
    }}/>
  </Modal>;
}

// ── Batch menu ────────────────────────────────────────────────
function BatchMenu({sy,sm,sd,activeStaff,att,setAtt,hid,showT,api}) {
  const [open,setOpen]=useState(false);
  const ref=useRef(null);

  useEffect(()=>{
    if(!open) return;
    function handler(e){if(ref.current&&!ref.current.contains(e.target))setOpen(false);}
    document.addEventListener("mousedown",handler);
    document.addEventListener("touchstart",handler);
    return()=>{document.removeEventListener("mousedown",handler);document.removeEventListener("touchstart",handler);};
  },[open]);

  const OPTIONS=[
    {label:"All Present",status:"P",bg:"#dcfce7",color:"#15803d"},
    {label:"All Absent",status:"A",bg:"#ffe4e6",color:"#be123c"},
    {label:"All Half Day",status:"H",bg:"#fef3c7",color:"#92400e"},
    {label:"All Leave",status:"L",bg:"#ede9fe",color:"#6d28d9"},
  ];

  async function apply(status) {
    setOpen(false);
    const dateKey=dk(sy,sm,sd);
    const dw=dow(sy,sm,sd);
    // For Leave: include everyone (including week off). For P/A/H: exclude week off.
    const eligible=activeStaff.filter(s=>{
      if(s.joinDate&&new Date(dateKey)<new Date(s.joinDate)) return false;
      if(status!=="L"&&isWO(s,dw)) return false;
      return true;
    });
    // Optimistic update — set ALL slots for each eligible staff
    setAtt(prev=>{
      const n={...prev};
      eligible.forEach(s=>{
        n[s.id]={...(n[s.id]||{})};
        (s.slots||["M"]).forEach((slotId,si)=>{
          const k=sk(dateKey,si);
          n[s.id][k]=status;
        });
      });
      return n;
    });
    // Fire API calls in parallel for all slots
    const results=await Promise.allSettled(eligible.flatMap(s=>
      (s.slots||["M"]).map((_,si)=>
        api("/attendance","POST",{staff_id:s.id,date:dateKey,session_index:si,status},hid)
      )
    ));
    if(results.some(r=>r.status==="rejected")) showT("Some attendance marks failed to save","error");
    const label=OPTIONS.find(o=>o.status===status)?.label||"All";
    showT(`All staff marked as ${label}`);
  }

  return(
    <div ref={ref} style={{position:"relative"}}>
      <button onClick={()=>setOpen(v=>!v)} style={{padding:"6px 12px",borderRadius:"9px",border:"none",fontWeight:700,fontSize:"11px",background:open?"rgba(245,197,24,.25)":"rgba(255,255,255,.07)",color:open?C.gold:"rgba(255,255,255,.5)",transition:"all .15s"}}>
        ⚡ Batch
      </button>
      {open&&<div style={{position:"absolute",right:0,top:"calc(100% + 6px)",background:"#1a1a2e",borderRadius:"14px",boxShadow:"0 8px 32px rgba(0,0,0,.5)",border:"1px solid rgba(255,255,255,.1)",overflow:"hidden",zIndex:500,minWidth:"148px"}}>
        {OPTIONS.map(o=>(
          <button key={o.status} onClick={()=>apply(o.status)} style={{display:"block",width:"100%",padding:"11px 16px",background:"none",border:"none",textAlign:"left",fontSize:"12px",fontWeight:700,color:"#fff",cursor:"pointer",borderBottom:"1px solid rgba(255,255,255,.06)"}}>
            <span style={{display:"inline-block",width:"10px",height:"10px",borderRadius:"50%",background:o.bg,border:`1.5px solid ${o.color}`,marginRight:"8px",verticalAlign:"middle"}}/>
            {o.label}
          </button>
        ))}
      </div>}
    </div>
  );
}

// ── Other modals ──────────────────────────────────────────────
function TimeModal({sid,d,staffMember:sm,existing,onClose,onSave}) {
  const [inT,setIn]=useState(existing?.inTime||"");
  const [outT,setOut]=useState(existing?.outTime||"");
  const hrs=inT&&outT?Math.max(0,(t2m(outT)-t2m(inT))/60).toFixed(1):null;
  const late=inT&&sm?.startTime?Math.max(0,t2m(inT)-t2m(sm.startTime)):0;
  const otH=hrs&&sm?.stdHours?Math.max(0,Number(hrs)-(sm.stdHours||8)).toFixed(1):0;
  return <Modal onClose={onClose} title="Log Time" sub={d} accent="#4f46e5">
    {sm?.startTime&&<InfoBox color={C.light}>Start: <strong>{sm.startTime}</strong> · Std: <strong>{sm.stdHours||8}h</strong></InfoBox>}
    <Fld label="Time In"><input type="time" value={inT} onChange={e=>setIn(e.target.value)} style={IS}/></Fld>
    {inT&&(late>0?<InfoBox color="#c2410c" bg="#fff7ed" icon="⚠️">{late} mins late</InfoBox>:<InfoBox color={C.green} bg="#dcfce7" icon="✓">On time</InfoBox>)}
    <Fld label="Time Out"><input type="time" value={outT} onChange={e=>setOut(e.target.value)} style={IS}/></Fld>
    {hrs&&<InfoBox color="#4f46e5" bg="#f0f4ff">⏱ {hrs}h{Number(otH)>0?` · ${otH}h OT`:""}</InfoBox>}
    <SaveRow onClose={onClose} label="Save Time" onSave={()=>onSave(inT,outT)}/>
  </Modal>;
}

function RemModal({name,d,existing,onClose,onSave}) {
  const [txt,setTxt]=useState(existing||"");
  return <Modal onClose={onClose} title="Remark" sub={`${name} · ${d}`} accent={C.amber}>
    <Fld label="Note"><textarea value={txt} onChange={e=>setTxt(e.target.value)} placeholder="e.g. Left early, asked for leave…" rows={4} style={{...IS,resize:"vertical"}}/></Fld>
    <SaveRow onClose={onClose} label="Save" onSave={()=>onSave(txt)}/>
  </Modal>;
}

function IncrModal({name,cur,onClose,onSave}) {
  const [date,setDate]=useState(new Date().toISOString().slice(0,10));
  const [amt,setAmt]=useState(""); const [rsn,setRsn]=useState(""); const [l,setL]=useState(false);
  const diff=amt?Number(amt)-cur:0;
  return <Modal onClose={onClose} title="Salary Increment" sub={name} accent={C.green}>
    <InfoBox color={C.light}>Current: <strong>{fmtC(cur)}</strong></InfoBox>
    <Fld label="New Salary (₹)" req><input value={amt} onChange={e=>setAmt(e.target.value)} type="number" placeholder="e.g. 6000" style={IS}/></Fld>
    <Fld label="Effective From" req><input type="date" value={date} onChange={e=>setDate(e.target.value)} style={IS}/></Fld>
    <Fld label="Reason"><input value={rsn} onChange={e=>setRsn(e.target.value)} placeholder="e.g. Annual increment, Diwali bonus" style={IS}/></Fld>
    {diff>0&&<InfoBox color={C.green} bg="#dcfce7">↑ Increase of {fmtC(diff)}</InfoBox>}
    {diff<0&&<InfoBox color={C.red} bg="#ffe4e6">↓ Decrease of {fmtC(Math.abs(diff))}</InfoBox>}
    <SaveRow loading={l} label="Save Increment" onClose={onClose} onSave={async()=>{if(!amt||!date)return;setL(true);try{await onSave(date,Number(amt),rsn);}finally{setL(false);}}}/>
  </Modal>;
}

function AdvModal({name,sid,onClose,onSave}) {
  const [amt,setAmt]=useState(""); const [dateGiven,setDateGiven]=useState(new Date().toISOString().slice(0,10)); const [note,setNote]=useState(""); const [l,setL]=useState(false);
  return <Modal onClose={onClose} title="Record Advance" sub={`for ${name}`} accent={C.gold}>
    <Fld label="Amount Given (₹)" req><input value={amt} onChange={e=>setAmt(e.target.value)} type="number" placeholder="e.g. 5000" style={IS}/></Fld>
    <Fld label="Date Given"><input type="date" value={dateGiven} onChange={e=>setDateGiven(e.target.value)} style={IS}/></Fld>
    <Fld label="Note (optional)"><input value={note} onChange={e=>setNote(e.target.value)} placeholder="e.g. Medical, festival bonus" style={IS}/></Fld>
    <InfoBox icon="ℹ️" color={C.light}>Recovery amount will be entered at salary time each month.</InfoBox>
    <SaveRow loading={l} label="Record Advance" onClose={onClose} onSave={async()=>{if(!amt)return;setL(true);try{await onSave({staffId:sid,totalAmount:Number(amt),amount:Number(amt),dateGiven,note});}finally{setL(false);}}}/>
  </Modal>;
}

function PayModal({name,calc:c,onClose,onSave}) {
  const [amt,setAmt]=useState(String(Math.max(0,c?.net||0))); const [meth,setMeth]=useState("Cash"); const [by,setBy]=useState("Self"); const [note,setNote]=useState(""); const [l,setL]=useState(false);
  const diff=Number(amt)-(c?.net||0);
  return <Modal onClose={onClose} title="Record Payment" sub={`for ${name}`} accent={C.green}>
    <div style={{background:C.bg,borderRadius:"14px",padding:"16px 18px",marginBottom:"16px",textAlign:"center"}}>
      <div style={{fontSize:"12px",color:C.xlight,marginBottom:"4px"}}>Net Payable</div>
      <div style={{fontWeight:800,fontSize:"32px",color:C.text}}>{fmtC(c?.net||0)}</div>
    </div>
    <Fld label="Amount Paid (₹)"><input value={amt} onChange={e=>setAmt(e.target.value)} type="number" style={IS}/></Fld>
    {diff>0&&<InfoBox color={C.purple} bg="#ede9fe" icon="ℹ️">Overpaid {fmtC(diff)} → credited next cycle</InfoBox>}
    {diff<0&&<InfoBox color={C.red} bg="#ffe4e6" icon="ℹ️">Short {fmtC(Math.abs(diff))} → debited next cycle</InfoBox>}
    <Fld label="Method"><Seg opts={[{v:"Cash",l:"Cash"},{v:"UPI",l:"UPI"},{v:"Bank",l:"Bank"},{v:"Office",l:"Office"}]} val={meth} onChange={setMeth}/></Fld>
    <Fld label="Paid By"><input value={by} onChange={e=>setBy(e.target.value)} placeholder="Self, Husband…" style={IS}/></Fld>
    <Fld label="Note"><input value={note} onChange={e=>setNote(e.target.value)} placeholder="Any notes…" style={IS}/></Fld>
    <InfoBox icon="🔒">This will lock the period and reduce advance balances.</InfoBox>
    <SaveRow loading={l} label="Record & Lock" onClose={onClose} onSave={async()=>{if(!amt)return;setL(true);try{await onSave(Number(amt),meth,by,note);}finally{setL(false);}}}/>
  </Modal>;
}


// ── AdjModal ──────────────────────────────────────────────────
function AdjModal({name,existing,onClose,onSave}) {
  const [amt,setAmt]=useState(existing?String(existing.amount):"");
  const [note,setNote]=useState(existing?.note||"");
  const [err,setErr]=useState("");
  const [l,setL]=useState(false);
  const isEdit=!!existing;
  return <Modal onClose={onClose} title={isEdit?"Edit Adjustment":"Add Adjustment"} sub={`for ${name}`} accent={C.light}>
    <InfoBox icon="ℹ️">Positive = credit (+₹50 bonus). Negative = deduction (-₹200 breakage).</InfoBox>
    <Fld label="Amount (₹)" req>
      <input value={amt} onChange={e=>{setAmt(e.target.value);setErr("");}} type="number" placeholder="e.g. 50 or -200" style={{...IS,borderColor:err?C.red:C.border}}/>
      {err&&<div style={{color:C.red,fontSize:"12px",marginTop:"4px"}}>{err}</div>}
    </Fld>
    <Fld label="Reason"><input value={note} onChange={e=>setNote(e.target.value)} placeholder="e.g. Diwali bonus, breakage" style={IS}/></Fld>
    <SaveRow loading={l} label={isEdit?"Save Changes":"Add Adjustment"} onClose={onClose} onSave={async()=>{
      if(!amt){setErr("Please enter an amount.");return;}
      setL(true);
      try{await onSave(Number(amt),note);}
      finally{setL(false);}
    }}/>
  </Modal>;
}

// ══ Phase-3 components ══════════════════════════════════════════

// ── Per-staff salary card: stacked cycle boxes + advance box ──
function StaffSalaryCard({s,att,sl,tlog,pays,advs,adjs,advBalance,onOpenCycle,onSettle,onOpenAdv}){
  const today=new Date();
  const {boxes,allSettled}=openCycleBoxes(s,pays,today);
  return <div style={{background:C.white,borderRadius:"18px",padding:"14px 14px 12px",marginBottom:"14px",boxShadow:"0 2px 12px rgba(0,0,0,.06)",border:`1px solid ${C.border}`}}>
    {/* name + role + cycle-start (no status badge) */}
    <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"12px"}}>
      <Avatar name={s.name} photo={s.photoUrl} size={36} fs={14}/>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontWeight:700,color:C.text,fontSize:"16px"}}>{s.name}</div>
        <div style={{color:C.xlight,fontSize:"11px"}}>{ri(s.role)} {s.role} · Settles {ord(s.cycleStart||1)}</div>
      </div>
    </div>

    {/* cycle boxes, oldest unpaid on top → accruing at bottom */}
    {boxes.map((b,i)=>{
      const period=cyclePeriodLabel(s,b.year,b.month);
      if(b.accruing){
        const c=calcSal(s,att,sl,tlog,pays,advs,adjs,b.year,b.month);
        return <button key={`acc-${b.year}-${b.month}`} onClick={()=>onOpenCycle(b.year,b.month)} style={{width:"100%",textAlign:"left",border:`1px dashed ${C.border}`,background:C.bg,borderRadius:"14px",padding:"12px 14px",marginBottom:"8px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <div style={{fontSize:"11px",color:C.xlight,fontWeight:600}}>{period}</div>
            <div style={{fontSize:"12px",color:C.light,fontWeight:600,marginTop:"2px"}}>Now earning</div>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:"9px",color:C.xlight,fontWeight:700,letterSpacing:".5px"}}>SO FAR</div>
            <div style={{fontWeight:800,color:C.mid,fontSize:"20px",lineHeight:1.1}}>{fmtC(c.net)}</div>
          </div>
        </button>;
      }
      const c=calcSal(s,att,sl,tlog,pays,advs,adjs,b.year,b.month);
      const pending=b.state==='pending';
      const pay=pending?payFor(pays,s.id,b.year,b.month):null;
      const shortfall=Number(pay?.shortfall||0);
      const accent=pending?C.red:C.gold;
      const amount=pending?shortfall:c.net;
      return <div key={`${b.year}-${b.month}`} style={{border:`1px solid ${pending?"#fecaca":C.border}`,borderLeft:`3px solid ${accent}`,background:pending?"#fff5f5":C.white,borderRadius:"14px",padding:"12px 14px",marginBottom:"8px"}}>
        <button onClick={()=>onOpenCycle(b.year,b.month)} style={{width:"100%",textAlign:"left",background:"none",border:"none",padding:0,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div style={{minWidth:0}}>
            <div style={{fontSize:"11px",color:C.xlight,fontWeight:600}}>{period}</div>
            <div style={{fontSize:"12px",fontWeight:700,color:pending?C.red:C.light,marginTop:"2px"}}>{pending?`You owe ${fmtC(shortfall)}`:"Due — unpaid"}</div>
          </div>
          <div style={{fontWeight:800,color:pending?C.red:C.text,fontSize:"22px",lineHeight:1,flexShrink:0,marginLeft:"10px"}}>{fmtC(amount)}</div>
        </button>
        <button onClick={()=>onSettle(b.year,b.month)} style={{marginTop:"10px",width:"100%",background:C.gold,color:C.ind,border:"none",borderRadius:"10px",padding:"9px",fontWeight:700,fontSize:"13px"}}>Settle now</button>
      </div>;
    })}
    {allSettled&&<div style={{fontSize:"12px",color:C.green,fontWeight:600,textAlign:"center",margin:"2px 0 8px"}}>All settled ✓</div>}

    {/* advance box, visually separate */}
    <button onClick={onOpenAdv} style={{width:"100%",textAlign:"left",marginTop:"4px",background:advBalance>0?"#fffbeb":C.bg,border:`1px solid ${advBalance>0?"#fde68a":C.border}`,borderRadius:"12px",padding:"10px 14px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
      <div style={{fontSize:"12px",color:advBalance>0?"#92400e":C.light,fontWeight:600}}>💰 Advance balance</div>
      <div style={{fontWeight:700,fontSize:"15px",color:advBalance>0?C.amber:C.xlight}}>{advBalance>0?fmtC(advBalance):"Nil"} ›</div>
    </button>
  </div>;
}

// ── Settle screen: read-only breakdown + amount field + decision table ──
function SettleScreen({staff:s,calc:c,advBalance,year,month,isAdmin,unmarked=0,requiredDays=0,onClose,onSettle}){
  const net=c.net;
  const blocked=unmarked>0;
  const [amt,setAmt]=useState(String(Math.max(0,net)));
  const [meth,setMeth]=useState("Cash");
  const [by,setBy]=useState("Self");
  const [note,setNote]=useState("");
  const [decision,setDecision]=useState(null); // 'recover' | 'gift' | 'pending'
  const [l,setL]=useState(false);
  const DR=c.dailyRate||0;
  const paid=Number(amt||0);
  const diff=paid-net;                 // >0 over, <0 under
  const over=diff>0?diff:0;
  const under=diff<0?-diff:0;
  const period=cyclePeriodLabel(s,year,month);

  function SRow({label,value,sub,color,bold}){
    return <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0"}}>
      <span style={{fontSize:"13px",color:sub?C.xlight:C.mid,fontWeight:bold?700:400}}>{label}</span>
      <span style={{fontSize:"13px",fontWeight:bold?700:600,color:color||C.text}}>{value}</span>
    </div>;
  }

  // ── decision availability ──
  const needOverDecision = over>0;
  const needUnderDecision = under>0 && advBalance>0; // if no balance, always pending
  const canSubmit = !isAdmin ? false
    : blocked ? false
    : over>0 ? (decision==='recover'||decision==='gift')
    : under>0 ? (advBalance>0 ? (decision==='recover'||decision==='pending') : true)
    : true; // equal

  async function submit(){
    const opts={method:meth,paid_by:by,note};
    if(over>0) opts.over_decision=decision;            // 'recover'|'gift'
    else if(under>0){
      if(advBalance>0) opts.under_decision=decision;   // 'recover'|'pending'
      else opts.under_decision='pending';              // no balance → pending
    }
    setL(true);
    try{ await onSettle(paid,opts); }
    finally{ setL(false); }
  }

  return <Modal onClose={onClose} title="Settle cycle" sub={`${s.name} · ${period}`} accent={C.gold}>
    {/* read-only breakdown */}
    <div style={{background:C.bg,borderRadius:"16px",padding:"14px 16px",marginBottom:"14px"}}>
      <div style={{fontSize:"10px",fontWeight:700,letterSpacing:"1px",textTransform:"uppercase",color:C.xlight,marginBottom:"4px"}}>Attendance</div>
      <SRow label="Present" value={`${c.P} day${c.P!==1?"s":""}`}/>
      {c.W>0&&<SRow label="Week Off" value={`${c.W} days`} sub/>}
      {c.A>0&&<SRow label="Absent" value={`${c.A} day${c.A!==1?"s":""}`} color={C.red}/>}
      {c.H>0&&<SRow label="Half Day" value={`${c.H}`} color={C.amber}/>}
      {c.paidLeavesUsed>0&&<SRow label="Paid Leave" value={`${c.paidLeavesUsed} of ${s.paidLeaveDays||0}`} color={C.green}/>}
      {c.woWorked>0&&<SRow label="WO Worked" value={`${c.woWorked}`} color={C.purple}/>}
      <div style={{fontSize:"10px",fontWeight:700,letterSpacing:"1px",textTransform:"uppercase",color:C.xlight,margin:"6px 0 4px"}}>Earnings</div>
      <SRow label="Monthly Salary" value={fmtC(s.salary||0)}/>
      {s.salaryType==="monthly"&&<SRow label={`Daily Rate (÷${c.daysInCycle})`} value={`₹${DR.toFixed(2)}`} sub/>}
      <SRow label="Salary Earned" value={fmtC(c.earned)} bold color={C.green}/>
      {c.otAmt>0&&<SRow label="Overtime" value={`+${fmtC(c.otAmt)}`} color={C.purple}/>}
      {(c.pfDed>0||c.esicDed>0)&&<div style={{fontSize:"10px",fontWeight:700,letterSpacing:"1px",textTransform:"uppercase",color:C.xlight,margin:"6px 0 4px"}}>Deductions</div>}
      {c.pfDed>0&&<SRow label="PF 12%" value={`–${fmtC(c.pfDed)}`} color={C.red}/>}
      {c.esicDed>0&&<SRow label="ESIC 0.75%" value={`–${fmtC(c.esicDed)}`} color={C.red}/>}
      {c.adjs.length>0&&<>
        <div style={{fontSize:"10px",fontWeight:700,letterSpacing:"1px",textTransform:"uppercase",color:C.xlight,margin:"6px 0 4px"}}>Adjustments</div>
        {c.adjs.map(a=><SRow key={a.id} label={a.note||"Adjustment"} value={`${Number(a.amount)>0?"+":""}${fmtC(a.amount)}`} color={Number(a.amount)>0?C.green:C.red}/>)}
      </>}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:"8px",paddingTop:"8px",borderTop:`1px dashed ${C.border}`}}>
        <span style={{fontWeight:700,color:C.text,fontSize:"15px"}}>Net Payable</span>
        <span style={{fontWeight:800,color:C.text,fontSize:"28px",lineHeight:1}}>{fmtC(net)}</span>
      </div>
      <div style={{display:"flex",justifyContent:"space-between",marginTop:"6px",fontSize:"12px"}}>
        <span style={{color:C.xlight}}>Advance balance</span>
        <span style={{color:advBalance>0?C.amber:C.xlight,fontWeight:700}}>{advBalance>0?`${fmtC(advBalance)} outstanding`:"Nil"}</span>
      </div>
    </div>

    {!isAdmin&&<InfoBox icon="ℹ️">Only admins can settle a cycle.</InfoBox>}

    {blocked&&<div style={{background:"#fff0f0",border:"1px solid #fecaca",borderRadius:"12px",padding:"12px 14px",marginBottom:"14px"}}>
      <div style={{fontWeight:700,fontSize:"13px",color:C.red,marginBottom:"4px"}}>{unmarked} of {requiredDays} day{requiredDays!==1?"s":""} unmarked — mark them to settle.</div>
      <div style={{fontSize:"12px",color:"#b45309"}}>Every elapsed day in this cycle must be marked before settling. Unmarked days are <strong>not</strong> counted as absent.</div>
    </div>}

    {/* amount field */}
    <Fld label="Amount Paying (₹)"><input value={amt} onChange={e=>{setAmt(e.target.value);setDecision(null);}} type="number" style={IS} disabled={!isAdmin}/></Fld>

    {/* decision UI */}
    {over>0&&<div style={{background:"#ede9fe",borderRadius:"12px",padding:"12px 14px",marginBottom:"14px"}}>
      <div style={{fontSize:"13px",fontWeight:700,color:"#5b21b6",marginBottom:"8px"}}>Paying {fmtC(over)} over Net Payable — recover as advance, or gift?</div>
      <div style={{display:"flex",gap:"8px"}}>
        <button onClick={()=>setDecision('recover')} style={{flex:1,padding:"9px",borderRadius:"10px",border:`1.5px solid ${decision==='recover'?C.ind:C.border}`,background:decision==='recover'?C.ind:C.white,color:decision==='recover'?C.gold:C.mid,fontWeight:700,fontSize:"13px"}}>Recover ↑ advance</button>
        <button onClick={()=>setDecision('gift')} style={{flex:1,padding:"9px",borderRadius:"10px",border:`1.5px solid ${decision==='gift'?C.ind:C.border}`,background:decision==='gift'?C.ind:C.white,color:decision==='gift'?C.gold:C.mid,fontWeight:700,fontSize:"13px"}}>Gift (nothing carries)</button>
      </div>
    </div>}
    {under>0&&advBalance>0&&<div style={{background:"#fffbeb",borderRadius:"12px",padding:"12px 14px",marginBottom:"14px"}}>
      <div style={{fontSize:"13px",fontWeight:700,color:"#92400e",marginBottom:"8px"}}>Short by {fmtC(under)} — recover from advance ({fmtC(advBalance)} available)?</div>
      <div style={{display:"flex",gap:"8px"}}>
        <button onClick={()=>setDecision('recover')} style={{flex:1,padding:"9px",borderRadius:"10px",border:`1.5px solid ${decision==='recover'?C.ind:C.border}`,background:decision==='recover'?C.ind:C.white,color:decision==='recover'?C.gold:C.mid,fontWeight:700,fontSize:"13px"}}>Yes, recover from advance</button>
        <button onClick={()=>setDecision('pending')} style={{flex:1,padding:"9px",borderRadius:"10px",border:`1.5px solid ${decision==='pending'?C.red:C.border}`,background:decision==='pending'?"#fff0f0":C.white,color:C.red,fontWeight:700,fontSize:"13px"}}>No — mark pending</button>
      </div>
    </div>}
    {under>0&&advBalance===0&&<InfoBox icon="⚠️" color={C.red} bg="#fff0f0">No advance to draw from — this cycle will be marked <strong>Pending</strong>, you owe {fmtC(under)}.</InfoBox>}
    {diff===0&&isAdmin&&<InfoBox icon="✓" color="#15803d" bg="#dcfce7">Paying exactly Net Payable — clean settle.</InfoBox>}

    <Fld label="Method"><Seg opts={[{v:"Cash",l:"Cash"},{v:"UPI",l:"UPI"},{v:"Bank",l:"Bank"},{v:"Office",l:"Office"}]} val={meth} onChange={setMeth}/></Fld>
    <Fld label="Paid By"><input value={by} onChange={e=>setBy(e.target.value)} placeholder="Self, Husband…" style={IS}/></Fld>
    <Fld label="Note"><input value={note} onChange={e=>setNote(e.target.value)} placeholder="Any notes…" style={IS}/></Fld>

    <div style={{display:"flex",gap:"10px",marginTop:"8px"}}>
      <Btn label="Cancel" onClick={onClose} variant="ghost" full/>
      <Btn label="Settle" onClick={submit} loading={l} disabled={!canSubmit} variant="gold" full/>
    </div>
  </Modal>;
}

// ── Advance ledger sheet: running ledger + give/repay/write-off ──
function AdvLedgerSheet({staff:s,hid,isAdmin,api,onClose,onEntry,friendlyError}){
  const [data,setData]=useState(null); // {balance,entries}
  const [loading,setLoading]=useState(true);
  const [err,setErr]=useState("");
  const [form,setForm]=useState(null); // {type}
  const [amt,setAmt]=useState("");
  const [note,setNote]=useState("");
  const [date,setDate]=useState(new Date().toISOString().slice(0,10));
  const [saving,setSaving]=useState(false);

  async function load(){
    setLoading(true);
    try{ const d=await api(`/salary/advance-ledger/${s.id}`,"GET",null,hid); setData(d); }
    catch(e){ setErr(friendlyError(e.message)); }
    finally{ setLoading(false); }
  }
  useEffect(()=>{load();},[]); // eslint-disable-line

  const TYPE_META={
    given:{l:"Given",sign:"+",color:C.amber},
    cycle_overpay:{l:"Cycle overpay",sign:"+",color:C.amber},
    recovered:{l:"Recovered",sign:"–",color:C.green},
    repaid:{l:"Repaid",sign:"–",color:C.green},
    written_off:{l:"Written off",sign:"–",color:C.light},
  };
  const balance=Number(data?.balance||0);

  async function submitEntry(){
    if(!amt||Number(amt)<=0){setErr("Enter a positive amount.");return;}
    setSaving(true); setErr("");
    try{
      await onEntry(form.type,Number(amt),note,date);
      setForm(null); setAmt(""); setNote("");
      await load();
    }catch(e){ setErr(friendlyError(e.message)); }
    finally{ setSaving(false); }
  }

  return <Modal onClose={onClose} title="Advance ledger" sub={s.name} accent={C.gold}>
    {/* balance */}
    <div style={{background:HDR,borderRadius:"16px",padding:"16px 18px",marginBottom:"14px",textAlign:"center"}}>
      <div style={{fontSize:"10px",color:"rgba(255,255,255,.45)",fontWeight:700,letterSpacing:".6px",textTransform:"uppercase",marginBottom:"4px"}}>Outstanding balance</div>
      <div style={{fontWeight:800,color:balance>0?C.gold:"#86efac",fontSize:"30px",lineHeight:1}}>{balance>0?fmtC(balance):"Nil"}</div>
    </div>

    {/* actions */}
    {isAdmin&&!form&&<div style={{display:"flex",gap:"8px",marginBottom:"14px"}}>
      <button onClick={()=>{setForm({type:'given'});setErr("");}} style={{flex:1,padding:"10px",borderRadius:"12px",border:"none",background:C.gold,color:C.ind,fontWeight:700,fontSize:"12px"}}>Give advance</button>
      <button onClick={()=>{setForm({type:'repaid'});setErr("");}} disabled={balance<=0} style={{flex:1,padding:"10px",borderRadius:"12px",border:`1px solid ${C.border}`,background:balance>0?C.white:C.bg,color:balance>0?C.mid:C.xlight,fontWeight:700,fontSize:"12px"}}>Record repayment</button>
      <button onClick={()=>{setForm({type:'written_off'});setErr("");}} disabled={balance<=0} style={{flex:1,padding:"10px",borderRadius:"12px",border:`1px solid ${C.border}`,background:balance>0?C.white:C.bg,color:balance>0?C.red:C.xlight,fontWeight:700,fontSize:"12px"}}>Write off</button>
    </div>}

    {form&&<div style={{background:C.bg,borderRadius:"14px",padding:"14px",marginBottom:"14px"}}>
      <div style={{fontWeight:700,fontSize:"13px",color:C.text,marginBottom:"10px"}}>{TYPE_META[form.type].l}</div>
      <Fld label="Amount (₹)" req><input value={amt} onChange={e=>{setAmt(e.target.value);setErr("");}} type="number" placeholder="e.g. 2000" style={IS}/></Fld>
      {form.type==='given'&&<Fld label="Date"><input type="date" value={date} onChange={e=>setDate(e.target.value)} style={IS}/></Fld>}
      <Fld label="Note (optional)"><input value={note} onChange={e=>setNote(e.target.value)} placeholder="e.g. Medical, festival" style={IS}/></Fld>
      {err&&<div style={{color:C.red,fontSize:"12px",marginBottom:"8px"}}>{err}</div>}
      <div style={{display:"flex",gap:"8px"}}>
        <Btn label="Cancel" onClick={()=>{setForm(null);setErr("");}} variant="ghost" full/>
        <Btn label="Save" onClick={submitEntry} loading={saving} variant="gold" full/>
      </div>
    </div>}

    {/* ledger */}
    <div style={{fontSize:"11px",color:C.xlight,fontWeight:700,letterSpacing:".8px",marginBottom:"8px"}}>LEDGER</div>
    {loading?<div style={{textAlign:"center",padding:"24px"}}><Spin/></div>:
      !data||!data.entries?.length?<div style={{fontSize:"13px",color:C.xlight,textAlign:"center",padding:"16px 0"}}>No advance movements yet.</div>:
      data.entries.slice().reverse().map(e=>{
        const m=TYPE_META[e.entry_type]||{l:e.entry_type,sign:Number(e.amount)>=0?"+":"–",color:C.mid};
        return <div key={e.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"9px 0",borderBottom:`1px dashed ${C.bg}`}}>
          <div style={{minWidth:0}}>
            <div style={{fontSize:"13px",color:C.text,fontWeight:600}}>{m.l}{e.note?` · ${e.note}`:""}</div>
            <div style={{fontSize:"11px",color:C.xlight}}>{fmtDate(e.entry_date)}</div>
          </div>
          <div style={{textAlign:"right",flexShrink:0,marginLeft:"8px"}}>
            <div style={{fontSize:"14px",fontWeight:700,color:m.color}}>{m.sign}{fmtC(Math.abs(Number(e.amount)))}</div>
            <div style={{fontSize:"11px",color:C.xlight}}>Bal {fmtC(Number(e.running))}</div>
          </div>
        </div>;
      })}
    {err&&!form&&<div style={{color:C.red,fontSize:"12px",marginTop:"8px"}}>{err}</div>}
  </Modal>;
}